import { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation, NavLink, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { adminApi, API, clearAdminToken, fmtTRY, fmtDate, getAdminRole } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import {
  Boxes, FileText, LogOut, Plus, Pencil, Trash2, Upload,
  Package, ShieldCheck, Users, BarChart3, FileSpreadsheet, Eye, Tag, Bell, BookOpen, UserCheck,
  FilePlus2, Percent, Download, Send, CheckSquare, Square, RefreshCw, Camera, Search, Wand2,
  ZoomIn, ZoomOut, RotateCw, ExternalLink, Image as ImageIcon, Brain, RotateCcw, CheckCircle2, MessageCircle,
  Menu, X, ChevronsLeft, ChevronsRight, ArrowLeft, Check, CheckCheck, Phone,
} from "lucide-react";
import CategoriesView from "@/pages/admin/CategoriesView";
import PriceRequestsView from "@/pages/admin/PriceRequestsView";
import ProductMemoryView from "@/pages/admin/ProductMemoryView";
import AuthorizedUsersView from "@/pages/admin/AuthorizedUsersView";

const SERIF = "'Fraunces', 'Libre Caslon Text', Georgia, 'Times New Roman', serif";
const ADMIN_LOGO = "data:image/png;base64,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";

export default function AdminDashboard() {
  const nav = useNavigate();
  const adminRole = getAdminRole();
  const [pendingCount, setPendingCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [waCount, setWaCount] = useState({ messages: 0, quotes: 0 });
  const notifUnread = useNotificationPoll(nav); // 17.38

  const loadPendingCount = async () => {
    try {
      const r = await adminApi.get("/admin/price-requests");
      setPendingCount((r.data || []).length);
    } catch {
      // Yetki/bağlantı hatasında menüyü bozma.
    }
  };
  const loadReviewCount = async () => {
    try {
      const r = await adminApi.get("/admin/review-count");
      setReviewCount(Number(r.data?.quotes || 0));
    } catch {
      // Bildirim sayacı admin ekranını bozmasın.
    }
  };

  useEffect(() => {
    // V17.23: arka plandaki teklif bitince "Aç" düğmesi Teklif Oluştur sayfasına götürür.
    quoteJobStore.navigate = nav;
    return () => { if (quoteJobStore.navigate === nav) quoteJobStore.navigate = null; };
  }, [nav]);

  const loadWaCount = async () => {
    try {
      const r = await adminApi.get("/admin/whatsapp/unread-count");
      setWaCount({ messages: Number(r.data?.messages || 0), quotes: Number(r.data?.quotes || 0) });
    } catch {
      // WhatsApp sayacı menüyü bozmasın.
    }
  };

  useEffect(() => {
    loadPendingCount();
    loadReviewCount();
    loadWaCount();
    const t = setInterval(() => { loadPendingCount(); loadReviewCount(); loadWaCount(); }, 30000);
    return () => clearInterval(t);
  }, []);
  const logout = () => {
    clearAdminToken();
    nav("/admin/login");
  };

  // 17.31: menü masaüstünde daraltılabilir (tercih hatırlanır), telefonda soldan açılan çekmece.
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem("pramid_admin_menu") === "closed"; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 17.33: giriş sayfasıyla aynı başlık yazı tipi (Fraunces)
  useEffect(() => {
    if (document.getElementById("pramid-serif-font")) return;
    const link = document.createElement("link");
    link.id = "pramid-serif-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap";
    document.head.appendChild(link);
  }, []);

  const toggleCollapsed = () => setCollapsed((c) => {
    try { window.localStorage.setItem("pramid_admin_menu", c ? "open" : "closed"); } catch { /* tarayıcı izin vermezse önemli değil */ }
    return !c;
  });
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const navItems = [
    { to: "/admin", end: true, testId: ADMIN.navBrands, icon: Boxes, label: "Markalar" },
    { to: "/admin/products", testId: ADMIN.navProducts, icon: Package, label: "Ürünler" },
    { to: "/admin/create-quote", icon: FilePlus2, label: "Teklif Oluştur" },
    { to: "/admin/price-requests", icon: Bell, label: "Fiyat Bekleyenler", badges: [{ n: pendingCount, cls: "bg-[#E8762D] text-white" }] },
    { to: "/admin/product-memory", icon: BookOpen, label: "Tedarikçi Hafızası" },
    { to: "/admin/learned-matches", icon: Brain, label: "Öğrenilen Eşleşmeler" },
    { to: "/admin/catalog-health", icon: BarChart3, label: "Katalog Sağlığı" },
    ...(adminRole === "admin" ? [{ to: "/admin/staff", icon: UserCheck, label: "Yetkili Kullanıcılar" }] : []),
    { to: "/admin/security", icon: ShieldCheck, label: "Şifreler" },
    { to: "/admin/categories", testId: ADMIN.navCategories, icon: Tag, label: "Kategori Öncelikleri" },
    { to: "/admin/customers", icon: Users, label: "Müşteriler" },
    { to: "/admin/whatsapp", icon: MessageCircle, label: "WhatsApp", badges: [
      { n: waCount.quotes, cls: "bg-[#16A34A] text-white", title: "Yeni WhatsApp teklifi" },
      { n: waCount.messages, cls: "bg-[#E8762D] text-white", title: "Okunmamış mesaj" },
    ] },
    { to: "/admin/quotes", testId: ADMIN.navQuotes, icon: FileText, label: "Teklif Talepleri", badges: [{ n: reviewCount, cls: "bg-amber-400 text-[#0F1B2D] font-bold" }] },
    { to: "/admin/notifications", icon: Bell, label: "Bildirimler", badges: [{ n: notifUnread, cls: "bg-[#E8762D] text-white", title: "Okunmamış bildirim" }] },
  ];
  const totalBadges = Number(pendingCount || 0) + Number(waCount.quotes || 0) + Number(waCount.messages || 0) + Number(reviewCount || 0) + Number(notifUnread || 0);

  const renderNav = (mini, onPick) => (
    <nav className={`flex-1 py-4 space-y-1 overflow-y-auto ${mini ? "px-2" : "px-3"}`}>
      {navItems.map((it) => (
        <SideLink key={it.to} to={it.to} end={it.end} testId={it.testId} icon={it.icon} label={it.label}
          badges={(it.badges || []).filter((b) => Number(b.n || 0) > 0)} mini={mini} onClick={onPick} />
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-[#F7F4EF]">
      {/* Sidebar (masaüstü) */}
      <aside className={`hidden lg:flex ${collapsed ? "w-[68px]" : "w-60"} shrink-0 h-screen sticky top-0 bg-[#0F1B2D] text-white flex-col transition-[width] duration-200`}>
        <div className={`${collapsed ? "px-2" : "px-4"} py-4 border-b border-white/10`}>
          {collapsed ? (
            <div className="flex justify-center">
              <img src={ADMIN_LOGO} alt="Pramid" title="PRAMID Admin" className="h-9 w-auto select-none" draggable={false} />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <img src={ADMIN_LOGO} alt="Pramid" className="h-11 w-auto select-none" draggable={false} />
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 border-l border-white/15 pl-2.5">Admin</div>
              </div>
              <Button
                data-testid={ADMIN.logoutBtn}
                onClick={logout}
                variant="ghost"
                size="sm"
                className="px-2 text-white/80 hover:bg-white/10 hover:text-white"
                title="Çıkış"
              >
                <LogOut className="w-4 h-4 mr-1" /> Çıkış
              </Button>
            </div>
          )}
        </div>

        {renderNav(collapsed)}

        <div className={`border-t border-white/10 p-2 flex ${collapsed ? "flex-col items-center gap-1" : "items-center justify-between"}`}>
          {collapsed && (
            <button type="button" onClick={logout} title="Çıkış" className="p-2 rounded-md text-white/75 hover:bg-white/10 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Menüyü aç" : "Menüyü daralt"}
            className="flex items-center gap-2 px-2 py-2 rounded-md text-xs text-white/70 hover:bg-white/10 hover:text-white"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /> Menüyü daralt</>}
          </button>
        </div>
      </aside>

      {/* Çekmece menü (telefon / tablet) */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] bg-[#0F1B2D] text-white flex flex-col shadow-2xl">
            <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src={ADMIN_LOGO} alt="Pramid" className="h-11 w-auto select-none" draggable={false} />
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 border-l border-white/15 pl-2.5">Admin</div>
              </div>
              <button type="button" onClick={() => setDrawerOpen(false)} className="p-2 rounded-md hover:bg-white/10" title="Kapat">
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderNav(false, () => setDrawerOpen(false))}
            <div className="border-t border-white/10 p-3">
              <button type="button" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-white/80 hover:bg-white/10">
                <LogOut className="w-4 h-4" /> Çıkış
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="lg:hidden sticky top-0 z-50 h-14 bg-[#0F1B2D] text-white flex items-center gap-3 px-3 shadow">
          <button type="button" onClick={() => setDrawerOpen(true)} className="relative p-2 -ml-1 rounded-md hover:bg-white/10" title="Menü">
            <Menu className="w-6 h-6" />
            {totalBadges > 0 && <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#E8762D] ring-2 ring-[#0F1B2D]" />}
          </button>
          <img src={ADMIN_LOGO} alt="Pramid" className="h-9 w-auto select-none" draggable={false} />
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 border-l border-white/15 pl-2.5">Admin</div>
          <button type="button" onClick={() => nav("/admin/notifications")} className="ml-auto relative p-2 rounded-md hover:bg-white/10" title="Bildirimler">
            <Bell className="w-5 h-5" />
            {notifUnread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E8762D] text-[10px] font-bold flex items-center justify-center">{notifUnread > 99 ? "99+" : notifUnread}</span>}
          </button>
        </div>
        <QuoteJobsBar />
        <Routes>
          <Route index element={<BrandsView />} />
          <Route path="products" element={<ProductsView />} />
          <Route path="create-quote" element={<AdminQuoteCreateView />} />
          <Route path="price-requests" element={<PriceRequestsView PageHeader={PageHeader} onChanged={loadPendingCount} />} />
          <Route path="product-memory" element={<ProductMemoryView PageHeader={PageHeader} />} />
          <Route path="learned-matches" element={<LearnedMatchesView />} />
          <Route path="staff" element={adminRole === "admin" ? <AuthorizedUsersView PageHeader={PageHeader} /> : <Navigate to="/admin" replace />} />
          <Route path="security" element={<PasswordView isAdmin={adminRole === "admin"} />} />
          <Route path="categories" element={<CategoriesView PageHeader={PageHeader} />} />
          <Route path="quotes" element={<QuotesView />} />
          <Route path="whatsapp" element={<WhatsAppView onChanged={loadWaCount} />} />
          <Route path="customers" element={<CustomersView isAdmin={adminRole === "admin"} />} />
          <Route path="catalog-health" element={<CatalogHealthView />} />
          <Route path="notifications" element={<NotificationsView isAdmin={adminRole === "admin"} />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function SideLink({ to, end = false, icon: Icon, label, badges = [], mini = false, onClick, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={onClick}
      title={mini ? label : undefined}
      className={({ isActive }) =>
        `relative flex items-center ${mini ? "justify-center px-0 py-2.5" : "gap-2.5 px-3 py-2"} rounded-md text-sm transition-colors ${
          isActive
            ? "bg-[#E8762D] text-white font-medium"
            : "text-white/75 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Icon className={mini ? "w-5 h-5" : "w-4 h-4"} />
      {mini ? (
        badges.length > 0 && <span className={`absolute top-1 right-2 w-2.5 h-2.5 rounded-full ${badges[0].cls.split(" ")[0]} ring-2 ring-[#0F1B2D]`} />
      ) : (
        <>
          <span className="flex-1 truncate">{label}</span>
          {badges.map((b, i) => (
            <span key={i} title={b.title} className={`${i ? "ml-1 " : ""}min-w-5 h-5 px-1 rounded-full text-[10px] flex items-center justify-center ${b.cls}`}>{b.n}</span>
          ))}
        </>
      )}
    </NavLink>
  );
}

function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="border-b border-[#E6E0D6] bg-[#F7F4EF] px-4 sm:px-8 py-4 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[22px] sm:text-[28px] leading-tight text-[#0F1B2D]" style={{ fontFamily: SERIF, fontWeight: 600 }}>{title}</h1>
        {subtitle && (
          <p className="text-xs text-[#4B5563] mt-0.5">{subtitle}</p>
        )}
      </div>
      {actions && <div className="min-w-0">{actions}</div>}
    </div>
  );
}


// ---------- 17.38: teklif durumu + bildirimler ----------
const SALES = {
  gonderildi: { label: "Gönderildi", cls: "border-slate-300 text-slate-700 bg-white" },
  gorusuluyor: { label: "Görüşülüyor", cls: "border-sky-300 text-sky-700 bg-sky-50" },
  onay: { label: "Onaylandı", cls: "border-emerald-500 text-white bg-emerald-600" },
  onay_nakit: { label: "Onay · nakit", cls: "border-emerald-500 text-white bg-emerald-600" },
  onay_kart: { label: "Onay · kart", cls: "border-emerald-500 text-white bg-emerald-600" },
  revize: { label: "Revize istendi", cls: "border-amber-400 text-amber-800 bg-amber-50" },
  soru: { label: "Sorusu var", cls: "border-violet-300 text-violet-700 bg-violet-50" },
  siparis: { label: "Siparişe döndü", cls: "border-emerald-700 text-white bg-emerald-800" },
  kayip: { label: "Kaybedildi", cls: "border-red-300 text-red-700 bg-red-50" },
};

function SalesBadge({ s, reason }) {
  const d = SALES[s];
  if (!d) return null;
  return <Badge variant="outline" className={d.cls} title={reason ? `Neden: ${reason}` : undefined}>{d.label}</Badge>;
}

function SalesStatusControl({ quote, onQuoteChange }) {
  const [saving, setSaving] = useState(false);
  const save = async (st) => {
    let reason = "";
    if (st === "kayip") {
      reason = window.prompt("Teklif neden kaybedildi? (ör. fiyat yüksek, başka firmadan aldı, iş iptal)", quote.sales_reason || "") || "";
      if (!reason.trim()) return;
    }
    setSaving(true);
    try {
      await adminApi.post(`/admin/quotes/${quote.id}/sales-status`, { status: st === "_" ? "" : st, reason });
      onQuoteChange?.({ ...quote, sales_status: st === "_" ? "" : st, sales_reason: reason, sales_status_at: new Date().toISOString() });
      toast.success("Teklif durumu kaydedildi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-[#6B7280]">Durum:</span>
      <Select value={quote.sales_status || "_"} onValueChange={save} disabled={saving}>
        <SelectTrigger className="h-8 w-[170px] text-xs bg-white" data-testid="sales-status">
          <SelectValue placeholder="Seçilmedi" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_">Seçilmedi</SelectItem>
          {Object.entries(SALES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {quote.sales_status === "kayip" && quote.sales_reason && (
        <span className="text-xs text-red-700 max-w-[220px] truncate" title={quote.sales_reason}>Neden: {quote.sales_reason}</span>
      )}
    </div>
  );
}

let _audioCtx = null;
function notifBeep() {
  try {
    if (window.localStorage.getItem("pramid_notif_sound") === "off") return;
  } catch { /* yok say */ }
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    _audioCtx = _audioCtx || new AC();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    [[880, 0], [1320, 0.16]].forEach(([f, t]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.22);
      o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.25);
    });
  } catch { /* ses çalınamazsa önemli değil */ }
}

const NOTIF_KIND = {
  new_quote: { label: "Yeni teklif", cls: "bg-emerald-100 text-emerald-800" },
  appended: { label: "Teklife ek", cls: "bg-sky-100 text-sky-800" },
  after_sent: { label: "Gönderilmiş teklife mesaj", cls: "bg-amber-100 text-amber-800" },
  button: { label: "Müşteri seçimi", cls: "bg-violet-100 text-violet-800" },
  chat: { label: "WhatsApp mesajı", cls: "bg-green-100 text-green-800" },
  read_failed: { label: "Okunamadı", cls: "bg-red-100 text-red-800" },
  test: { label: "Deneme", cls: "bg-slate-100 text-slate-700" },
};

const notifTarget = (n) => (n.quote_id ? `/admin/quotes?teklif=${n.quote_id}` : (n.wa_id ? "/admin/whatsapp" : "/admin/notifications"));

function useNotificationPoll(nav) {
  const [unread, setUnread] = useState(0);
  const since = useRef(null);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await adminApi.get("/admin/notifications", { params: { after: since.current || "", limit: 20 } });
        if (!alive) return;
        setUnread(Number(r.data?.unread || 0));
        const items = r.data?.items || [];
        if (since.current === null) { since.current = r.data?.now || ""; return; }
        if (!items.length) return;
        since.current = items[0].created_at;
        notifBeep();
        items.slice(0, 3).reverse().forEach((n) => {
          toast(n.title, {
            description: n.body,
            duration: 12000,
            action: { label: n.quote_id ? "Teklifi aç" : "Aç", onClick: () => nav(notifTarget(n)) },
          });
          try {
            if (document.hidden && "Notification" in window && Notification.permission === "granted") {
              const dn = new Notification(n.title, { body: n.body, tag: n.id });
              dn.onclick = () => { window.focus(); nav(notifTarget(n)); dn.close(); };
            }
          } catch { /* bazı tarayıcılar sayfadan bildirime izin vermez */ }
        });
      } catch { /* bağlantı hatası menüyü bozmasın */ }
    };
    poll();
    const t = setInterval(poll, 20000);
    const onRead = () => poll();
    window.addEventListener("pramid-notif-read", onRead);
    return () => { alive = false; clearInterval(t); window.removeEventListener("pramid-notif-read", onRead); };
  }, [nav]);
  return unread;
}

function urlB64ToUint8Array(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = window.atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function NotificationsView({ isAdmin }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [emails, setEmails] = useState("");
  const [mailOn, setMailOn] = useState(true);
  const [kinds, setKinds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [perm, setPerm] = useState(() => ("Notification" in window ? Notification.permission : "unsupported"));
  const [sound, setSound] = useState(() => { try { return window.localStorage.getItem("pramid_notif_sound") !== "off"; } catch { return true; } });
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [n, s] = await Promise.all([
        adminApi.get("/admin/notifications", { params: { limit: 100 } }),
        adminApi.get("/admin/notify-settings"),
      ]);
      setItems(n.data?.items || []);
      setSettings(s.data);
      setEmails(s.data?.emails || "");
      setMailOn(!!s.data?.mail);
      setKinds(s.data?.mail_kinds || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bildirimler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markAll = async () => {
    try {
      await adminApi.post("/admin/notifications/read", { all: true });
      setItems((p) => p.map((x) => ({ ...x, read: true })));
      window.dispatchEvent(new Event("pramid-notif-read"));
    } catch { toast.error("İşaretlenemedi."); }
  };
  const openItem = async (n) => {
    if (!n.read) {
      try { await adminApi.post("/admin/notifications/read", { ids: [n.id] }); } catch { /* yok say */ }
      window.dispatchEvent(new Event("pramid-notif-read"));
    }
    nav(notifTarget(n));
  };
  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/notify-settings", { emails, mail: mailOn, mail_kinds: kinds });
      setEmails(r.data?.emails || "");
      toast.success("Bildirim ayarları kaydedildi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };
  const askBrowser = async () => {
    if (!("Notification" in window)) return toast.error("Bu tarayıcı bildirimleri desteklemiyor.");
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") toast.success("Tarayıcı bildirimleri açık. Panel açıkken (arka planda da) bildirim gelir.");
    else toast.error("Bildirim izni verilmedi. Tarayıcı ayarlarından bu site için izin verebilirsiniz.");
  };
  const toggleSound = () => {
    const v = !sound;
    setSound(v);
    try { window.localStorage.setItem("pramid_notif_sound", v ? "on" : "off"); } catch { /* yok say */ }
    if (v) notifBeep();
  };
  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return toast.error("Bu tarayıcı telefon bildirimini desteklemiyor. iPhone'da önce Paylaş > Ana Ekrana Ekle ile siteyi ekleyip oradan açın.");
    }
    setPushBusy(true);
    try {
      const k = await adminApi.get("/admin/push/key");
      if (!k.data?.available) throw new Error(k.data?.reason || "Sunucuda telefon bildirimi kapalı.");
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p !== "granted") throw new Error("Bildirim izni verilmedi.");
      const reg = await navigator.serviceWorker.register("/pramid-sw.js");
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(k.data.key) });
      const label = `${/iphone|ipad/i.test(navigator.userAgent) ? "iPhone" : /android/i.test(navigator.userAgent) ? "Android" : "Bilgisayar"} · ${new Date().toLocaleDateString("tr-TR")}`;
      await adminApi.post("/admin/push/subscribe", { subscription: sub.toJSON(), label });
      toast.success("Bu cihaz bildirim alacak. Denemek için 'Deneme bildirimi gönder'e basın.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Cihaz kaydedilemedi.");
    } finally {
      setPushBusy(false);
    }
  };
  const removeDevice = async (id) => {
    try { await adminApi.delete(`/admin/push/subscribe/${id}`); load(); } catch { toast.error("Silinemedi."); }
  };
  const sendTest = async () => {
    try { await adminApi.post("/admin/notifications/test"); toast.success("Deneme bildirimi gönderildi (birkaç saniye içinde gelir)."); } catch { toast.error("Gönderilemedi."); }
  };
  const unread = items.filter((x) => !x.read).length;

  return (
    <div>
      <PageHeader title="Bildirimler" subtitle="Yeni teklif, müşteri onayı / revize, gönderilmiş teklife gelen mesaj ve okunamayan listeler"
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={sendTest}><Bell className="w-4 h-4 mr-1" /> Deneme bildirimi gönder</Button>
          <Button variant="outline" onClick={markAll} disabled={!unread}><CheckCheck className="w-4 h-4 mr-1" /> Tümünü okundu say</Button>
        </div>} />
      <div className="p-4 sm:p-8 grid grid-cols-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_380px] gap-6">
        <Card className="bg-white border-[#E6E0D6] overflow-hidden">
          {loading ? <div className="p-6 text-sm text-[#6B7280]">Yükleniyor…</div> : !items.length ? (
            <div className="p-6 text-sm text-[#6B7280]">Henüz bildirim yok.</div>
          ) : (
            <ul className="divide-y divide-[#F0EBE3]">
              {items.map((n) => (
                <li key={n.id}>
                  <button type="button" onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 hover:bg-[#FBF9F5] flex gap-3 ${n.read ? "" : "bg-orange-50/60"}`}>
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#E8762D]"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${(NOTIF_KIND[n.kind] || NOTIF_KIND.test).cls}`}>{(NOTIF_KIND[n.kind] || { label: n.kind }).label}</span>
                        <span className="font-medium text-sm text-[#0F1B2D]">{n.title}</span>
                      </span>
                      {n.body && <span className="block text-xs text-[#4B5563] mt-0.5 break-words">{n.body}</span>}
                      <span className="block text-[11px] text-[#9EA1A8] mt-0.5">{fmtDate(n.created_at)}{n.mailed ? " · e-posta gönderildi" : ""}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="bg-white border-[#E6E0D6] p-4 space-y-3">
            <div className="font-semibold text-[#0F1B2D]">Bu cihaz</div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span>Tarayıcı bildirimi: <b>{perm === "granted" ? "açık" : perm === "denied" ? "engellendi" : perm === "unsupported" ? "desteklenmiyor" : "kapalı"}</b></span>
              {perm !== "granted" && perm !== "unsupported" && <Button size="sm" variant="outline" onClick={askBrowser}>Aç</Button>}
            </div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span>Sesli uyarı: <b>{sound ? "açık" : "kapalı"}</b></span>
              <Button size="sm" variant="outline" onClick={toggleSound}>{sound ? "Kapat" : "Aç"}</Button>
            </div>
            <div className="border-t border-[#F0EBE3] pt-3">
              <div className="text-sm mb-2">Telefon / bilgisayar bildirimi (panel kapalıyken de gelir)</div>
              <Button size="sm" className="bg-[#E8762D] hover:bg-[#d56a26] text-white" onClick={enablePush} disabled={pushBusy || settings?.push_available === false}>
                <Phone className="w-4 h-4 mr-1" /> {pushBusy ? "Kaydediliyor…" : "Bu cihaza bildirim gönder"}
              </Button>
              {settings?.push_available === false && (
                <p className="text-xs text-amber-700 mt-2">{settings?.test_mode ? "Deneme sunucusunda telefon bildirimi kapalı." : "Sunucuda telefon bildirimi paketi kurulu değil (kurulum notuna bakın). Panel ve e-posta bildirimleri çalışır."}</p>
              )}
              <p className="text-[11px] text-[#6B7280] mt-2">iPhone: önce Safari'de Paylaş &gt; Ana Ekrana Ekle, sonra ana ekrandaki simgeden açıp bu düğmeye basın.</p>
              {!!(settings?.devices || []).length && (
                <ul className="mt-2 space-y-1">
                  {settings.devices.map((d) => (
                    <li key={d.id} className="flex items-center justify-between text-xs bg-[#FBF9F5] rounded px-2 py-1">
                      <span>{d.label || "Cihaz"}</span>
                      <button type="button" className="text-red-600 hover:underline" onClick={() => removeDevice(d.id)}>kaldır</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <Card className="bg-white border-[#E6E0D6] p-4 space-y-3">
            <div className="font-semibold text-[#0F1B2D]">E-posta bildirimi</div>
            {!settings?.smtp && <p className="text-xs text-amber-700">Sunucuda e-posta (SMTP) ayarı yok; e-posta gönderilemez.</p>}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={mailOn} onChange={(e) => setMailOn(e.target.checked)} disabled={!isAdmin} /> E-posta ile bildir
            </label>
            <div>
              <Label className="text-xs">Adres(ler) — virgülle birden fazla</Label>
              <Input value={emails} onChange={(e) => setEmails(e.target.value)} disabled={!isAdmin} placeholder="ornek@firma.com" />
            </div>
            <div className="space-y-1">
              <div className="text-xs text-[#6B7280]">Hangi durumlarda e-posta gelsin?</div>
              {Object.entries(settings?.kinds || {}).filter(([k]) => k !== "test").map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" disabled={!isAdmin} checked={kinds.includes(k)}
                    onChange={(e) => setKinds((p) => (e.target.checked ? [...p, k] : p.filter((x) => x !== k)))} /> {v}
                </label>
              ))}
            </div>
            {isAdmin ? (
              <Button onClick={saveSettings} disabled={saving} className="bg-[#0F1B2D] hover:bg-[#1c2c44] text-white">{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
            ) : <p className="text-xs text-[#6B7280]">Ayarları yalnız ana yönetici değiştirebilir.</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}


// ---------- 17.39: üyelik / KVKK ayarları + katalog sağlığı ----------
function CustomerSettingsCard({ isAdmin }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [req, setReq] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    adminApi.get("/admin/customer-settings").then((r) => { setS(r.data); setText(r.data?.kvkk_text || ""); setReq(!!r.data?.require_verify); }).catch(() => {});
  }, []);
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/customer-settings", { require_verify: req, kvkk_text: text });
      setS((p) => ({ ...p, ...r.data }));
      toast.success("Ayarlar kaydedildi.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); } finally { setSaving(false); }
  };
  const active = s.require_verify && s.whatsapp_configured && !s.test_mode;
  return (
    <Card className="border-[#E6E0D6] p-3 sm:p-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center justify-between gap-2 text-left">
        <span className="font-semibold text-[#0F1B2D]">Üyelik ve KVKK</span>
        <span className="text-xs text-[#4B5563]">
          WhatsApp ile telefon doğrulaması: <b className={active ? "text-emerald-700" : "text-amber-700"}>{active ? "açık" : "kapalı"}</b>
          {!s.whatsapp_configured && " (WhatsApp yapılandırılmadı)"} · KVKK metni: {s.kvkk_custom ? "özel" : "varsayılan"} · {open ? "gizle" : "düzenle"}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={req} disabled={!isAdmin} onChange={(e) => setReq(e.target.checked)} />
            <span>Müşteri ilk girişte telefonunu WhatsApp ile doğrulasın (cihaz 90 gün hatırlanır). Kapalıysa müşteri doğrulamadan teklif alabilir.</span>
          </label>
          <div>
            <Label className="text-xs">KVKK aydınlatma metni (giriş sayfasında "Aydınlatma Metni" bağlantısıyla gösterilir)</Label>
            <Textarea value={text} disabled={!isAdmin} onChange={(e) => setText(e.target.value)} rows={10} className="text-xs" />
            <p className="text-[11px] text-amber-700 mt-1">Yayına almadan önce metni bir hukukçuya kontrol ettirin.</p>
          </div>
          {isAdmin ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} className="bg-[#0F1B2D] hover:bg-[#1c2c44] text-white">{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
              <Button variant="outline" onClick={() => setText(s.default_kvkk || "")}>Varsayılan metne dön</Button>
            </div>
          ) : <p className="text-xs text-[#6B7280]">Ayarları yalnız ana yönetici değiştirebilir.</p>}
        </div>
      )}
    </Card>
  );
}

const HEALTH_TABS = [
  ["unmatched", "Bulunamayanlar"], ["duplicates", "Çift kayıtlar"], ["priceless", "Fiyatsız ürünler"],
  ["unused", "Kullanılmayanlar"], ["learned", "Öğrenilen eşleşmeler"], ["orphans", "Sorunlu öğrenmeler"],
];

function CatalogHealthView() {
  const nav = useNavigate();
  const [days, setDays] = useState("90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("unmatched");
  const [filter, setFilter] = useState("");
  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/catalog-health", { params: { days, refresh }, timeout: 180000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Yüklenemedi."); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const ignore = async (key) => {
    try { await adminApi.post("/admin/catalog-health/ignore", { key }); setData((d) => ({ ...d, unmatched: d.unmatched.filter((g) => g.key !== key) })); }
    catch { toast.error("Kaydedilemedi."); }
  };
  const delProduct = async (p) => {
    if (!window.confirm(`${p.code} — ${p.name} silinsin mi?\nEski tekliflerdeki satırlar değişmez; bu ürüne öğretilmiş eşleşmeler silinir.`)) return;
    try { await adminApi.delete(`/admin/products/${p.id}`); toast.success("Ürün silindi."); load(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi."); }
  };
  const delLearned = async (k) => {
    if (!window.confirm("Bu öğrenilmiş eşleşme silinsin mi?")) return;
    try { await adminApi.delete("/admin/learned-matches", { params: { key: k } }); toast.success("Silindi."); load(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi."); }
  };
  const f = filter.trim().toLocaleLowerCase("tr");
  const match = (s) => !f || String(s || "").toLocaleLowerCase("tr").includes(f);
  const c = data?.counts || {};
  const money = (p) => (p.currency && p.currency !== "TRY" ? `${p.list_price} ${p.currency}` : fmtTRY(p.list_price));
  const cell = "px-3 py-2 align-top";
  const head = "px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold";
  const openQuote = (id) => nav(`/admin/quotes?teklif=${id}`);

  return (
    <div>
      <PageHeader title="Katalog Sağlığı" subtitle="Kataloğa ne eklemeli, neyi temizlemeli: tekliflerden ve katalogdan otomatik çıkarılır"
        actions={<div className="flex flex-wrap gap-2 items-center">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Son 30 gün</SelectItem><SelectItem value="90">Son 90 gün</SelectItem>
              <SelectItem value="180">Son 180 gün</SelectItem><SelectItem value="365">Son 1 yıl</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => load(true)} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile</Button>
        </div>} />
      <div className="p-3 sm:p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {HEALTH_TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-full text-sm border ${tab === k ? "bg-[#0F1B2D] text-white border-[#0F1B2D]" : "bg-white border-[#E6E0D6] text-[#0F1B2D] hover:bg-[#F1ECE4]"}`}>
              {label} <span className={`ml-1 text-xs ${tab === k ? "text-white/70" : "text-[#9EA1A8]"}`}>{c[k] ?? "…"}</span>
            </button>
          ))}
        </div>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Ara…" className="bg-white max-w-sm" />
        <Card className="bg-white border-[#E6E0D6] overflow-x-auto">
          {loading && !data ? <div className="p-6 text-sm text-[#6B7280]">Hesaplanıyor… (büyük katalogda birkaç saniye sürebilir)</div> : !data ? null : (
            <>
              {tab === "unmatched" && (
                <table className="w-full text-sm">
                  <thead className="bg-[#FBF9F5]"><tr><th className={head}>Aranan (örnek yazım)</th><th className={head}>Kaç kez</th><th className={head}>Müşteri</th><th className={head}>Son</th><th className={head} /></tr></thead>
                  <tbody className="divide-y divide-[#F0EBE3]">
                    {data.unmatched.filter((g) => match(g.example) || match(g.key)).map((g) => (
                      <tr key={g.key}>
                        <td className={cell}><div className="font-medium text-[#0F1B2D] break-words">{g.example}</div>{g.reason && <div className="text-[11px] text-[#9EA1A8]">{g.reason}</div>}</td>
                        <td className={cell}><b>{g.count}</b></td><td className={cell}>{g.customers}</td>
                        <td className={`${cell} text-xs text-[#6B7280] whitespace-nowrap`}>{fmtDate(g.last)}</td>
                        <td className={`${cell} whitespace-nowrap text-right`}>
                          <Button size="sm" variant="outline" onClick={() => openQuote(g.quote_id)}>Teklifte aç</Button>
                          <Button size="sm" variant="ghost" onClick={() => ignore(g.key)} className="text-[#6B7280]">Yok say</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === "duplicates" && (
                <div className="divide-y divide-[#F0EBE3]">
                  {data.duplicates.filter((d) => d.products.some((p) => match(p.name) || match(p.code))).map((d, i) => (
                    <div key={i} className="p-3">
                      <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-1">{d.reason}</div>
                      {d.products.map((p) => (
                        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-1 text-sm">
                          <span className="min-w-0"><b>{p.code}</b> · {p.name} <span className="text-[#6B7280]">· {p.brand} · {money(p)}</span></span>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delProduct(p)}><Trash2 className="w-4 h-4 mr-1" /> Sil</Button>
                        </div>
                      ))}
                    </div>
                  ))}
                  {!data.duplicates.length && <div className="p-6 text-sm text-[#6B7280]">Çift kayıt yok.</div>}
                </div>
              )}
              {(tab === "priceless" || tab === "unused") && (
                <table className="w-full text-sm">
                  <thead className="bg-[#FBF9F5]"><tr><th className={head}>Kod</th><th className={head}>Ürün</th><th className={head}>Marka</th>
                    {tab === "priceless" ? <th className={head}>Fiyat beklettiği teklif</th> : <th className={head}>Liste</th>}</tr></thead>
                  <tbody className="divide-y divide-[#F0EBE3]">
                    {data[tab].filter((p) => match(p.name) || match(p.code) || match(p.brand)).slice(0, 500).map((p) => (
                      <tr key={p.id}><td className={`${cell} whitespace-nowrap`}>{p.code}</td><td className={cell}>{p.name}</td><td className={cell}>{p.brand}</td>
                        <td className={cell}>{tab === "priceless" ? (p.pending_quotes ? <b className="text-amber-700">{p.pending_quotes}</b> : "—") : money(p)}</td></tr>
                    ))}
                  </tbody>
                  {tab === "unused" && <caption className="caption-bottom text-xs text-[#6B7280] p-2 text-left">Son 180 günde hiçbir teklifte geçmeyen aktif ürünler ({data.unused_total}). İlk 500 gösteriliyor.</caption>}
                </table>
              )}
              {(tab === "learned" || tab === "orphans") && (
                <table className="w-full text-sm">
                  <thead className="bg-[#FBF9F5]"><tr><th className={head}>Müşteri yazımı</th><th className={head}>Öğretilen ürün</th><th className={head}>Kullanım</th><th className={head}>Durum</th><th className={head} /></tr></thead>
                  <tbody className="divide-y divide-[#F0EBE3]">
                    {data[tab].filter((r) => match(r.query) || match(r.product_name)).map((r) => (
                      <tr key={r.key}>
                        <td className={cell}>{r.query}</td><td className={cell}>{r.product_name} <span className="text-[#6B7280]">· {r.brand_name}</span></td>
                        <td className={`${cell} whitespace-nowrap`}>{r.uses ? <><b>{r.uses}</b> <span className="text-xs text-[#6B7280]">son {fmtDate(r.last_used)}</span></> : <span className="text-[#9EA1A8]">kullanılmadı</span>}</td>
                        <td className={cell}>{r.problem ? <span className="text-red-700 text-xs">{r.problem}</span> : <span className="text-emerald-700 text-xs">tamam</span>}</td>
                        <td className={`${cell} text-right`}><Button size="sm" variant="ghost" className="text-red-600" onClick={() => delLearned(r.key)}><Trash2 className="w-4 h-4" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Card>
        <p className="text-xs text-[#6B7280]">Bulunamayanlar: seçilen dönemde eşleşmeyen satırlar, adetsiz yazıma göre gruplanır. "Teklifte aç" ile o satırı ürüne bağlayıp öğretebilirsiniz; "Yok say" listeden kaldırır.</p>
      </div>
    </div>
  );
}


// ---------- 17.40: alıştan iskonto hesabı ----------
const BUY_METHODS = [
  ["nakit", "Nakit alıyorum"], ["vadeli", "90 gün vadeli alıyorum"], ["kart", "Kredi kartıyla alıyorum"],
];

function PricingCalcDialog({ brand, purchase, fromExcel, onClose, onSaved }) {
  const p0 = purchase || {};
  const [step, setStep] = useState(fromExcel ? "ask" : "form");
  const [f, setF] = useState({
    method: p0.method || "", supplier: p0.supplier || "",
    buy_discount: p0.buy_discount ?? "", margin: p0.margin ?? "", extra: p0.extra || "", card_commission: p0.card_commission ?? "",
  });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(String(v).replace(",", ".")));
  const body = () => ({ method: f.method, supplier: f.supplier, buy_discount: num(f.buy_discount), margin: num(f.margin),
    extra: num(f.extra), card_commission: num(f.card_commission) });
  const ready = f.method && f.buy_discount !== "" && f.margin !== "" && f.card_commission !== "";
  useEffect(() => {
    if (!brand || step !== "form" || !ready) { setRes(null); return undefined; }
    const t = setTimeout(async () => {
      try { const r = await adminApi.post(`/admin/brands/${brand.id}/pricing-calc`, body()); setRes(r.data); }
      catch (e) { setRes({ error: e?.response?.data?.detail || "Hesaplanamadı." }); }
    }, 250);
    return () => clearTimeout(t);
  }, [brand?.id, step, f.method, f.buy_discount, f.margin, f.extra, f.card_commission]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!brand) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/brands/${brand.id}/pricing-apply`, body());
      toast.success(`${brand.name}: nakit %${r.data.cash_discount}, kart %${r.data.card_discount} kaydedildi.`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); } finally { setBusy(false); }
  };
  const field = (k, label, hint) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6B7280]">%</span>
        <Input inputMode="decimal" value={f[k]} onChange={set(k)} className="pl-7" placeholder="0" />
      </div>
      {hint && <p className="text-[11px] text-[#6B7280] mt-0.5">{hint}</p>}
    </div>
  );
  const ex = res?.example;
  return (
    <Dialog open={!!brand} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: SERIF }} className="text-xl text-[#0F1B2D]">{brand.name} — alıştan iskonto</DialogTitle>
          <DialogDescription>Yalnız sizin yazdığınız rakamlar kullanılır; kaydetmeden önce sonucu görürsünüz.</DialogDescription>
        </DialogHeader>
        {step === "ask" ? (
          <div className="space-y-4">
            <p className="text-sm text-[#1F2937]">Fiyat listesi yüklendi. Bu markanın <b>alış iskontosu</b> ya da alış şekli değişti mi?</p>
            {p0.method && <p className="text-xs text-[#6B7280]">Son kayıt: {BUY_METHODS.find(([k]) => k === p0.method)?.[1]} · alış %{p0.buy_discount} · kâr %{p0.margin}{p0.supplier ? ` · ${p0.supplier}` : ""}</p>}
            <div className="flex gap-2">
              <Button onClick={() => setStep("form")} className="bg-[#E8762D] hover:bg-[#d56a26] text-white">Evet, yeniden hesapla</Button>
              <Button variant="outline" onClick={onClose}>Değişmedi</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Bu markayı nasıl alıyorsunuz?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                {BUY_METHODS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setF((x) => ({ ...x, method: k }))}
                    className={`px-3 py-2 rounded-md border text-sm text-left ${f.method === k ? "border-[#E8762D] bg-orange-50 text-[#0F1B2D] font-medium" : "border-[#E6E0D6] hover:bg-[#F7F4EF]"}`}>{label}</button>
                ))}
              </div>
            </div>
            {f.method && (
              <>
                <div>
                  <Label className="text-xs">Nereden alıyorsunuz? (tedarikçi, isteğe bağlı)</Label>
                  <Input value={f.supplier} onChange={set("supplier")} placeholder="ör. Kalde Ankara bayi" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {field("buy_discount", "Alış iskontosu", "Tedarikçinin size yaptığı iskonto")}
                  {field("margin", f.method === "nakit" ? "Kâr marjı" : "Kâr", "Maliyetin üstüne nakit satışta")}
                  {f.method === "vadeli" && field("extra", "Vade farkı", "Yalnız kart fiyatına eklenir (boş = 0)")}
                  {f.method === "kart" && field("extra", "Alış kart komisyonu", "Yalnız kart fiyatına eklenir (boş = 0)")}
                  {field("card_commission", "Kart komisyonu (satış)", "Bankanın sizden kestiği; kesildikten sonra elinize tam kalır")}
                </div>
                {res?.error && <p className="text-sm text-red-700">{res.error}</p>}
                {res && !res.error && (
                  <div className="rounded-lg border border-[#E6E0D6] bg-[#FBF9F5] p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-md bg-white border border-[#E6E0D6] p-2">
                        <div className="text-[10px] uppercase tracking-wide text-[#6B7280]">Nakit iskonto</div>
                        <div className="text-lg font-bold text-[#0F1B2D]">%{res.current?.cash_discount} → <span className="text-[#E8762D]">%{res.cash_discount}</span></div>
                        <div className="text-[10px] text-[#9EA1A8]">hesap %{res.raw_cash} (aşağı yuvarlandı)</div>
                      </div>
                      <div className="rounded-md bg-white border border-[#E6E0D6] p-2">
                        <div className="text-[10px] uppercase tracking-wide text-[#6B7280]">Kart iskonto</div>
                        <div className="text-lg font-bold text-[#0F1B2D]">%{res.current?.card_discount} → <span className="text-[#E8762D]">%{res.card_discount}</span></div>
                        <div className="text-[10px] text-[#9EA1A8]">hesap %{res.raw_card} (aşağı yuvarlandı)</div>
                      </div>
                    </div>
                    {ex && (
                      <p className="text-xs text-[#4B5563]">
                        Örnek: liste {fmtTRY(ex.list)} → maliyet {fmtTRY(ex.cost)} · nakit satış {fmtTRY(ex.cash_price)} (kâr {fmtTRY(ex.cash_profit)}, %{ex.cash_profit_pct}) ·
                        kart satış {fmtTRY(ex.card_price)}, komisyon sonrası elinize {fmtTRY(ex.card_net)} (kâr {fmtTRY(ex.card_profit)}).
                      </p>
                    )}
                    {(res.warnings || []).map((w) => <p key={w} className="text-xs text-red-700">{w}</p>)}
                    <p className="text-[11px] text-[#6B7280]">Ürüne özel iskontolar değişmez. Mevcut tekliflere yansıtmak için teklifte "Katalogdan güncelle".</p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>Vazgeç</Button>
                  <Button onClick={save} disabled={!res || res.error || busy} className="bg-[#0F1B2D] hover:bg-[#1c2c44] text-white">
                    {busy ? "Kaydediliyor…" : "Onayla ve kaydet"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------- 17.42: marka içi iskonto grupları ----------
function GroupDiscountDialog({ open, brand, products, onClose, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [name, setName] = useState("");
  const [cash, setCash] = useState("");
  const [card, setCard] = useState("");
  const [busy, setBusy] = useState(false);
  const loadGroups = () => brand && adminApi.get(`/admin/brands/${brand.id}/discount-groups`).then((r) => setGroups(r.data?.groups || [])).catch(() => {});
  useEffect(() => { if (open) { loadGroups(); setQ(""); setPicked(new Set()); setName(""); setCash(""); setCard(""); } }, [open, brand?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || !brand) return null;
  const words = q.trim().toLocaleLowerCase("tr").split(/\s+/).filter(Boolean);
  const found = words.length ? products.filter((p) => { const t = `${p.code || ""} ${p.name || ""}`.toLocaleLowerCase("tr"); return words.every((w) => t.includes(w)); }) : [];
  const own = (p) => p.default_cash_discount !== null && p.default_cash_discount !== undefined && p.default_cash_discount !== "";
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pickAllFound = () => setPicked((s) => { const n = new Set(s); found.forEach((p) => n.add(p.id)); return n; });
  const editGroup = (g) => {
    setPicked(new Set(g.product_ids)); setName(g.group);
    setCash(g.cash_discount ?? ""); setCard(g.card_discount ?? ""); setQ("");
  };
  const pickedList = products.filter((p) => picked.has(p.id));
  const apply = async (clear = false) => {
    if (!picked.size) return toast.error("Önce ürün seçin.");
    const msg = clear
      ? `${picked.size} ürünün özel iskontosu kaldırılacak; marka iskontosuna (%${brand.cash_discount} / %${brand.card_discount}) dönecek. Devam edilsin mi?`
      : `${picked.size} ürüne nakit %${cash}, kart %${card} iskonto uygulanacak${name ? ` ("${name}" grubu)` : ""}. Devam edilsin mi?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/brands/${brand.id}/discount-groups/apply`, {
        product_ids: [...picked], group: name, cash_discount: clear ? null : Number(String(cash).replace(",", ".")),
        card_discount: clear ? null : Number(String(card).replace(",", ".")), clear,
      });
      toast.success(`${r.data?.updated || 0} ürün güncellendi. Eski tekliflere yansıtmak için teklifte "Katalogdan güncelle".`);
      onSaved?.();
      loadGroups();
      setPicked(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); } finally { setBusy(false); }
  };
  const row = (p, checked) => (
    <label key={p.id} className="flex items-start gap-2 py-1.5 text-sm cursor-pointer">
      <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggle(p.id)} />
      <span className="min-w-0 flex-1"><b className="text-[#0F1B2D]">{p.code}</b> {p.name}</span>
      <span className="text-xs whitespace-nowrap text-[#6B7280]">{own(p) ? <span className="text-[#E8762D]">özel %{p.default_cash_discount}/%{p.default_card_discount}{p.discount_group ? ` · ${p.discount_group}` : ""}</span> : "marka"}</span>
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: SERIF }} className="text-xl text-[#0F1B2D]">{brand.name} — iskonto grupları</DialogTitle>
          <DialogDescription>Markanın iskontosu %{brand.cash_discount} / %{brand.card_discount}. Farklı iskontolu ürünleri seçip gruba özel iskonto verin; fiyat listesi yeniden yüklense de korunur.</DialogDescription>
        </DialogHeader>
        {!!groups.length && (
          <div className="rounded-lg border border-[#E6E0D6] divide-y divide-[#F0EBE3]">
            {groups.map((g) => (
              <div key={g.group} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span><b>{g.group}</b> · {g.count} ürün · {g.mixed ? "karışık iskonto" : `nakit %${g.cash_discount} / kart %${g.card_discount}`}</span>
                <Button size="sm" variant="outline" onClick={() => editGroup(g)}>Seç / düzenle</Button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Ürün ara (adında geçen kelimeler, ör. "galvaniz dirsek")</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kelime yazın…" />
            {words.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-[#6B7280]"><span>{found.length} ürün bulundu</span>
                  {!!found.length && <button type="button" className="underline" onClick={pickAllFound}>Hepsini seç</button>}</div>
                <div className="max-h-64 overflow-y-auto mt-1">{found.slice(0, 300).map((p) => row(p, picked.has(p.id)))}</div>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-[#6B7280] mb-1">Seçilenler: <b className="text-[#0F1B2D]">{picked.size}</b> ürün {picked.size > 0 && <button type="button" className="underline ml-2" onClick={() => setPicked(new Set())}>temizle</button>}</div>
            <div className="max-h-64 overflow-y-auto rounded border border-[#F0EBE3] px-2">{pickedList.length ? pickedList.map((p) => row(p, true)) : <div className="text-xs text-[#9EA1A8] py-3">Soldan arayıp ürün seçin.</div>}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Grup adı</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ör. Galvaniz patent" /></div>
          <div><Label className="text-xs">Nakit iskonto %</Label><Input inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} /></div>
          <div><Label className="text-xs">Kart iskonto %</Label><Input inputMode="decimal" value={card} onChange={(e) => setCard(e.target.value)} /></div>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" disabled={busy || !picked.size} onClick={() => apply(true)}>Seçilenlerin özel iskontosunu kaldır</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Kapat</Button>
            <Button className="bg-[#0F1B2D] hover:bg-[#1c2c44] text-white" disabled={busy || !picked.size || cash === "" || card === ""} onClick={() => apply(false)}>
              {busy ? "Kaydediliyor…" : `${picked.size} ürüne uygula`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- 17.35: döviz ----------
const CUR_SYMBOL = { TRY: "₺", USD: "$", EUR: "€" };
const CUR_LABEL = { TRY: "TL (₺)", USD: "Dolar ($)", EUR: "Euro (€)" };
const fmtCur = (cur, v) => {
  const n = Number(v || 0);
  if (!cur || cur === "TRY") return fmtTRY(n);
  return `${CUR_SYMBOL[cur] || cur} ${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtRate = (r) => Number(r || 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const quoteFxRates = (items) => {
  const out = {};
  (items || []).forEach((it) => {
    const fx = it?.fx;
    if (fx && fx.rate && it.matched) {
      const prev = out[fx.cur];
      if (!prev || String(fx.date || "") >= String(prev.date || "")) out[fx.cur] = fx;
    }
  });
  return out;
};
const fxDateTR = (d) => { try { return new Date(d).toLocaleDateString("tr-TR"); } catch { return String(d || ""); } };

function FxCard() {
  const [fx, setFx] = useState(null);
  const [form, setForm] = useState({ usd: "", eur: "", margin: "" });
  const [busy, setBusy] = useState(false);
  const apply = (d) => {
    setFx(d);
    setForm({
      usd: d?.manual_today && d?.manual?.USD ? String(d.manual.USD).replace(".", ",") : "",
      eur: d?.manual_today && d?.manual?.EUR ? String(d.manual.EUR).replace(".", ",") : "",
      margin: String(d?.margin_pct ?? 0).replace(".", ","),
    });
  };
  const load = async () => { try { const r = await adminApi.get("/admin/fx"); apply(r.data); } catch { /* sessiz */ } };
  useEffect(() => { load(); }, []);
  const num = (v) => { const t = String(v || "").trim().replace(",", "."); return t === "" ? null : Number(t); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/fx", { manual_usd: num(form.usd), manual_eur: num(form.eur), margin_pct: num(form.margin) ?? 0 });
      apply(r.data); toast.success("Kur ayarı kaydedildi.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try { const r = await adminApi.post("/admin/fx/refresh"); apply(r.data); toast.success("Merkez Bankası kuru alındı."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kur alınamadı."); }
    finally { setBusy(false); }
  };
  const eff = fx?.effective || {};
  return (
    <Card className="border-[#E6E0D6] p-4 sm:p-5 mb-5">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        <div className="min-w-0 lg:w-72">
          <div className="text-sm font-semibold text-[#0F1B2D]">Döviz Kurları</div>
          <div className="text-xs text-[#6B6F78] mt-0.5">
            Merkez Bankası döviz satış: {fx?.tcmb?.USD ? <>USD {fmtRate(fx.tcmb.USD)} · EUR {fmtRate(fx.tcmb.EUR)} ({fxDateTR(fx.tcmb_date)})</> : "henüz alınmadı"}
          </div>
          {fx?.fetch_error && <div className="text-xs text-red-600 mt-0.5">Son deneme başarısız: {fx.fetch_error}</div>}
          <div className="mt-2 flex gap-2 flex-wrap">
            {["USD", "EUR"].map((c) => (
              <span key={c} className="text-xs rounded-md border border-[#E6E0D6] bg-[#F7F4EF] px-2 py-1">
                <b>{c}</b> {eff[c] ? <>{fmtRate(eff[c].rate)} TL <span className="text-[#9EA1A8]">({eff[c].source})</span></> : <span className="text-red-600">kur yok</span>}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 flex-1">
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wide">Bugün USD (elle)</Label><Input value={form.usd} onChange={(e) => setForm({ ...form, usd: e.target.value })} placeholder="boş = TCMB" /></div>
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wide">Bugün EUR (elle)</Label><Input value={form.eur} onChange={(e) => setForm({ ...form, eur: e.target.value })} placeholder="boş = TCMB" /></div>
          <div className="space-y-1"><Label className="text-[10px] uppercase tracking-wide">Kur farkı payı %</Label><Input value={form.margin} onChange={(e) => setForm({ ...form, margin: e.target.value })} placeholder="0" /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> TCMB'den al</Button>
          <Button size="sm" onClick={save} disabled={busy} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">Kaydet</Button>
        </div>
      </div>
      <p className="text-[11px] text-[#9EA1A8] mt-3">Elle girilen kur yalnız bugün geçerlidir; yarın yeniden Merkez Bankası kuru kullanılır. Teklif hazırlandığı günün kuruyla kalır; teklif ekranındaki "Kuru güncelle" ile bugünün kuruna çevrilir.</p>
    </Card>
  );
}

const CurrencySelect = ({ value, onChange, allowInherit, inheritLabel }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 rounded-md border border-[#E6E0D6] bg-white px-3 text-sm">
    {allowInherit && <option value="">{inheritLabel || "Markanın para birimi"}</option>}
    {["TRY", "USD", "EUR"].map((c) => <option key={c} value={c}>{CUR_LABEL[c]}</option>)}
  </select>
);


// ---------- 17.37: Katalogdan güncelle (önizleme + uygula) ----------
const REFRESH_KIND = { price: "Fiyat / iskonto değişti", filled: "Fiyatı girildi", matched: "Yeni eşleşti" };
function CatalogRefreshDialog({ state, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const { loading, single, bulk, error } = state;
  const tokens = single ? (single.token ? [single.token] : []) : (bulk || []).filter((q) => q.token).map((q) => q.token);
  const apply = async () => {
    if (!tokens.length) return onClose();
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/catalog-refresh/apply", { tokens }, { timeout: 120000 });
      if (r.data?.stale) toast.error(`${r.data.stale} teklif önizlemeden sonra değişti; yeniden "Güncelle" deyin.`);
      if (r.data?.applied) toast.success(`${r.data.applied} teklif güncellendi.`);
      onApplied?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi."); }
    finally { setBusy(false); }
  };
  const money = (v) => fmtTRY(v || 0);
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-white w-[calc(100vw-12px)] sm:w-full max-w-3xl max-h-[90dvh] overflow-y-auto grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D]">Katalogdan güncelle</DialogTitle>
          <DialogDescription>Katalogdaki güncel fiyat, iskonto ve kurla karşılaştırıldı. Elle verdiğiniz satır fiyatları korunur; toplu iskonto verdiğiniz satırlarda iskonto kalır, liste fiyatı güncellenir.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-[#6B6F78]"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Katalog kontrol ediliyor… (eşleşmeyen satırlar yeniden aranıyor, uzun sürebilir)</div>
        ) : error ? (
          <div className="py-6 text-sm text-red-600">{error}</div>
        ) : single ? (
          <div className="space-y-3">
            {single.sent && <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">Bu teklif müşteriye gönderilmiş. Güncellerseniz yeni hâlini yeniden göndermeyi unutmayın.</div>}
            {!single.changes.length ? (
              <div className="py-6 text-center text-sm text-[#6B6F78]">Değişiklik yok; teklif katalogla aynı.{single.kept_overrides ? ` (${single.kept_overrides} satırda elle verilen fiyat korunuyor)` : ""}</div>
            ) : (
              <>
                <div className="overflow-x-auto scroll-soft border border-[#E6E0D6] rounded-md">
                  <table className="w-full text-xs min-w-[620px]">
                    <thead className="bg-[#F7F4EF]"><tr><Th>Satır</Th><Th>Değişiklik</Th><Th className="text-right">Liste</Th><Th className="text-right">İsk. N/K</Th><Th className="text-right">Nakit tutar</Th></tr></thead>
                    <tbody>
                      {single.changes.map((c) => (
                        <tr key={c.index} className="border-b border-[#F1ECE4] align-top">
                          <td className="px-3 py-2"><div className="text-[#6B6F78]">{c.searched}</div>{c.new.name && <div className="font-medium text-[#1F2937]">{c.new.name}</div>}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{REFRESH_KIND[c.kind] || c.kind}{c.fx ? ` (${c.fx})` : ""}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{c.old.pending || c.kind === "matched" ? "—" : money(c.old.list)} → <b>{money(c.new.list)}</b></td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">%{c.old.cash_discount}/{c.old.card_discount} → <b>%{c.new.cash_discount}/{c.new.card_discount}</b></td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{money(c.old.line_cash)} → <b>{money(c.new.line_cash)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="border border-[#E6E0D6] rounded p-2">Nakit toplam: {money(single.totals_old?.cash)} → <b className="text-[#E8762D]">{money(single.totals_new?.cash)}</b></div>
                  <div className="border border-[#E6E0D6] rounded p-2">Kart toplam: {money(single.totals_old?.card)} → <b>{money(single.totals_new?.card)}</b></div>
                </div>
                <div className="text-xs text-[#6B6F78]">Eşleşmeyen: {single.unmatched_old ?? 0} → {single.unmatched_new ?? 0} · Fiyat bekleyen: {single.pending_old ?? 0} → {single.pending_new ?? 0}{single.kept_overrides ? ` · Elle fiyatlı ${single.kept_overrides} satır korunuyor` : ""}</div>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto scroll-soft border border-[#E6E0D6] rounded-md">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="bg-[#F7F4EF]"><tr><Th>Teklif</Th><Th className="text-right">Değişen satır</Th><Th className="text-right">Nakit toplam</Th><Th></Th></tr></thead>
              <tbody>
                {(bulk || []).map((q) => (
                  <tr key={q.quote_id} className="border-b border-[#F1ECE4]">
                    <td className="px-3 py-2"><div className="font-medium">{q.customer?.name || "—"} <span className="text-[#9EA1A8] font-mono">{String(q.quote_id).slice(0, 8).toUpperCase()}</span></div><div className="text-[#6B6F78]">{q.customer?.company || ""}</div></td>
                    <td className="px-3 py-2 text-right">{q.error ? <span className="text-red-600">hata</span> : q.changes}{q.matched_new ? ` (${q.matched_new} yeni eşleşme)` : ""}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{q.changes ? <>{money(q.cash_old)} → <b>{money(q.cash_new)}</b></> : "değişiklik yok"}</td>
                    <td className="px-3 py-2">{q.sent && q.changes ? <span className="text-amber-700">gönderilmiş</span> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>{tokens.length ? "İptal" : "Kapat"}</Button>
          {!loading && tokens.length > 0 && (
            <Button onClick={apply} disabled={busy} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{busy ? "Uygulanıyor…" : `Güncelle${bulk ? ` (${tokens.length} teklif)` : ""}`}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Brands view ----------
function BrandsView() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [calcFor, setCalcFor] = useState(null); // 17.40: { brand, fromExcel }
  const [purchases, setPurchases] = useState({});
  const loadPurchases = () => adminApi.get("/admin/brands-purchase").then((r) => setPurchases(r.data || {})).catch(() => {});
  useEffect(() => { loadPurchases(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/brands");
      setBrands(res.data);
    } catch {
      toast.error("Markalar alınamadı.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!window.confirm(`"${b.name}" markasını ve tüm ürünlerini silmek istediğinize emin misiniz?`)) return;
    try {
      await adminApi.delete(`/admin/brands/${b.id}`);
      toast.success("Marka silindi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme hatası.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Marka Yönetimi"
        subtitle="İskontoları düzenleyin, Excel fiyat listelerini yükleyin"
        actions={
          <Button
            data-testid={ADMIN.addBrandBtn}
            onClick={() => setEditing({ name: "", cash_discount: 0, card_discount: 0 })}
            className="bg-[#E8762D] hover:bg-[#D4651F] text-white uppercase tracking-wide text-xs"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Marka
          </Button>
        }
      />
      <div className="p-3 sm:p-8">
        <FxCard />
        {loading ? (
          <div className="text-sm text-[#4B5563]">Yükleniyor…</div>
        ) : brands.length === 0 ? (
          <Card className="border-[#E6E0D6] p-12 text-center">
            <Boxes className="w-10 h-10 mx-auto text-[#0F1B2D] mb-3" />
            <p className="text-sm text-[#4B5563]">Henüz marka eklenmemiş.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {brands.map((b) => (
              <Card key={b.id} className="border-[#E6E0D6] rounded-lg overflow-hidden">
                <div className="bg-[#0F1B2D] h-1" />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-[#0F1B2D] tracking-tight">
                        {b.name}
                      </h3>
                      <p className="text-xs text-[#4B5563] mt-0.5">
                        {b.product_count} ürün
                        {b.currency && b.currency !== "TRY" && <span className="ml-2 inline-block rounded bg-[#FFF7ED] border border-orange-200 text-[#B85A1C] px-1.5 py-0.5 text-[10px] font-semibold">{CUR_LABEL[b.currency]}</span>}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditing(b)}
                        className="p-1.5 hover:bg-[#F1ECE4] rounded transition-colors"
                        title="Düzenle"
                      >
                        <Pencil className="w-3.5 h-3.5 text-[#4B5563]" />
                      </button>
                      <button
                        data-testid={`${ADMIN.brandDeleteBtn}-${b.name}`}
                        onClick={() => remove(b)}
                        className="p-1.5 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#EF4444]" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-[#F7F4EF] border border-[#E6E0D6] rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wide text-[#4B5563]">Nakit İsk.</div>
                      <div className="text-lg font-bold text-[#1F2937] tabular-nums">%{b.cash_discount}</div>
                    </div>
                    <div className="bg-[#F7F4EF] border border-[#E6E0D6] rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wide text-[#4B5563]">Kart İsk.</div>
                      <div className="text-lg font-bold text-[#1F2937] tabular-nums">%{b.card_discount}</div>
                    </div>
                  </div>
                  {purchases[b.id] && (
                    <p className="text-[11px] text-[#6B7280] -mt-2 mb-3">
                      {BUY_METHODS.find(([k]) => k === purchases[b.id].method)?.[1]} · alış %{purchases[b.id].buy_discount} · kâr %{purchases[b.id].margin}
                      {purchases[b.id].supplier ? ` · ${purchases[b.id].supplier}` : ""}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid={`${ADMIN.brandUploadBtn}-${b.name}`}
                      onClick={() => setUploadFor(b)}
                      variant="outline"
                      className="w-full border-[#E6E0D6] text-[#0F1B2D] hover:bg-[#F1ECE4] text-xs uppercase tracking-wide"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Excel Yükle
                    </Button>
                    <Button
                      onClick={() => setCalcFor({ brand: b, fromExcel: false })}
                      variant="outline"
                      className="w-full border-[#E6E0D6] text-[#0F1B2D] hover:bg-[#F1ECE4] text-xs uppercase tracking-wide"
                      title="Alış iskontosu, kâr ve kart komisyonundan satış iskontosunu hesapla"
                    >
                      <Percent className="w-3.5 h-3.5 mr-1.5" /> Alıştan hesapla
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BrandEditDialog
        key={`edit-${editing?.id || "new"}-${editing ? "open" : "closed"}`}
        brand={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
      <ExcelUploadDialog
        key={`upload-${uploadFor?.id || "none"}`}
        brand={uploadFor}
        onClose={() => setUploadFor(null)}
        onDone={() => { const b = uploadFor; setUploadFor(null); load(); if (b) setCalcFor({ brand: b, fromExcel: true }); }}
      />
      <PricingCalcDialog
        key={`calc-${calcFor?.brand?.id || "none"}-${calcFor?.fromExcel ? "x" : "m"}`}
        brand={calcFor?.brand}
        purchase={calcFor?.brand ? purchases[calcFor.brand.id] : null}
        fromExcel={!!calcFor?.fromExcel}
        onClose={() => setCalcFor(null)}
        onSaved={() => { setCalcFor(null); load(); loadPurchases(); }}
      />
    </div>
  );
}

function BrandEditDialog({ brand, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: brand?.name || "",
    cash_discount: brand?.cash_discount || 0,
    card_discount: brand?.card_discount || 0,
  });
  const [currency, setCurrency] = useState(brand?.currency || "TRY");
  const [saving, setSaving] = useState(false);

  if (!brand) return null;
  const isNew = !brand.id;

  const save = async () => {
    setSaving(true);
    try {
      let id = brand.id;
      if (isNew) {
        const r = await adminApi.post("/admin/brands", form);
        id = r.data?.id;
        toast.success("Marka eklendi.");
      } else {
        await adminApi.put(`/admin/brands/${brand.id}`, form);
        toast.success("Marka güncellendi.");
      }
      if (id && (currency || "TRY") !== (brand.currency || "TRY")) {
        await adminApi.put(`/admin/brands/${id}/currency`, { currency });
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!brand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D]">
            {isNew ? "Yeni Marka" : `Marka: ${brand.name}`}
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-xs">
            İskonto oranları yüzde olarak girilir.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Marka Adı</Label>
            <Input
              data-testid={ADMIN.brandNameInput}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border-[#E6E0D6]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Fiyat Para Birimi</Label>
            <CurrencySelect value={currency} onChange={setCurrency} />
            {currency !== "TRY" && <p className="text-[11px] text-[#B85A1C]">Bu markanın liste fiyatları {CUR_LABEL[currency]} olarak girilir; teklifte günün kuruyla TL'ye çevrilir.</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Nakit İsk. %</Label>
              <Input
                data-testid={ADMIN.brandCashInput}
                type="number"
                value={form.cash_discount}
                onChange={(e) => setForm({ ...form, cash_discount: parseFloat(e.target.value) || 0 })}
                className="border-[#E6E0D6] tabular-nums"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Kart İsk. %</Label>
              <Input
                data-testid={ADMIN.brandCardInput}
                type="number"
                value={form.card_discount}
                onChange={(e) => setForm({ ...form, card_discount: parseFloat(e.target.value) || 0 })}
                className="border-[#E6E0D6] tabular-nums"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button
            data-testid={ADMIN.brandSaveBtn}
            onClick={save}
            disabled={saving}
            className="bg-[#E8762D] hover:bg-[#D4651F] text-white"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExcelUploadDialog({ brand, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sheet, setSheet] = useState("");
  const [mapping, setMapping] = useState({ code_col: "", name_col: "", price_col: "" });
  const [busy, setBusy] = useState(false);

  if (!brand) return null;

  const upload = async () => {
    if (!file) return toast.error("Dosya seçin.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post(`/admin/brands/${brand.id}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      const first = res.data.sheet_names[0];
      setSheet(first);
      setMapping({
        code_col: res.data.suggestions.code_col || "",
        name_col: res.data.suggestions.name_col || "",
        price_col: res.data.suggestions.price_col || "",
      });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız.");
    } finally { setBusy(false); }
  };

  const importExcel = async () => {
    if (!mapping.name_col) {
      return toast.error("Ürün Adı sütununu eşleştirin.");
    }
    setBusy(true);
    try {
      const res = await adminApi.post(`/admin/brands/${brand.id}/import`, {
        upload_id: preview.upload_id,
        ...mapping,
        sheet_name: sheet,
      });
      toast.success(`${res.data.imported} ürün işlendi · ${res.data.inserted || 0} yeni · ${res.data.updated || 0} güncellendi · ${res.data.price_missing || 0} fiyatsız · ${res.data.skipped || 0} atlandı.`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarma başarısız.");
    } finally { setBusy(false); }
  };

  const cols = preview?.sheets[sheet]?.columns || [];
  const previewRows = preview?.sheets[sheet]?.preview || [];

  return (
    <Dialog open={!!brand} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D]">
            {brand.name} — Excel Ürün / Fiyat Listesi
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-xs">
            {step === 1
              ? "Excel dosyasını yükleyin. Sistem sütun isimlerini otomatik tespit eder."
              : "Sütunları eşleştirin. Liste fiyatı opsiyoneldir; fiyatsız ürünler katalogda kalır ve mevcut fiyatlı kayıtları ezmez."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="py-4">
            <label className="block border-2 border-dashed border-[#E6E0D6] rounded-md p-10 text-center hover:border-[#E8762D] hover:bg-[#FFF7ED]/30 transition-colors cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0])}
                className="hidden"
              />
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-[#0F1B2D]" />
              <div className="text-sm font-medium text-[#1F2937]">
                {file ? file.name : "Excel dosyası seçin"}
              </div>
              <div className="text-xs text-[#4B5563] mt-1">.xlsx veya .xls</div>
            </label>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={onClose}>İptal</Button>
              <Button
                onClick={upload}
                disabled={busy || !file}
                className="bg-[#E8762D] hover:bg-[#D4651F] text-white"
              >
                {busy ? "İşleniyor…" : "Devam Et"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && preview && (
          <div className="py-2 space-y-4">
            {preview.sheet_names.length > 1 && (
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Sayfa</Label>
                <Select value={sheet} onValueChange={setSheet}>
                  <SelectTrigger className="border-[#E6E0D6] mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {preview.sheet_names.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <MapField
                label="Ürün Kodu (Opsiyonel)"
                value={mapping.code_col}
                onChange={(v) => setMapping({ ...mapping, code_col: v })}
                cols={cols}
                testId={ADMIN.mapCodeSelect}
                allowNone
              />
              <MapField
                label="Ürün Adı *"
                value={mapping.name_col}
                onChange={(v) => setMapping({ ...mapping, name_col: v })}
                cols={cols}
                testId={ADMIN.mapNameSelect}
              />
              <MapField
                label="Liste Fiyatı (Opsiyonel)"
                value={mapping.price_col}
                onChange={(v) => setMapping({ ...mapping, price_col: v })}
                cols={cols}
                testId={ADMIN.mapPriceSelect}
                allowNone
              />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-2">
                Önizleme — {preview.sheets[sheet]?.rows} satır
              </div>
              <div className="border border-[#E6E0D6] rounded-md overflow-x-auto scroll-soft max-h-56">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F7F4EF]">
                      {cols.map((c) => (
                        <th key={c} className="px-3 py-2 text-left text-[10px] uppercase tracking-wide text-[#4B5563] border-b border-[#E6E0D6]">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-b border-[#F1ECE4]">
                        {r.map((v, j) => (
                          <td key={j} className="px-3 py-2 text-[#1F2937]">{String(v).slice(0, 60)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Geri</Button>
              <Button
                data-testid={ADMIN.mapImportBtn}
                onClick={importExcel}
                disabled={busy}
                className="bg-[#E8762D] hover:bg-[#D4651F] text-white"
              >
                {busy ? "İçe aktarılıyor…" : "İçe Aktar"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MapField({ label, value, onChange, cols, testId, allowNone }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">{label}</Label>
      <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger data-testid={testId} className="border-[#E6E0D6]">
          <SelectValue placeholder="Seçin…" />
        </SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value="__none__">— Yok —</SelectItem>}
          {cols.map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------- Products view ----------
// ---------- V17.24: fiyat alanları (liste + iskonto  YA DA  nakit + kart fiyatı; biri girilince diğeri hesaplanır) ----------
const pfNum = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/\./g, (m, i, str) => (str.indexOf(",") > -1 ? "" : m)).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const pfFmt = (n) => (n === null || n === undefined || !Number.isFinite(n) ? "" : String(Math.round(n * 100) / 100).replace(".", ","));
const emptyPrice = () => ({ list: "", cd: "", kd: "", cp: "", kp: "" });

function priceFromProduct(p) {
  const v = emptyPrice();
  const L = Number(p?.list_price || 0);
  if (L > 0) v.list = pfFmt(L);
  if (p?.default_cash_discount !== null && p?.default_cash_discount !== undefined && p?.default_cash_discount !== "") v.cd = pfFmt(Number(p.default_cash_discount));
  if (p?.default_card_discount !== null && p?.default_card_discount !== undefined && p?.default_card_discount !== "") v.kd = pfFmt(Number(p.default_card_discount));
  return v;
}

function pricePayload(v) {
  return {
    list_price: pfNum(v.list),
    cash_discount: pfNum(v.cd),
    card_discount: pfNum(v.kd),
    cash_price: pfNum(v.cp),
    card_price: pfNum(v.kp),
  };
}

function PriceFields({ value, onChange, brand, currency }) {
  const sym = currency && currency !== "TRY" ? ` (${CUR_SYMBOL[currency]})` : "";
  const bc = Number(brand?.cash_discount || 0);
  const bk = Number(brand?.card_discount || 0);
  const recalcPrices = (n) => {
    const L = pfNum(n.list);
    const cd = n.cd === "" ? bc : pfNum(n.cd);
    const kd = n.kd === "" ? bk : pfNum(n.kd);
    if (L && L > 0) {
      n.cp = cd !== null ? pfFmt(L * (1 - cd / 100)) : "";
      n.kp = kd !== null ? pfFmt(L * (1 - kd / 100)) : "";
    }
    return n;
  };
  useEffect(() => { onChange(recalcPrices({ ...value })); }, [brand?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setDisc = (patch) => onChange(recalcPrices({ ...value, ...patch }));
  const setPrice = (patch) => {
    const n = { ...value, ...patch };
    let L = pfNum(n.list);
    const cp = pfNum(n.cp);
    const kp = pfNum(n.kp);
    if (!(L > 0)) {
      const m = Math.max(cp || 0, kp || 0);
      if (m > 0) { L = m; n.list = pfFmt(m); }
    }
    if (L > 0) {
      if (cp > 0) n.cd = pfFmt((1 - cp / L) * 100);
      if (kp > 0) n.kd = pfFmt((1 - kp / L) * 100);
    }
    onChange(n);
  };
  const box = "space-y-1";
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div className={box}><Label className="text-xs">Liste Fiyatı{sym}</Label><Input value={value.list} onChange={(e) => setDisc({ list: e.target.value })} placeholder="0,00" /></div>
        <div className={box}><Label className="text-xs">Nakit İsk. %</Label><Input value={value.cd} onChange={(e) => setDisc({ cd: e.target.value })} placeholder={`marka: ${bc}`} /></div>
        <div className={box}><Label className="text-xs">Kart İsk. %</Label><Input value={value.kd} onChange={(e) => setDisc({ kd: e.target.value })} placeholder={`marka: ${bk}`} /></div>
      </div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="text-[11px] text-[#6B6F78] pb-2">ya da doğrudan:</div>
        <div className={box}><Label className="text-xs">Nakit Fiyat{sym}</Label><Input value={value.cp} onChange={(e) => setPrice({ cp: e.target.value })} placeholder="0,00" /></div>
        <div className={box}><Label className="text-xs">Kart Fiyat{sym}</Label><Input value={value.kp} onChange={(e) => setPrice({ kp: e.target.value })} placeholder="0,00" /></div>
      </div>
      <p className="text-[11px] text-[#6B6F78]">
        Biri girilince diğeri hesaplanır. İskonto boş bırakılırsa markanın iskontosu (nakit %{bc}, kart %{bk}) kullanılır.
        Fiyat hiç girilmezse ürün "fiyat bekliyor" olarak eklenir.
      </p>
    </div>
  );
}

function ProductsView() {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [groupOpen, setGroupOpen] = useState(false); // 17.42
  const reloadProducts = () => selected && adminApi.get("/admin/products", { params: { brand_id: selected } }).then((r) => setProducts(r.data)).catch(() => {});

  useEffect(() => {
    adminApi.get("/admin/brands").then((r) => {
      setBrands(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    adminApi.get("/admin/products", { params: { brand_id: selected } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [selected]);

  const brand = brands.find((b) => b.id === selected);
  const bc = Number(brand?.cash_discount || 0);
  const bk = Number(brand?.card_discount || 0);
  const needle = search.trim().toLocaleLowerCase("tr");
  const shown = needle
    ? products.filter((p) => `${p.code || ""} ${p.name || ""}`.toLocaleLowerCase("tr").includes(needle))
    : products;

  const removeProduct = async (p) => {
    if (!window.confirm(`"${p.name}" katalogdan silinsin mi?\n\nBu ürüne bağlı öğrenilmiş eşleşmeler de silinir; sistem bu ürünü bir daha önermez. Eski tekliflerdeki satırlar değişmez.`)) return;
    try {
      const r = await adminApi.delete(`/admin/products/${p.id}`);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`Ürün silindi${r.data?.learned_removed ? ` (${r.data.learned_removed} öğrenilmiş eşleşme de silindi)` : ""}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ürün silinemedi."); }
  };

  return (
    <div>
      <PageHeader
        title="Ürünler"
        subtitle="Katalogdaki ürünler; ürüne özel iskonto, düzenleme ve silme"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kod ya da ad ara…" className="w-full sm:w-56 border-[#E6E0D6]" />
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-48 border-[#E6E0D6]">
                <SelectValue placeholder="Marka seçin…" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} ({b.product_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setGroupOpen(true)} disabled={!selected} className="border-[#E6E0D6] text-xs" title="Markadaki bazı ürünlere toplu özel iskonto">
              <Percent className="w-4 h-4 mr-1.5" /> İskonto grupları
            </Button>
            <Button
              onClick={() => selected && setEditing({ mode: "create", brand_id: selected })}
              disabled={!selected}
              className="bg-[#E8762D] hover:bg-[#D4651F] text-white text-xs"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Ürün
            </Button>
          </div>
        }
      />
      <div className="p-3 sm:p-8">
        <Card className="border-[#E6E0D6] overflow-hidden">
          <div className="overflow-x-auto scroll-soft">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F7F4EF]">
                  <Th>#</Th>
                  <Th>Ürün Kodu</Th>
                  <Th>Ürün Adı</Th>
                  <Th className="text-right">Liste Fiyatı</Th>
                  <Th className="text-right">İsk. (Nakit / Kart)</Th>
                  <Th className="text-right">Nakit</Th>
                  <Th className="text-right">Kart</Th>
                  <Th className="text-right">İşlem</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-[#4B5563]">Yükleniyor…</td></tr>
                ) : shown.length === 0 ? (
                  <tr><td colSpan={8} className="p-12 text-center text-[#4B5563]">{products.length ? "Aramaya uyan ürün yok." : "Bu markaya ürün yüklenmemiş."}</td></tr>
                ) : shown.map((p, i) => {
                  const L = Number(p.list_price || 0);
                  const own = p.default_cash_discount !== null && p.default_cash_discount !== undefined && p.default_cash_discount !== "";
                  const ownK = p.default_card_discount !== null && p.default_card_discount !== undefined && p.default_card_discount !== "";
                  const cd = own ? Number(p.default_cash_discount) : bc;
                  const kd = ownK ? Number(p.default_card_discount) : bk;
                  const pc = p.currency || brand?.currency || "TRY";
                  return (
                    <tr key={p.id} className="border-b border-[#F1ECE4] hover:bg-[#F7F4EF]">
                      <td className="px-4 py-3 text-[#9CA3AF] text-xs font-mono">{String(i + 1).padStart(3, "0")}</td>
                      <td className="px-4 py-3 text-[#4B5563] font-mono text-xs">{p.code || "—"}</td>
                      <td className="px-4 py-3 text-[#1F2937]">{p.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#1F2937] font-medium">{L > 0 ? fmtCur(pc, L) : <span className="text-amber-700 text-xs font-medium">Fiyat yok</span>}{p.currency && p.currency !== (brand?.currency || "TRY") && <div className="text-[10px] text-[#B85A1C]">ürüne özel</div>}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-xs">
                        <span className={own || ownK ? "text-[#B85A1C] font-semibold" : "text-[#6B6F78]"} title={own || ownK ? "Ürüne özel iskonto" : "Marka iskontosu"}>
                          %{Math.round(cd * 100) / 100} / %{Math.round(kd * 100) / 100}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{L > 0 ? fmtCur(pc, L * (1 - cd / 100)) : "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{L > 0 ? fmtCur(pc, L * (1 - kd / 100)) : "—"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button type="button" onClick={() => setEditing({ mode: "edit", product: p, brand_id: p.brand_id })} className="text-[#0F1B2D] hover:text-[#E8762D] p-1" title="Düzenle"><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => removeProduct(p)} className="text-[#9EA1A8] hover:text-red-600 p-1 ml-1" title="Sil"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <ProductDialog
        value={editing}
        brands={brands}
        onClose={() => setEditing(null)}
        onSaved={(p, mode) => {
          setProducts((prev) => (mode === "edit" ? prev.map((x) => (x.id === p.id ? p : x)) : [p, ...prev]));
          setEditing(null);
        }}
      />
      <GroupDiscountDialog open={groupOpen} brand={brand} products={products} onClose={() => setGroupOpen(false)} onSaved={reloadProducts} />
    </div>
  );
}

function ProductDialog({ value, brands, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [price, setPrice] = useState(emptyPrice());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!value) { setForm(null); return; }
    const p = value.product || {};
    setForm({ brand_id: value.brand_id || p.brand_id || "", code: p.code || "", name: p.name || "",
      box_qty: p.box_qty ? String(p.box_qty).replace(".", ",") : "", currency: p.currency || "" });
    setPrice(value.mode === "edit" ? priceFromProduct(p) : emptyPrice());
  }, [value]);
  if (!value || !form) return null;
  const brand = brands.find((b) => b.id === form.brand_id);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Ürün adı girin.");
    setSaving(true);
    try {
      const boxText = String(form.box_qty || "").trim();
      const box = boxText ? pfNum(boxText) : 0;
      if (boxText && !(box > 0)) { setSaving(false); return toast.error("Kutu içi adet 0'dan büyük bir sayı olmalı (boş bırakılabilir)."); }
      const body = { brand_id: form.brand_id, code: form.code.trim(), name: form.name.trim(), ...pricePayload(price), box_qty: box || 0 };
      const r = value.mode === "edit"
        ? await adminApi.put(`/admin/products/${value.product.id}`, body)
        : await adminApi.post("/admin/products", body);
      const pid = r.data?.id || value.product?.id;
      if (pid && (form.currency || "") !== ((value.product || {}).currency || "")) {
        await adminApi.put(`/admin/products/${pid}/currency`, { currency: form.currency || null });
      }
      r.data = { ...r.data, currency: form.currency || undefined };
      toast.success(value.mode === "edit" ? "Ürün güncellendi." : (Number(r.data.list_price || 0) > 0 ? "Ürün eklendi." : "Fiyatsız ürün eklendi; eşleştiğinde fiyat bekliyor görünür."));
      onSaved(r.data, value.mode);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ürün kaydedilemedi."); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D]">{value.mode === "edit" ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle>
          <DialogDescription>Kod aynı markada tekrar edemez. Fiyat ya liste + iskonto ya da nakit + kart fiyatı olarak girilebilir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Marka</Label>
            <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
              <SelectTrigger className="border-[#E6E0D6]"><SelectValue placeholder="Marka seçin…" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5"><Label>Ürün Kodu</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Ürün Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Fiyat para birimi</Label>
            <CurrencySelect value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} allowInherit
              inheritLabel={`Markanın para birimi (${CUR_LABEL[brand?.currency || "TRY"]})`} />
          </div>
          <PriceFields value={price} onChange={setPrice} brand={brand} currency={form.currency || brand?.currency || "TRY"} />
          <div className="space-y-1.5">
            <Label>Kutu / koli içi adet</Label>
            <Input value={form.box_qty} onChange={(e) => setForm({ ...form, box_qty: e.target.value })} placeholder="Boş: sistemdeki kutu tablosu kullanılır" inputMode="decimal" />
            <div className="text-[11px] text-[#6B6F78]">Listede "3 kutu" yazılırsa adet = 3 × bu sayı. Boş bırakılırsa kutu tablosuna bakılır; orada da yoksa satır sarı olur.</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{saving ? "Kaydediliyor…" : value.mode === "edit" ? "Kaydet" : "Ürünü Ekle"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- V17.24: teklife satır ekleme (katalogdan ya da elle) ----------
function AddQuoteLineDialog({ open, quote, onClose, onAdded }) {
  const [tab, setTab] = useState("catalog");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ brand_id: "", code: "", name: "" });
  const [price, setPrice] = useState(emptyPrice());
  const [saveToCatalog, setSaveToCatalog] = useState(true);
  const [saving, setSaving] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setTab("catalog"); setQ(""); setResults([]); setPicked(null); setQty("1");
    setForm({ brand_id: "", code: "", name: "" }); setPrice(emptyPrice()); setSaveToCatalog(true);
    adminApi.get("/admin/brands").then((r) => {
      const list = r.data || [];
      setBrands(list);
      const diger = list.find((b) => String(b.name || "").toLocaleUpperCase("tr") === "DİĞER") || list[0];
      if (diger) setForm((f) => ({ ...f, brand_id: diger.id }));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || tab !== "catalog") return;
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await adminApi.get("/products/search", { params: { q: query, limit: 30 } });
        if (id !== reqRef.current) return;
        const data = r.data;
        setResults(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
      } catch { if (id === reqRef.current) setResults([]); }
      finally { if (id === reqRef.current) setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, tab, open]);

  if (!open || !quote) return null;
  const brand = brands.find((b) => b.id === form.brand_id);

  const submit = async () => {
    const n = pfNum(qty);
    if (!(n > 0)) return toast.error("Adet girin.");
    let body;
    if (tab === "catalog") {
      if (!picked) return toast.error("Katalogdan bir ürün seçin.");
      body = { product_id: picked.id, qty: n };
    } else {
      if (!form.name.trim()) return toast.error("Ürün adı girin.");
      if (!form.brand_id) return toast.error("Marka seçin.");
      body = { ...form, code: form.code.trim(), name: form.name.trim(), qty: n, save_to_catalog: saveToCatalog, ...pricePayload(price) };
    }
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/items/add`, body);
      toast.success(r.data?.saved_to_catalog ? "Satır eklendi; ürün kataloğa da kaydedildi." : "Satır eklendi.");
      onAdded(r.data.quote);
    } catch (e) { toast.error(e?.response?.data?.detail || "Satır eklenemedi."); }
    finally { setSaving(false); }
  };

  const tabBtn = (key, text) => (
    <button type="button" onClick={() => setTab(key)}
      className={`px-3 py-1.5 text-xs font-medium rounded-md border ${tab === key ? "bg-[#0F1B2D] text-white border-[#0F1B2D]" : "bg-white text-[#334155] border-[#D9D3C9] hover:bg-[#F7F4EF]"}`}>
      {text}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D]">Teklife Satır Ekle</DialogTitle>
          <DialogDescription>Satır teklifin sonuna eklenir, toplamlar yeniden hesaplanır.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">{tabBtn("catalog", "Katalogdan Seç")}{tabBtn("manual", "Katalogda Yok (Elle Gir)")}</div>
        {tab === "catalog" ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı, ölçü ya da kod…" className="pl-9" />
            </div>
            <div className="border border-[#E6E0D6] rounded-md max-h-72 overflow-y-auto divide-y divide-[#F1ECE4]">
              {searching ? <div className="p-4 text-center text-sm text-[#6B6F78]">Aranıyor…</div>
                : results.length === 0 ? <div className="p-4 text-center text-sm text-[#9EA1A8]">{q.trim().length < 2 ? "Aramak için yazın." : "Ürün bulunamadı. \"Katalogda Yok\" sekmesinden elle ekleyebilirsiniz."}</div>
                : results.map((p) => (
                  <button key={p.id} type="button" onClick={() => setPicked(p)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 ${picked?.id === p.id ? "bg-[#FFF7ED] border-l-4 border-l-[#E8762D]" : "hover:bg-[#F7F4EF] border-l-4 border-l-transparent"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1F2937] truncate">{p.name}</div>
                      <div className="text-[11px] text-[#6B6F78]">{p.brand_name}{p.code ? ` · ${p.code}` : ""}</div>
                    </div>
                    <div className="text-sm tabular-nums whitespace-nowrap">{Number(p.list_price || 0) > 0 ? fmtTRY(p.list_price) : <span className="text-amber-700 text-xs">Fiyat bekliyor</span>}</div>
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label>Marka</Label>
                <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                  <SelectTrigger className="border-[#E6E0D6]"><SelectValue placeholder="Marka…" /></SelectTrigger>
                  <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Ürün Kodu</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Ürün Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            </div>
            <PriceFields value={price} onChange={setPrice} brand={brand} currency={brand?.currency || "TRY"} />
            {brand?.currency && brand.currency !== "TRY" && <p className="text-[11px] text-[#B85A1C]">Bu markanın fiyatları {CUR_LABEL[brand.currency]}; teklife günün kuruyla TL olarak girer.</p>}
            <label className="flex items-center gap-2 text-sm text-[#334155]">
              <input type="checkbox" checked={saveToCatalog} onChange={(e) => setSaveToCatalog(e.target.checked)} />
              Kataloğa da kaydet (bir dahaki tekliflerde sistem bu ürünü bulur)
            </label>
          </div>
        )}
        <div className="flex items-end justify-between gap-3 pt-2">
          <div className="space-y-1.5 w-28"><Label>Adet</Label><Input value={qty} onChange={(e) => setQty(e.target.value)} /></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{saving ? "Ekleniyor…" : "Satırı Ekle"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Admin quote create ----------
// ---------- V17.23: teklif oluşturma arka planda sürer ----------
// Sayfa değiştirince teklif iptal olmaz; aynı anda birden fazla teklif hazırlanabilir. İstek ve ilerleme yoklaması
// sayfa bileşeninden bağımsız tutulur; üstteki şerit her sayfada görünür, bitince bildirim + "Aç".
const quoteJobStore = {
  jobs: [],
  listeners: new Set(),
  openJobId: null,
  navigate: null,
  emit() {
    this.jobs = [...this.jobs];
    this.listeners.forEach((l) => { try { l(); } catch (_) { /* yoksay */ } });
  },
  patch(id, p) {
    this.jobs = this.jobs.map((j) => (j.id === id ? { ...j, ...p } : j));
    this.emit();
  },
  remove(id) {
    this.jobs = this.jobs.filter((j) => j.id !== id);
    this.emit();
  },
};

function useQuoteJobs() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l = () => setTick((t) => t + 1);
    quoteJobStore.listeners.add(l);
    return () => { quoteJobStore.listeners.delete(l); };
  }, []);
  return quoteJobStore.jobs;
}

function openQuoteJob(id) {
  quoteJobStore.openJobId = id;
  quoteJobStore.emit();
  if (quoteJobStore.navigate) quoteJobStore.navigate("/admin/create-quote");
}

function startQuoteJob({ progressId, label, sourceText, request }) {
  const id = progressId;
  quoteJobStore.jobs = [...quoteJobStore.jobs, {
    id, label, percent: 1, stage: "Başlatılıyor", detail: "", status: "running", result: null, startedAt: Date.now(),
  }];
  quoteJobStore.emit();
  let stopped = false;
  const poll = async () => {
    if (stopped) return;
    try {
      const pr = await fetch(`${API}/quote-progress/${encodeURIComponent(progressId)}`);
      if (pr.ok) {
        const d = await pr.json();
        if (!stopped) quoteJobStore.patch(id, { percent: Number(d.percent) || 1, stage: d.stage || "İşleniyor", detail: d.detail || "" });
      }
    } catch (_) { /* ilerleme gelmezse teklif yine oluşur */ }
    if (!stopped) setTimeout(poll, 1000);
  };
  setTimeout(poll, 700);
  const finishOk = (data) => {
    stopped = true;
    const n = data?.items?.length || 0;
    quoteJobStore.patch(id, { status: "done", percent: 100, stage: "Tamamlandı", detail: `${n} kalem`, result: data });
    toast.success(`${label}: ${sourceText}${n} kalemlik teklif hazır.`, {
      duration: 15000,
      action: { label: "Aç", onClick: () => openQuoteJob(id) },
    });
  };
  const finishErr = (msg) => {
    stopped = true;
    quoteJobStore.patch(id, { status: "error", stage: "Hata", detail: String(msg) });
    toast.error(`${label}: ${msg}`);
  };
  // 17.34: uzun listede bağlantı süresi (sunucu ara katmanı ~5 dk) dolarsa teklif sunucuda hazırlanmaya devam eder;
  // hata vermek yerine ilerleme izlenir, teklif bitince getirilir (en fazla 20 dk).
  const waitForResult = async () => {
    const started = Date.now();
    while (Date.now() - started < 20 * 60 * 1000) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const pr = await fetch(`${API}/quote-progress/${encodeURIComponent(progressId)}`);
        if (!pr.ok) continue;
        const d = await pr.json();
        if (d.quote_id) {
          const q = await adminApi.get(`/admin/quotes/${d.quote_id}`, { timeout: 60000 });
          finishOk(q.data);
          return;
        }
        if (d.failed || d.error) {
          finishErr(d.failed_detail || "Teklif oluşturulamadı.");
          return;
        }
        quoteJobStore.patch(id, { detail: `Uzun liste, sonuç bekleniyor${d.detail ? " · " + d.detail : ""}` });
      } catch (_) { /* bağlantı gelince tekrar dene */ }
    }
    finishErr("Teklif hâlâ hazırlanıyor olabilir; birkaç dakika sonra Teklif Talepleri'ne bakın.");
  };
  request()
    .then((r) => finishOk(r.data))
    .catch((e) => {
      const st = Number(e?.response?.status || 0);
      if (!e?.response || st === 502 || st === 503 || st === 504) {
        quoteJobStore.patch(id, { stage: "Hazırlanıyor", detail: "Uzun liste, sonuç bekleniyor" });
        waitForResult();
        return;
      }
      finishErr(e?.response?.data?.detail || "Teklif oluşturulamadı.");
    });
}

function QuoteJobsBar() {
  const jobs = useQuoteJobs();
  if (!jobs.length) return null;
  return (
    <div className="sticky top-14 lg:top-0 z-40 bg-white border-b border-[#E6E0D6] px-3 sm:px-8 py-2 space-y-1.5" data-testid="quote-jobs-bar">
      {jobs.map((j) => (
        <div key={j.id} className="flex items-center gap-3 text-xs">
          <span className={`shrink-0 font-semibold ${j.status === "error" ? "text-red-700" : j.status === "done" ? "text-emerald-700" : "text-[#9A3412]"}`}>
            {j.status === "running" ? "Teklif hazırlanıyor" : j.status === "done" ? "Teklif hazır" : "Teklif oluşturulamadı"}
          </span>
          <span className="shrink min-w-0 text-[#334155] font-medium truncate max-w-[220px]">{j.label}</span>
          {j.status === "running" ? (
            <>
              <div className="flex-1 h-1.5 rounded-full bg-[#E6E0D6] overflow-hidden min-w-[80px]">
                <div className="h-full bg-[#E8762D] transition-all duration-500" style={{ width: `${Math.max(1, Math.min(100, Number(j.percent) || 0))}%` }} />
              </div>
              <span className="shrink-0 font-mono text-[#E8762D]">%{Math.max(1, Math.min(100, Math.round(Number(j.percent) || 0)))}</span>
              <span className="hidden sm:inline shrink-0 text-[#6B6F78] truncate max-w-[240px]">{j.stage}{j.detail ? ` · ${j.detail}` : ""}</span>
            </>
          ) : (
            <span className="flex-1 text-[#6B6F78] truncate">{j.detail}</span>
          )}
          {j.status === "done" && (
            <Button size="sm" onClick={() => openQuoteJob(j.id)} className="h-6 px-2.5 text-[11px] bg-[#0F1B2D] hover:bg-[#1B2B44] text-white">Aç</Button>
          )}
          {j.status !== "running" && (
            <button type="button" onClick={() => quoteJobStore.remove(j.id)} className="text-[#9EA1A8] hover:text-[#4B505B] px-1" title="Kapat">✕</button>
          )}
        </div>
      ))}
    </div>
  );
}

function AdminQuoteCreateView() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "" });
  const [inputMode, setInputMode] = useState("text");
  const [lines, setLines] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [quote, setQuote] = useState(null);
  const [shownJobId, setShownJobId] = useState(null);
  const [starting, setStarting] = useState(false);
  const jobs = useQuoteJobs();
  const myJobsRef = useRef(new Set());

  // V17.23: "Aç" ile gelinen ya da bu sayfadan başlatılıp biten teklifi göster.
  useEffect(() => {
    const openId = quoteJobStore.openJobId;
    let job = openId ? jobs.find((j) => j.id === openId && j.status === "done") : null;
    if (job) {
      quoteJobStore.openJobId = null;
    } else if (!quote) {
      job = [...jobs].reverse().find((j) => j.status === "done" && myJobsRef.current.has(j.id));
    }
    if (job && job.id !== shownJobId) {
      myJobsRef.current.delete(job.id);
      setShownJobId(job.id);
      setQuote(job.result);
      if (job.result?.ocr_text) setLines(job.result.ocr_text);
    }
  }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const onQuoteChange = (q) => {
    setQuote(q);
    if (shownJobId) quoteJobStore.jobs = quoteJobStore.jobs.map((j) => (j.id === shownJobId ? { ...j, result: q } : j));
  };

  const validateCustomer = () => {
    if (!form.name.trim()) {
      toast.error("Müşteri adı girin.");
      return false;
    }
    if (!form.company.trim()) {
      toast.error("Firma adı girin.");
      return false;
    }
    return true;
  };

  const addPhotoFiles = (event) => {
    const selected = Array.from(event.target.files || []).filter((f) =>
      String(f.type || "").startsWith("image/")
    );
    if (!selected.length) {
      toast.error("Lütfen geçerli fotoğraf dosyaları seçin.");
      return;
    }

    const existing = new Set(photoFiles.map((f) => `${f.name}::${f.size}::${f.lastModified}`));
    const merged = [...photoFiles];
    selected.forEach((f) => {
      const key = `${f.name}::${f.size}::${f.lastModified}`;
      if (!existing.has(key)) {
        existing.add(key);
        merged.push(f);
      }
    });
    if (merged.length > 20) {
      toast.warning("Tek seferde en fazla 20 fotoğraf yüklenebilir. İlk 20 fotoğraf alındı.");
    }
    setPhotoFiles(merged.slice(0, 20));
    event.target.value = "";
  };

  const createQuote = async () => {
    if (!validateCustomer()) return;

    // V17.15: müşteri ekranındaki gibi ilerleme yüzdesi
    const progressId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `p${Date.now()}${Math.random().toString(36).slice(2)}`;
    const progressHeaders = { "X-Progress-Id": progressId };
    let request;
    if (inputMode === "text") {
      const cleanLines = lines.split("\n").map((x) => x.trim()).filter(Boolean);
      if (!cleanLines.length) return toast.error("En az bir ürün satırı girin.");
      request = () => adminApi.post("/admin/quotes/create", { ...form, lines: cleanLines }, { headers: progressHeaders });
    } else {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("company", form.company.trim());
      fd.append("phone", form.phone.trim());
      fd.append("email", form.email.trim());

      let endpoint = "";
      if (inputMode === "excel") {
        if (!excelFile) return toast.error("Excel dosyası seçin.");
        fd.append("file", excelFile);
        endpoint = "/admin/quotes/excel";
      } else if (inputMode === "pdf") {
        if (!pdfFile) return toast.error("PDF dosyası seçin.");
        fd.append("file", pdfFile);
        endpoint = "/admin/quotes/pdf";
      } else if (inputMode === "photos") {
        if (!photoFiles.length) return toast.error("En az bir fotoğraf seçin.");
        photoFiles.forEach((f) => fd.append("files", f));
        endpoint = "/admin/quotes/photos";
      }

      request = () => adminApi.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data", ...progressHeaders },
      });
    }

    // V17.23: teklif arka planda hazırlanır; sayfa değiştirilebilir, aynı anda başka teklif başlatılabilir.
    const sourceText = inputMode === "photos"
      ? `${photoFiles.length} fotoğraftan `
      : inputMode === "excel"
        ? "Excel'den "
        : inputMode === "pdf"
          ? "PDF'den "
          : "";
    const label = [form.name.trim(), form.company.trim()].filter(Boolean).join(" · ") || "Teklif";
    myJobsRef.current.add(progressId);
    startQuoteJob({ progressId, label, sourceText, request });
    setStarting(true);
    setTimeout(() => setStarting(false), 1500);
    toast.info("Teklif arka planda hazırlanıyor. Başka sayfaya geçebilir ya da yeni teklif başlatabilirsiniz.");
  };

  const reset = () => {
    setQuote(null);
    setShownJobId(null);
    setLines("");
    setExcelFile(null);
    setPdfFile(null);
    setPhotoFiles([]);
    setForm({ name: "", company: "", phone: "", email: "" });
  };

  const modeButtonClass = (active) => active
    ? "bg-[#0F1B2D] hover:bg-[#1B2B44] text-white"
    : "bg-white text-[#334155] border border-[#D9D3C9] hover:bg-[#F7F4EF]";

  return (
    <div>
      <PageHeader
        title="Teklif Oluştur"
        subtitle="Admin panelinden metin, Excel, PDF veya çoklu fotoğraf ile müşteri adına teklif hazırlayın"
        actions={quote ? (
          <Button variant="outline" onClick={reset} className="text-xs">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Teklif
          </Button>
        ) : null}
      />
      <div className="p-3 sm:p-8 space-y-6">
        <Card className="border-[#E6E0D6] p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
            <div className="space-y-1.5">
              <Label>Müşteri *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ad Soyad" />
            </div>
            <div className="space-y-1.5">
              <Label>Firma *</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Firma adı" />
            </div>
            <div className="space-y-1.5">
              <Label>Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="05..." />
            </div>
            <div className="space-y-1.5">
              <Label>E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teklif@firma.com" />
            </div>
          </div>

          <div className="mb-5">
            <Label className="mb-2 block">Liste Girişi</Label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <Button type="button" onClick={() => setInputMode("text")} className={modeButtonClass(inputMode === "text")}>
                <FileText className="w-4 h-4 mr-1.5" /> Metin
              </Button>
              <Button type="button" onClick={() => setInputMode("excel")} className={modeButtonClass(inputMode === "excel")}>
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
              </Button>
              <Button type="button" onClick={() => setInputMode("pdf")} className={modeButtonClass(inputMode === "pdf")}>
                <Upload className="w-4 h-4 mr-1.5" /> PDF
              </Button>
              <Button type="button" onClick={() => setInputMode("photos")} className={modeButtonClass(inputMode === "photos")}>
                <Camera className="w-4 h-4 mr-1.5" /> Fotoğraf
              </Button>
            </div>
          </div>

          {inputMode === "text" && (
            <div className="space-y-1.5">
              <Label>Ürün Listesi *</Label>
              <Textarea
                value={lines}
                onChange={(e) => setLines(e.target.value)}
                rows={10}
                placeholder={"50 adet 50 mm dirsek 90°\n20 adet DN25 küresel vana\n10 adet 110 PVC çift çatal"}
                className="font-mono text-sm"
              />
              <p className="text-xs text-[#6B7280]">Her ürün ayrı satırda olmalı.</p>
            </div>
          )}

          {inputMode === "excel" && (
            <div className="rounded-md border border-dashed border-[#D9D3C9] bg-[#F7F4EF] p-5">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-6 h-6 text-[#0F1B2D] mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="font-medium text-[#16233A]">Excel listesini yükle</div>
                  <p className="text-xs text-[#6B6F78]">Müşteri panelindeki Excel okuyucu ile aynı eşleştirme motoru kullanılır.</p>
                  <Input type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={(e) => setExcelFile(e.target.files?.[0] || null)} />
                  {excelFile && <div className="text-xs text-[#4B505B]">Seçilen: <b>{excelFile.name}</b></div>}
                </div>
              </div>
            </div>
          )}

          {inputMode === "pdf" && (
            <div className="rounded-md border border-dashed border-[#D9D3C9] bg-[#F7F4EF] p-5">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-[#0F1B2D] mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="font-medium text-[#16233A]">PDF listesini yükle</div>
                  <p className="text-xs text-[#6B6F78]">PDF tüm sayfalarıyla okunur ve tek teklif olarak fiyatlandırılır.</p>
                  <Input type="file" accept="application/pdf,.pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
                  {pdfFile && <div className="text-xs text-[#4B505B]">Seçilen: <b>{pdfFile.name}</b></div>}
                </div>
              </div>
            </div>
          )}

          {inputMode === "photos" && (
            <div className="rounded-md border border-dashed border-[#D9D3C9] bg-[#F7F4EF] p-5">
              <div className="flex items-start gap-3">
                <Camera className="w-6 h-6 text-[#0F1B2D] mt-0.5" />
                <div className="flex-1 space-y-3">
                  <div>
                    <div className="font-medium text-[#16233A]">Fotoğrafları toplu yükle</div>
                    <p className="text-xs text-[#6B6F78]">Aynı anda en fazla 20 fotoğraf seçilebilir; hepsi tek liste ve tek teklif olarak işlenir.</p>
                  </div>
                  <Input type="file" accept="image/*" multiple onChange={addPhotoFiles} />
                  {photoFiles.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-xs font-medium text-[#334155]">{photoFiles.length} fotoğraf seçildi</div>
                      <div className="max-h-40 overflow-auto rounded border border-[#E6E0D6] bg-white divide-y divide-[#E6E0D6]">
                        {photoFiles.map((f, i) => (
                          <div key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-2 px-3 py-2 text-xs">
                            <span className="w-5 text-[#9EA1A8]">{i + 1}.</span>
                            <span className="flex-1 truncate text-[#334155]">{f.name}</span>
                            <button type="button" onClick={() => setPhotoFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-[#DC2626] hover:text-[#991B1B]">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4">
            <p className="text-xs text-[#6B7280]">Teklif oluştuktan sonra satırları seçip toplu nakit/kart iskontosu uygulayabilir, taslak PDF/Excel indirebilirsiniz.</p>
            <Button onClick={createQuote} disabled={starting} className="bg-[#E8762D] hover:bg-[#D4651F] text-white shrink-0">
              <FilePlus2 className="w-4 h-4 mr-1.5" /> {starting ? "Başlatıldı…" : inputMode === "photos" && photoFiles.length ? `${photoFiles.length} Fotoğrafı Fiyatlandır` : "Teklifi Oluştur"}
            </Button>
          </div>
          {jobs.some((j) => j.status === "running") && (
            <p className="mt-3 text-xs text-[#9A3412]" data-testid="admin-quote-progress">
              Hazırlanan teklifler üstteki şeritte görünür; bitince "Aç" ile buraya gelir. Bu sırada başka sayfaya geçebilirsiniz.
            </p>
          )}
        </Card>

        {quote && (
          <Card className="border-[#E6E0D6] overflow-hidden">
            <div className="p-5">
              <AdminQuoteEditor quote={quote} onQuoteChange={onQuoteChange} />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------- Quotes view ----------
function QuotesView() {
  const [quotes, setQuotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);
  // V17.16: seçerek silme (çöp kutusu, 30 gün geri alınabilir) — yalnız ana yönetici.
  const isAdmin = getAdminRole() === "admin";
  const [checked, setChecked] = useState(new Set());
  const [trashOpen, setTrashOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toggleChecked = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allChecked = quotes.length > 0 && quotes.every((q) => checked.has(q.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(quotes.map((q) => q.id)));
  const [bulkRefresh, setBulkRefresh] = useState(null); // 17.37
  const openBulkRefresh = async () => {
    const ids = Array.from(checked);
    if (ids.length > 30) return toast.error("Tek seferde en fazla 30 teklif güncellenebilir.");
    setBulkRefresh({ loading: true });
    try {
      const r = await adminApi.post("/admin/catalog-refresh/bulk", { ids, rematch: true }, { timeout: 290000 });
      setBulkRefresh({ bulk: r.data?.quotes || [] });
    } catch (e) { setBulkRefresh({ error: e?.response?.status === 504 || !e?.response ? "Çok sayıda teklif seçildi, süre yetmedi; daha az teklifle deneyin." : (e?.response?.data?.detail || "Kontrol edilemedi.") }); }
  };
  const moveToTrash = async () => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} teklif çöp kutusuna taşınacak. 30 gün içinde geri alınabilir, sonra kalıcı olarak silinir.\n\nDevam edilsin mi?`)) return;
    setDeleting(true);
    try {
      const r = await adminApi.post("/admin/quote-trash/move", { ids });
      setQuotes((prev) => prev.filter((q) => !checked.has(q.id)));
      setChecked(new Set());
      toast.success(`${r.data?.moved || 0} teklif çöp kutusuna taşındı.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Teklifler silinemedi.");
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // V17.9: liste hafif gelir; kalemler teklif açılınca ayrıca yüklenir.
      const r = await adminApi.get("/admin/quotes", { params: { limit: 300 }, timeout: 60000 });
      setQuotes(r.data || []);
      setChecked(new Set());
    } catch {
      toast.error("Teklifler alınamadı. Sunucu meşgul olabilir; Yenile ile tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const openQuote = async (q) => {
    if (opening) return;
    setOpening(q.id);
    try {
      const r = await adminApi.get(`/admin/quotes/${q.id}`, { timeout: 60000 });
      setSelected(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Teklif açılamadı.");
    } finally {
      setOpening(null);
    }
  };

  useEffect(() => { load(); }, []);
  // 17.38: bildirim / e-posta bağlantısı (?teklif=ID) teklifi doğrudan açar
  const qsLocation = useLocation();
  const qsNav = useNavigate();
  useEffect(() => {
    const id = new URLSearchParams(qsLocation.search).get("teklif");
    if (!id) return;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/quotes/${id}`, { timeout: 60000 });
        setSelected(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Teklif açılamadı.");
      }
      qsNav("/admin/quotes", { replace: true });
    })();
  }, [qsLocation.search]);

  const handleQuoteUpdated = (updated) => {
    setSelected(updated);
    setQuotes((prev) => prev.map((q) => {
      if (q.id !== updated.id) return q;
      const { items, lines, ...rest } = updated;
      const openRequests = (items || []).filter((it) => it.admin_requested_by_customer && !["completed", "cancelled"].includes(it.admin_request_status)).length;
      return { ...q, ...rest, item_count: items?.length || q.item_count || 0, admin_request_count: openRequests };
    }));
  };

  return (
    <div>
      <PageHeader
        title="Teklif Talepleri"
        subtitle="Müşteri ve admin tarafından oluşturulan teklifler (son 300); satır seçerek toplu iskonto uygulanabilir"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && checked.size > 0 && (
              <Button type="button" size="sm" onClick={openBulkRefresh} className="bg-[#E8762D] hover:bg-[#D4651F] text-white" title="Seçilen teklifleri katalogdaki güncel fiyat/iskonto/kurla güncelle">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {checked.size} Teklifi Güncelle
              </Button>
            )}
            {isAdmin && checked.size > 0 && (
              <Button type="button" size="sm" onClick={moveToTrash} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {deleting ? "Siliniyor…" : `${checked.size} Teklifi Sil`}
              </Button>
            )}
            {isAdmin && (
              <Button type="button" variant="outline" size="sm" onClick={() => setTrashOpen(true)}>
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Çöp Kutusu
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile</Button>
          </div>
        }
      />
      <div className="p-3 sm:p-8">
        <Card className="border-[#E6E0D6] overflow-hidden">
          <div className="overflow-x-auto scroll-soft">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F7F4EF]">
                  {isAdmin && (
                    <Th className="w-10">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-[#DC2626]" title="Tümünü seç" />
                    </Th>
                  )}
                  <Th className="hidden md:table-cell">Tarih</Th>
                  <Th>Müşteri</Th>
                  <Th className="hidden md:table-cell">Firma</Th>
                  <Th className="hidden md:table-cell">Durum</Th>
                  <Th className="text-right hidden md:table-cell">Kalem</Th>
                  <Th className="text-right">Toplam<span className="hidden sm:inline"> (Nakit)</span></Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-6 text-center text-[#4B5563]">Yükleniyor…</td></tr>
                ) : quotes.length === 0 ? (
                  <tr><td colSpan={8} className="p-12 text-center text-[#4B5563]">Henüz teklif yok.</td></tr>
                ) : quotes.map((q) => (
                  <tr key={q.id} className={`border-b border-[#F1ECE4] hover:bg-[#F7F4EF] ${checked.has(q.id) ? "bg-red-50/60" : ""}`}>
                    {isAdmin && (
                      <td className="px-2 md:px-4 py-3 text-center">
                        <input type="checkbox" checked={checked.has(q.id)} onChange={() => toggleChecked(q.id)} className="w-4 h-4 accent-[#DC2626]" />
                      </td>
                    )}
                    <td className="px-4 py-3 text-[#4B5563] text-xs whitespace-nowrap hidden md:table-cell">{fmtDate(q.created_at)}</td>
                    <td className="px-3 md:px-4 py-3 text-[#1F2937]" onClick={() => { if (window.innerWidth < 768 && !opening) openQuote(q); }}>
                      <div className="font-medium md:font-normal">{q.customer?.name || "—"}</div>
                      <div className="md:hidden text-xs text-[#6B6F78]">{q.customer?.company || "—"}</div>
                      <div className="md:hidden text-[11px] text-[#9EA1A8] mt-0.5">{fmtDate(q.created_at)} · {q.item_count ?? q.items?.length ?? 0} kalem</div>
                      <div className="md:hidden mt-1.5"><div className="flex flex-wrap gap-1">
                          {q.summary?.is_draft ? (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Taslak</Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Hazır</Badge>
                          )}
                          {q.source_type === "whatsapp" && (
                            <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">WhatsApp{q.whatsapp_status === "new" ? " · yeni" : ""}</Badge>
                          )}
                          {q.source_type === "whatsapp" && q.whatsapp?.info_missing && (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" title="Müşteri ad / firma / e-posta bilgisini henüz yazmadı">bilgi eksik</Badge>
                          )}
                          {q.whatsapp_sent_at && (
                            <Badge variant="outline" className={q.whatsapp_status === "failed" ? "border-red-300 text-red-700 bg-red-50" : "border-green-300 text-green-700 bg-white"}>{waStatusText(q.whatsapp_status)}</Badge>
                          )}
                          {q.sales_status && <SalesBadge s={q.sales_status} reason={q.sales_reason} />}
                          {Number(q.admin_request_count || 0) > 0 && (
                            <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">{q.admin_request_count} ürün admin bekliyor</Badge>
                          )}
                          {q.submitted_to_admin && q.customer_submission_status !== "completed" && Number(q.admin_request_count || 0) === 0 && (
                            <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">Eski toplu admin talebi</Badge>
                          )}
                        </div></div>
                    </td>
                    <td className="px-4 py-3 text-[#1F2937] hidden md:table-cell">{q.customer?.company || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {q.summary?.is_draft ? (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Taslak</Badge>
                        ) : (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Hazır</Badge>
                        )}
                        {q.source_type === "whatsapp" && (
                          <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">WhatsApp{q.whatsapp_status === "new" ? " · yeni" : ""}</Badge>
                        )}
                        {q.source_type === "whatsapp" && q.whatsapp?.info_missing && (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" title="Müşteri ad / firma / e-posta bilgisini henüz yazmadı">bilgi eksik</Badge>
                        )}
                        {q.whatsapp_sent_at && (
                          <Badge variant="outline" className={q.whatsapp_status === "failed" ? "border-red-300 text-red-700 bg-red-50" : "border-green-300 text-green-700 bg-white"}>{waStatusText(q.whatsapp_status)}</Badge>
                        )}
                        {q.sales_status && <SalesBadge s={q.sales_status} reason={q.sales_reason} />}
                        {Number(q.admin_request_count || 0) > 0 && (
                          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">{q.admin_request_count} ürün admin bekliyor</Badge>
                        )}
                        {q.submitted_to_admin && q.customer_submission_status !== "completed" && Number(q.admin_request_count || 0) === 0 && (
                          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">Eski toplu admin talebi</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{q.item_count ?? q.items?.length ?? 0}</td>
                    <td className="px-2 md:px-4 py-3 text-right tabular-nums font-medium text-[#E8762D] whitespace-nowrap text-xs md:text-sm">{fmtTRY(q.totals?.cash || 0)}</td>
                    <td className="px-1 md:px-4 py-3 text-right">
                      <button
                        onClick={() => openQuote(q)}
                        disabled={!!opening}
                        className="p-1.5 hover:bg-[#F1ECE4] rounded transition-colors disabled:opacity-50"
                        title="Görüntüle / düzenle"
                      >
                        {opening === q.id ? <RefreshCw className="w-4 h-4 text-[#0F1B2D] animate-spin" /> : <Eye className="w-4 h-4 text-[#0F1B2D]" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <QuoteDetailDialog quote={selected} onClose={() => setSelected(null)} onQuoteChange={handleQuoteUpdated} />
      <CatalogRefreshDialog state={bulkRefresh} onClose={() => setBulkRefresh(null)}
        onApplied={() => { setBulkRefresh(null); setChecked(new Set()); load(); }} />
      {isAdmin && <QuoteTrashDialog open={trashOpen} onClose={() => setTrashOpen(false)} onRestored={load} />}
    </div>
  );
}

function QuoteTrashDialog({ open, onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/quote-trash", { timeout: 60000 });
      setRows(r.data || []);
      setChecked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Çöp kutusu açılamadı.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const toggle = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
  const run = async (kind) => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    if (kind === "purge" && !window.confirm(`${ids.length} teklif ve yüklenen dosyaları KALICI olarak silinecek; geri alınamaz.\n\nEmin misiniz?`)) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/quote-trash/${kind}`, { ids });
      toast.success(kind === "restore" ? `${r.data?.restored || 0} teklif geri alındı.` : `${r.data?.purged || 0} teklif kalıcı olarak silindi.`);
      await load();
      if (kind === "restore") onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem yapılamadı.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D] flex items-center gap-2"><Trash2 className="w-4 h-4" /> Çöp Kutusu</DialogTitle>
          <DialogDescription className="text-xs text-[#4B5563]">
            Silinen teklifler 30 gün burada durur; geri alınabilir. Süresi dolan teklifler yüklenen dosyalarıyla birlikte kendiliğinden silinir.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" size="sm" variant="outline" disabled={busy || !checked.size} onClick={() => run("restore")}>
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Geri Al{checked.size ? ` (${checked.size})` : ""}
          </Button>
          <Button type="button" size="sm" disabled={busy || !checked.size} onClick={() => run("purge")} className="bg-red-600 hover:bg-red-700 text-white">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Kalıcı Sil{checked.size ? ` (${checked.size})` : ""}
          </Button>
        </div>
        <div className="overflow-x-auto border border-[#E6E0D6] rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F4EF]">
                <Th className="w-10"><input type="checkbox" checked={allChecked} onChange={() => setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)))} className="w-4 h-4" /></Th>
                <Th>Teklif Tarihi</Th>
                <Th>Müşteri</Th>
                <Th>Silinme</Th>
                <Th className="text-right">Kalem</Th>
                <Th className="text-right">Kalan Gün</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#4B5563]">Yükleniyor…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-[#4B5563]">Çöp kutusu boş.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F1ECE4]">
                  <td className="px-4 py-2 text-center"><input type="checkbox" checked={checked.has(r.id)} onChange={() => toggle(r.id)} className="w-4 h-4" /></td>
                  <td className="px-4 py-2 text-xs text-[#4B5563] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-2">{r.customer?.name || "—"}{r.customer?.company ? ` / ${r.customer.company}` : ""}</td>
                  <td className="px-4 py-2 text-xs text-[#4B5563] whitespace-nowrap">{fmtDate(r.deleted_at)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.summary?.total_items ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.days_left ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDetailDialog({ quote, onClose, onQuoteChange }) {
  const [view, setView] = useState("editor");
  useEffect(() => { setView("editor"); }, [quote?.id]);
  if (!quote) return null;
  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white w-[calc(100vw-12px)] sm:w-full max-w-6xl max-h-[96dvh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden p-3 sm:p-6 grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D] text-xl" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            Teklif {quote.id.slice(0, 8).toUpperCase()}
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-xs">
            {fmtDate(quote.created_at)} — {quote.customer?.name || "—"} / {quote.customer?.company || "—"} / {quote.customer?.phone || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 border-b border-[#E6E0D6] -mt-1">
          <button
            type="button"
            onClick={() => setView("editor")}
            className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b-2 -mb-px ${view === "editor" ? "border-[#E8762D] text-[#0F1B2D]" : "border-transparent text-[#6B6F78] hover:text-[#0F1B2D]"}`}
          >
            Teklif
          </button>
          <button
            type="button"
            onClick={() => setView("source")}
            className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b-2 -mb-px flex items-center gap-1.5 ${view === "source" ? "border-[#E8762D] text-[#0F1B2D]" : "border-transparent text-[#6B6F78] hover:text-[#0F1B2D]"}`}
          >
            <ImageIcon className="w-3.5 h-3.5" /> Gelen Liste
          </button>
        </div>
        {view === "editor"
          ? <AdminQuoteEditor quote={quote} onQuoteChange={onQuoteChange} />
          : <QuoteSourceView quote={quote} onQuoteChange={onQuoteChange} />}
      </DialogContent>
    </Dialog>
  );
}

function AdminQuoteEditor({ quote, onQuoteChange }) {
  const [shiftCash, setShiftCash] = useState(""); // 17.49
  const [shiftCard, setShiftCard] = useState("");
  const [shiftPrev, setShiftPrev] = useState(null); // { dir, c, k, body, rows, skipped, has_fraction }
  const [fxBusy, setFxBusy] = useState(false); // 17.35
  const [custEdit, setCustEdit] = useState(null); // 17.36
  const [custSaving, setCustSaving] = useState(false);
  const [catRefresh, setCatRefresh] = useState(null); // 17.37
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [cashDiscount, setCashDiscount] = useState("");
  const [cardDiscount, setCardDiscount] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [matchIdx, setMatchIdx] = useState(null);
  const [pricingIdx, setPricingIdx] = useState(null);
  const [includeOverrides, setIncludeOverrides] = useState(false);
  const [approvingAI, setApprovingAI] = useState(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [waSending, setWaSending] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendWa, setSendWa] = useState(false);
  const [sendMail, setSendMail] = useState(false);
  const [sendCash, setSendCash] = useState(true);
  const [sendCard, setSendCard] = useState(true);
  const aiApprovalPending = useRef(false);

  useEffect(() => {
    setSelectedRows(new Set());
    setMatchIdx(null);
    setPricingIdx(null);
    setIncludeOverrides(false);
    if (quote?.id && quote?.source_type === "whatsapp" && quote?.whatsapp_status === "new") {
      adminApi.post(`/admin/quotes/${quote.id}/whatsapp-seen`).catch(() => {});
    }
  }, [quote?.id]);

  if (!quote) return null;

  const approveAISuggestion = async (itemIdx) => {
    const item = quote.items?.[itemIdx];
    const suggestion = item?.ai_shadow;
    if (aiApprovalPending.current || item?.matched || !suggestion?.safe || !suggestion?.requires_admin_approval || !suggestion?.recommended_product_id) return;
    aiApprovalPending.current = true;
    setApprovingAI(itemIdx);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/items/${itemIdx}/select`, {
        product_id: suggestion.recommended_product_id,
        learn_match: true,
      });
      onQuoteChange?.(r.data.quote);
      toast.success(r.data?.quote?.items?.[itemIdx]?.price_pending
        ? "AI önerisi onaylandı; fiyat girişi bekliyor."
        : "AI önerisi onaylandı ve teklif yeniden hesaplandı.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI önerisi onaylanamadı.");
    } finally {
      aiApprovalPending.current = false;
      setApprovingAI(null);
    }
  };

  // 17.25: miktar değiştirme + satır silme (birim fiyat aynı kalır, toplamlar sunucuda yeniden hesaplanır)
  const saveQty = async (itemIdx, qty) => {
    const item = quote.items?.[itemIdx];
    setRowBusy(itemIdx);
    try {
      const r = await adminApi.patch(`/admin/quotes/${quote.id}/items/${itemIdx}/qty`, {
        qty, source_text: item?.source_text || item?.searched || "",
      });
      onQuoteChange?.(r.data.quote);
      toast.success("Miktar güncellendi.");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Miktar güncellenemedi.");
      return false;
    } finally {
      setRowBusy(null);
    }
  };

  const deleteRow = async (itemIdx) => {
    const item = quote.items?.[itemIdx];
    const label = item?.matched ? item.product_name : item?.searched;
    if (!window.confirm(`Bu satır tekliften silinsin mi?\n\n${label || ""}`)) return;
    setRowBusy(itemIdx);
    try {
      const r = await adminApi.delete(`/admin/quotes/${quote.id}/items/${itemIdx}`, {
        params: { source_text: item?.source_text || item?.searched || "" },
      });
      setSelectedRows(new Set());
      setMatchIdx(null);
      setPricingIdx(null);
      onQuoteChange?.(r.data.quote);
      toast.success("Satır silindi, toplamlar yeniden hesaplandı.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Satır silinemedi.");
    } finally {
      setRowBusy(null);
    }
  };

  const eligible = (quote.items || [])
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => it.matched && !it.price_pending)
    .map(({ i }) => i);
  const allEligibleSelected = eligible.length > 0 && eligible.every((i) => selectedRows.has(i));

  const toggleRow = (idx) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAllEligible = () => {
    setSelectedRows(() => allEligibleSelected ? new Set() : new Set(eligible));
  };

  const applyBulkDiscount = async () => {
    if (!selectedRows.size) return toast.error("İskonto uygulanacak ürünleri seçin.");
    const cash = Number(String(cashDiscount).replace(",", "."));
    const card = Number(String(cardDiscount).replace(",", "."));
    if (!Number.isFinite(cash) || !Number.isFinite(card) || cash < 0 || cash > 100 || card < 0 || card > 100) {
      return toast.error("Nakit ve kart iskontosunu 0-100 arasında girin.");
    }
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/bulk-discount`, {
        item_indexes: Array.from(selectedRows).sort((a, b) => a - b),
        cash_discount: cash,
        card_discount: card,
        include_overrides: includeOverrides,
      });
      onQuoteChange?.(r.data.quote);
      const skipped = r.data.skipped?.length || 0;
      toast.success(`${r.data.updated_count} kalemin iskontosu güncellendi${skipped ? `, ${skipped} kalem atlandı` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu iskonto uygulanamadı.");
    } finally {
      setSaving(false);
    }
  };

  // 17.49: seçili satırların iskontosunu oransal geri / ileri çekme
  const shiftNum = (v) => (String(v).trim() === "" ? null : Number(String(v).replace(",", ".")));
  const openShift = async (dir) => {
    if (!selectedRows.size) return toast.error("İskontosu değişecek ürünleri seçin.");
    const c = shiftNum(shiftCash), k = shiftNum(shiftCard);
    if ((c === null || c === 0) && (k === null || k === 0)) return toast.error("Nakit ya da kart için oran yazın (ör. 10).");
    if ((c !== null && (!Number.isFinite(c) || c < 0 || c >= 100)) || (k !== null && (!Number.isFinite(k) || k < 0 || k >= 100))) return toast.error("Oranı 0-99 arasında yazın.");
    const sign = dir === "geri" ? -1 : 1;
    const body = { item_indexes: Array.from(selectedRows).sort((a, b) => a - b), cash_pct: c === null ? null : sign * c,
      card_pct: k === null ? null : sign * k, include_overrides: includeOverrides, preview: true };
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/shift-discount`, body);
      if (!r.data?.rows?.length) return toast.error("Seçilen satırlarda iskontosu değişebilecek fiyatlı ürün yok.");
      setShiftPrev({ dir, c, k, body, ...r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Hesaplanamadı."); }
  };
  const applyShift = async (round) => {
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/shift-discount`, { ...shiftPrev.body, round, preview: false });
      onQuoteChange?.(r.data.quote);
      toast.success(`${r.data.updated_count} kalemin iskontosu ${shiftPrev.dir === "geri" ? "geri" : "ileri"} çekildi.`);
      setShiftPrev(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Uygulanamadı."); } finally { setSaving(false); }
  };
  const fmtPct = (v) => `%${Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`;
  const roundHalf = (v) => Math.floor(Number(v) + 0.5);

  const fxRates = quoteFxRates(quote.items);
  const fxCurs = Object.keys(fxRates);
  const openCatalogRefresh = async () => {
    setCatRefresh({ loading: true });
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/catalog-refresh`, { rematch: true }, { timeout: 280000 });
      setCatRefresh({ single: r.data });
    } catch (e) { setCatRefresh({ error: e?.response?.data?.detail || "Katalog kontrol edilemedi." }); }
  };
  const openCustEdit = () => {
    const c = quote.customer || {};
    setCustEdit({ name: c.name || "", company: c.company === "WhatsApp" ? "" : (c.company || ""), phone: c.phone || "", email: c.email || "", also_card: false });
  };
  const saveCust = async () => {
    if (!custEdit.name.trim() || !custEdit.company.trim()) return toast.error("Ad soyad ve firma zorunlu.");
    setCustSaving(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/customer`, custEdit);
      if (r.data?.quote) onQuoteChange?.(r.data.quote);
      toast.success(r.data?.card_updated ? "Teklif ve müşteri kartı güncellendi." : "Teklifteki müşteri bilgisi güncellendi.");
      setCustEdit(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); }
    finally { setCustSaving(false); }
  };
  const refreshFx = async () => {
    if (!window.confirm("Teklifteki dövizli ürünler bugünün kuruyla yeniden hesaplansın mı? (Elle fiyat verdiğiniz satırlar değişmez)")) return;
    setFxBusy(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/fx-refresh`);
      if (r.data?.quote) onQuoteChange?.(r.data.quote);
      if ((r.data?.missing || []).length) toast.error(`${r.data.missing.join(", ")} kuru yok; Markalar sayfasından girin.`);
      toast.success(`${r.data?.changed || 0} dövizli satır bugünün kuruna çevrildi.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kur güncellenemedi."); }
    finally { setFxBusy(false); }
  };
  const refreshQuote = async () => {
    setRefreshing(true);
    try {
      const r = await adminApi.get(`/admin/quotes/${quote.id}`);
      onQuoteChange?.(r.data);
      toast.success("Teklif yenilendi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Teklif yenilenemedi.");
    } finally {
      setRefreshing(false);
    }
  };

  // V17.16: yapay zekâ kararları onay bekler; PDF/Excel/mail uyarır ama engellemez.
  const quoteItems = quote.items || [];
  const aiPendingCount = quoteItems.filter((it) => it?.matched && it?.ai_decision && !it.ai_decision.approved).length;
  const missingCount = quoteItems.filter((it) => !it?.matched || it?.price_pending).length;
  const confirmAI = (action) => !aiPendingCount || window.confirm(
    `${aiPendingCount} satırda yapay zekânın seçtiği ürün henüz onaylanmadı.\n\n${action} yine de devam edilsin mi?`
  );
  // 17.32: nakit ve kredi kartı (taksitli) teklif ayrı dosya
  const openExport = (kind, mode) => {
    if (!confirmAI(kind === "pdf" ? "PDF indirmeye" : "Excel indirmeye")) return;
    window.open(`${API}/quotes/${quote.id}/${kind}?mode=${mode}`, "_blank");
  };
  const approveAIDecision = async (itemIdx) => {
    if (aiApprovalPending.current) return;
    aiApprovalPending.current = true;
    setApprovingAI(itemIdx);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/items/${itemIdx}/approve-ai`);
      onQuoteChange?.(r.data.quote);
      toast.success("Yapay zekâ kararı onaylandı; bu eşleşme bundan sonra doğrudan gelecek.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı.");
    } finally {
      aiApprovalPending.current = false;
      setApprovingAI(null);
    }
  };

  // 17.30: tek "Gönder" penceresi — kanal seçimi admin'de (WhatsApp, Mail ya da ikisi). Otomatik ikinci kanal yok.
  const waPhone = quote.source_type === "whatsapp" ? (quote.customer?.phone || "") : (/^(\+?90|0)?\s*5\d{2}/.test(String(quote.customer?.phone || "").replace(/\s/g, "")) ? quote.customer?.phone : "");
  const lastWa = (quote.whatsapp_sends || [])[Math.max(0, (quote.whatsapp_sends || []).length - 1)] || null;
  const mailTo = String(quote.customer?.email || "").trim();
  const openSend = () => { setSendWa(!!waPhone); setSendMail(!waPhone && !!mailTo); setSendCash(true); setSendCard(true); setSendOpen(true); };
  const sendModes = [sendCash ? "cash" : null, sendCard ? "card" : null].filter(Boolean);
  const doSend = async () => {
    if (!sendWa && !sendMail) return toast.error("Bir kanal seçin: WhatsApp ya da Mail.");
    if (!sendModes.length) return toast.error("Gönderilecek teklifi seçin: Nakit ve/veya Kart.");
    setSending(true);
    const done = [];
    try {
      if (sendWa) {
        setWaSending(true);
        try {
          const r = await adminApi.post(`/admin/quotes/${quote.id}/whatsapp-send`, { also_email: false, modes: sendModes }, { timeout: 120000 });
          const names = (r.data?.sent_pdfs || sendModes).map((m) => (m === "cash" ? "nakit" : "kart")).join(" + ");
          done.push(`WhatsApp ${names} (${r.data?.mode})`);
          (r.data?.errors || []).forEach((er) => toast.error("WhatsApp: " + er));
        } catch (e) { toast.error("WhatsApp: " + (e?.response?.data?.detail || "gönderilemedi.")); }
        finally { setWaSending(false); }
      }
      if (sendMail) {
        try {
          const r = await adminApi.post(`/admin/quotes/${quote.id}/send-ready-email`, { modes: sendModes }, { timeout: 90000 });
          if (r.data?.sent) done.push(`Mail (${r.data.recipient})`);
          else toast.error("Mail: " + (r.data?.reason === "smtp_not_configured" ? "mail ayarı henüz yapılmadı." : r.data?.reason === "customer_email_missing" ? "müşterinin e-postası yok." : (r.data?.reason || "gönderilemedi.")));
        } catch (e) { toast.error("Mail: " + (e?.response?.data?.detail || "gönderilemedi.")); }
      }
      if (done.length) toast.success(`Gönderildi: ${done.join(", ")}`);
      setSendOpen(false);
      try { const q = await adminApi.get(`/admin/quotes/${quote.id}`); onQuoteChange?.(q.data); } catch { /* sonra görünür */ }
    } finally {
      setSending(false);
    }
  };
  const customerNotes = quote.customer_notes || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {quote.source_type === "whatsapp" && (
            <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">WhatsApp · {quote.customer?.phone}</Badge>
          )}
          {quote.source_type === "whatsapp" && quote.whatsapp?.info_missing && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" title="Müşteriye ad / firma / e-posta soruldu; cevap gelince otomatik güncellenir">Müşteri bilgisi eksik</Badge>
          )}
          {lastWa && (
            <Badge variant="outline" className={quote.whatsapp_status === "failed" ? "border-red-300 text-red-700 bg-red-50" : "border-green-300 text-green-700 bg-white"}>
              {waStatusText(quote.whatsapp_status)} · {fmtDate(lastWa.at)}
            </Badge>
          )}
          {quote.summary?.is_draft ? (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Taslak teklif</Badge>
          ) : (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Teklif hazır</Badge>
          )}
          <span className="text-xs text-[#6B7280]">
            {quote.summary?.unmatched_items || 0} eşleşmeyen • {quote.summary?.price_pending_items || 0} fiyat bekleyen
          </span>
          <SalesStatusControl quote={quote} onQuoteChange={onQuoteChange} />
          {aiPendingCount > 0 && (
            <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50">{aiPendingCount} yapay zekâ kararı onay bekliyor</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" size="sm" onClick={openCatalogRefresh} className="bg-[#E8762D] hover:bg-[#D4651F] text-white" title="Katalogdaki güncel fiyat, iskonto ve kurla karşılaştır; değişenleri göster">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Katalogdan güncelle
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={openCustEdit} title="Teklifteki müşteri adı, firma, telefon, e-posta">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Müşteri bilgisi
          </Button>
          {fxCurs.length > 0 && (
            <span className="text-xs rounded-md border border-orange-200 bg-[#FFF7ED] text-[#B85A1C] px-2 py-1" title="Teklifin hazırlandığı günün kuru">
              {fxCurs.map((c) => `1 ${c} = ${fmtRate(fxRates[c].rate)} TL`).join(" · ")} ({fxDateTR(fxRates[fxCurs[0]].date)})
            </span>
          )}
          {fxCurs.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={refreshFx} disabled={fxBusy} title="Dövizli satırları bugünün kuruyla yeniden hesapla">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fxBusy ? "animate-spin" : ""}`} /> Kuru güncelle
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={refreshQuote} disabled={refreshing}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Yenile
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => openExport("pdf", "nakit")} title="Yalnız nakit fiyatlarla PDF">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Nakit PDF
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => openExport("pdf", "kart")} title="Yalnız kredi kartı (taksitli) fiyatlarla PDF">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Kart PDF
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => openExport("excel", "nakit")} title="Yalnız nakit fiyatlarla Excel">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Nakit Excel
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => openExport("excel", "kart")} title="Yalnız kredi kartı (taksitli) fiyatlarla Excel">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Kart Excel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={sending}
            onClick={openSend}
            className="bg-[#0F1B2D] hover:bg-[#1B2B44] text-white"
            title="Teklif PDF'ini müşteriye gönder (WhatsApp / Mail seçimi)"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Gönderiliyor…" : "Gönder"}
          </Button>
        </div>
      </div>

      {customerNotes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-semibold mb-1">Müşteri notları (WhatsApp)</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {customerNotes.map((n, k) => (
              <li key={k}>{n.text}{n.after_quote && <span className="text-xs text-amber-700"> (teklif hazırlandıktan sonra yazdı)</span>}</li>
            ))}
          </ul>
        </Card>
      )}

      <CatalogRefreshDialog state={catRefresh} onClose={() => setCatRefresh(null)}
        onApplied={(d) => { setCatRefresh(null); if (d?.quote) onQuoteChange?.(d.quote); }} />
      <Dialog open={!!custEdit} onOpenChange={(o) => { if (!o && !custSaving) setCustEdit(null); }}>
        <DialogContent className="bg-white max-w-md w-[calc(100vw-16px)] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">Müşteri bilgisi</DialogTitle>
            <DialogDescription>Bu teklifte (PDF, Excel, gönderim) görünen bilgiler.</DialogDescription>
          </DialogHeader>
          {custEdit && (
            <div className="space-y-3">
              {[["name", "Ad Soyad *"], ["company", "Firma *"], ["phone", "Telefon"], ["email", "E-posta"]].map(([k, lbl]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{lbl}</Label>
                  <Input value={custEdit[k]} onChange={(e) => setCustEdit({ ...custEdit, [k]: e.target.value })} />
                </div>
              ))}
              <label className={`flex items-center gap-2 text-sm ${quote.customer_id ? "text-[#334155]" : "text-[#9EA1A8]"}`}>
                <input type="checkbox" checked={custEdit.also_card} disabled={!quote.customer_id}
                  onChange={(e) => setCustEdit({ ...custEdit, also_card: e.target.checked })} />
                Müşteri kartına da işle{!quote.customer_id ? " (teklif bir karta bağlı değil)" : ""}
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCustEdit(null)} disabled={custSaving}>İptal</Button>
            <Button onClick={saveCust} disabled={custSaving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{custSaving ? "Kaydediliyor…" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={(o) => { if (!o && !sending) setSendOpen(false); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">Teklifi gönder</DialogTitle>
            <DialogDescription>Teklif {String(quote.id).slice(0, 8).toUpperCase()} PDF olarak gönderilir. Hangi teklifin ve hangi kanaldan gideceğini siz seçin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(missingCount > 0 || aiPendingCount > 0) && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                {missingCount > 0 && <div>{missingCount} satır eşleşmedi ya da fiyat bekliyor (PDF'te belirtilir).</div>}
                {aiPendingCount > 0 && <div>{aiPendingCount} satırda yapay zekâ kararı onay bekliyor.</div>}
              </div>
            )}
            <label className={`flex items-start gap-3 border rounded-md p-3 ${waPhone ? "cursor-pointer hover:bg-[#F7F4EF]" : "opacity-50"}`}>
              <input type="checkbox" className="mt-1 accent-[#16A34A]" checked={sendWa} disabled={!waPhone} onChange={(e) => setSendWa(e.target.checked)} />
              <div>
                <div className="font-medium flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-[#16A34A]" /> WhatsApp</div>
                <div className="text-xs text-[#6B6F78]">{waPhone ? waPhone : "Müşterinin cep telefonu yok"}{lastWa ? ` · daha önce gönderildi (${fmtDate(lastWa.at)})` : ""}</div>
              </div>
            </label>
            <label className={`flex items-start gap-3 border rounded-md p-3 ${mailTo ? "cursor-pointer hover:bg-[#F7F4EF]" : "opacity-50"}`}>
              <input type="checkbox" className="mt-1 accent-[#0F1B2D]" checked={sendMail} disabled={!mailTo} onChange={(e) => setSendMail(e.target.checked)} />
              <div>
                <div className="font-medium flex items-center gap-1.5"><Send className="w-4 h-4 text-[#0F1B2D]" /> Mail</div>
                <div className="text-xs text-[#6B6F78]">{mailTo || "Müşterinin e-postası yok"}</div>
              </div>
            </label>
          </div>
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B6F78]">Gönderilecek teklif</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 border rounded-md p-3 cursor-pointer hover:bg-[#F7F4EF]">
                <input type="checkbox" className="accent-[#E8762D]" checked={sendCash} onChange={(e) => setSendCash(e.target.checked)} />
                <span className="text-sm font-medium">Nakit teklif</span>
              </label>
              <label className="flex items-center gap-2 border rounded-md p-3 cursor-pointer hover:bg-[#F7F4EF]">
                <input type="checkbox" className="accent-[#334155]" checked={sendCard} onChange={(e) => setSendCard(e.target.checked)} />
                <span className="text-sm font-medium">Kart (taksitli) teklif</span>
              </label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>İptal</Button>
            <Button onClick={doSend} disabled={sending || (!sendWa && !sendMail) || !sendModes.length} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">
              {sending ? "Gönderiliyor…" : "Gönder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-[#E6E0D6] bg-[#F7F4EF] p-4">
        <div className="flex flex-col xl:flex-row xl:items-end gap-3">
          <button
            type="button"
            onClick={toggleAllEligible}
            className="h-10 px-3 border border-[#D9D3C9] rounded-md bg-white text-sm flex items-center gap-2 hover:bg-[#F7F4EF]"
          >
            {allEligibleSelected ? <CheckSquare className="w-4 h-4 text-[#E8762D]" /> : <Square className="w-4 h-4" />}
            {allEligibleSelected ? "Seçimi Temizle" : "Tüm Fiyatlı Ürünleri Seç"}
          </button>
          <div className="space-y-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wide">Nakit İskonto %</Label>
            <Input type="number" min="0" max="100" step="0.01" value={cashDiscount} onChange={(e) => setCashDiscount(e.target.value)} placeholder="55" />
          </div>
          <div className="space-y-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wide">Kart İskonto %</Label>
            <Input type="number" min="0" max="100" step="0.01" value={cardDiscount} onChange={(e) => setCardDiscount(e.target.value)} placeholder="50" />
          </div>
          <Button type="button" onClick={applyBulkDiscount} disabled={saving || !selectedRows.size} className="bg-[#E8762D] hover:bg-[#D4651F] text-white h-10">
            <Percent className="w-4 h-4 mr-1.5" /> {saving ? "Uygulanıyor…" : `${selectedRows.size} Seçili Ürüne Uygula`}
          </Button>
          <label className="flex items-center gap-2 text-xs text-[#4B505B] pb-2">
            <input type="checkbox" checked={includeOverrides} disabled={saving} onChange={(e) => setIncludeOverrides(e.target.checked)} />
            Elle değiştirilen satırlara da uygula
          </label>
          <div className="text-xs text-[#6B7280] xl:ml-auto pb-2">
            Fiyat bekleyen ve eşleşmeyen satırlar toplu iskontoda otomatik atlanır.
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#E6E0D6] flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
          <div className="text-xs font-semibold text-[#0F1B2D] sm:pb-2.5">Seçili satırların iskontosunu oransal kaydır:</div>
          <div className="space-y-1 w-full sm:w-[130px]">
            <Label className="text-[10px] uppercase tracking-wide">Nakit %</Label>
            <Input inputMode="decimal" value={shiftCash} onChange={(e) => setShiftCash(e.target.value)} placeholder="ör. 10" />
          </div>
          <div className="space-y-1 w-full sm:w-[130px]">
            <Label className="text-[10px] uppercase tracking-wide">Kart %</Label>
            <Input inputMode="decimal" value={shiftCard} onChange={(e) => setShiftCard(e.target.value)} placeholder="boş = değişmez" />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => openShift("geri")} disabled={saving || !selectedRows.size} className="h-10 border-[#D9D3C9] bg-white">Geri çek (−)</Button>
            <Button type="button" variant="outline" onClick={() => openShift("ileri")} disabled={saving || !selectedRows.size} className="h-10 border-[#D9D3C9] bg-white">İleri çek (+)</Button>
          </div>
          <div className="text-[11px] text-[#6B7280] sm:pb-2.5">Ör. %45 iskonto, %10 geri → %40,5. Önce sonucu gösterir.</div>
        </div>
      </Card>

      <Dialog open={!!shiftPrev} onOpenChange={(o) => { if (!o) setShiftPrev(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>İskontoyu {shiftPrev?.dir === "geri" ? "geri" : "ileri"} çek</DialogTitle>
            <DialogDescription>
              {shiftPrev?.c ? `Nakit: %${shiftPrev.c} ${shiftPrev.dir === "geri" ? "azalt" : "artır"}` : "Nakit değişmez"} · {shiftPrev?.k ? `Kart: %${shiftPrev.k} ${shiftPrev.dir === "geri" ? "azalt" : "artır"}` : "Kart değişmez"}
              {shiftPrev?.skipped?.length ? ` · ${shiftPrev.skipped.length} satır atlandı (eşleşmeyen / fiyat bekleyen / elle fiyatlı)` : ""}
            </DialogDescription>
          </DialogHeader>
          {shiftPrev && (
            <table className="w-full text-xs">
              <thead><tr className="bg-[#F7F4EF] text-left"><th className="p-2">Ürün</th><th className="p-2 text-right">Nakit</th><th className="p-2 text-right">Kart</th></tr></thead>
              <tbody className="divide-y divide-[#F0EBE3]">
                {shiftPrev.rows.map((r) => (
                  <tr key={r.index}>
                    <td className="p-2">{r.product_name || r.searched}</td>
                    <td className="p-2 text-right whitespace-nowrap">{fmtPct(r.old_cash)} → <b>{fmtPct(r.raw_cash)}</b>{r.raw_cash !== Math.trunc(r.raw_cash) && <span className="text-[#9EA1A8]"> ({fmtPct(roundHalf(r.raw_cash))})</span>}</td>
                    <td className="p-2 text-right whitespace-nowrap">{fmtPct(r.old_card)} → <b>{fmtPct(r.raw_card)}</b>{r.raw_card !== Math.trunc(r.raw_card) && <span className="text-[#9EA1A8]"> ({fmtPct(roundHalf(r.raw_card))})</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {shiftPrev?.has_fraction && <p className="text-sm text-[#0F1B2D] font-medium">Kesirli çıkan iskontolar en yakın tam sayıya yuvarlansın mı? (parantez içindeki değer; 0,5 yukarı yuvarlanır)</p>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShiftPrev(null)} disabled={saving}>Vazgeç</Button>
            {shiftPrev?.has_fraction ? (
              <>
                <Button variant="outline" onClick={() => applyShift(false)} disabled={saving}>Yuvarlamadan uygula</Button>
                <Button onClick={() => applyShift(true)} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">Yuvarlayarak uygula</Button>
              </>
            ) : (
              <Button onClick={() => applyShift(false)} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">Uygula</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="overflow-x-auto scroll-soft border border-[#E6E0D6] rounded-md">
        <table className="w-full text-xs min-w-[1050px]">
          <thead>
            <tr className="bg-[#F7F4EF]">
              <Th className="w-10">Seç</Th>
              <Th>Aranan</Th>
              <Th>Eşleşen</Th>
              <Th>Marka</Th>
              <Th className="text-right">Mik.</Th>
              <Th className="text-right">İsk. N/K</Th>
              <Th className="text-right">Liste</Th>
              <Th className="text-right">Nakit</Th>
              <Th className="text-right">Kart</Th>
            </tr>
          </thead>
          <tbody>
            {(quote.items || []).map((it, i) => {
              const canSelect = !!it.matched && !it.price_pending;
              const isSelected = selectedRows.has(i);
              return (
                <tr key={i} className={`border-b border-[#F1ECE4] ${isSelected ? "bg-orange-50" : it.price_pending ? "bg-amber-50/60" : !it.matched ? "bg-red-50/40" : ""}`}>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!canSelect}
                      onChange={() => toggleRow(i)}
                      className="w-4 h-4 accent-[#E8762D] disabled:opacity-30"
                      title={!it.matched ? "Eşleşmemiş ürün" : it.price_pending ? "Önce fiyat girilmeli" : "İskonto için seç"}
                    />
                  </td>
                  <td className="px-3 py-2 text-[#4B5563] max-w-[220px]">{it.searched}
                            {it.review_reason && (!it.matched || it.needs_review || it.qty_missing) && (
                              <div className="mt-1 text-xs text-amber-800">{it.review_reason}</div>
                            )}</td>
                  <td className="px-3 py-2 text-[#1F2937] max-w-[320px]">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {it.matched ? it.product_name : <span className="text-red-600 font-medium">Eşleşme bulunamadı</span>}
                        {it.matched && it.fx?.rate && <div className="text-[10px] text-[#B85A1C]">{fmtCur(it.fx.cur, it.fx.list)} liste · kur {fmtRate(it.fx.rate)}</div>}
                        {it.matched && it.fx?.missing && <div className="text-[10px] text-red-600">{it.fx.cur} kuru yok</div>}
                        {it.matched && it.ai_decision && (
                          it.ai_decision.approved ? (
                            <div className="mt-1 text-[10px] text-emerald-700 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Yapay zekâ kararı onaylandı
                            </div>
                          ) : (
                            <div className="mt-2 rounded border border-violet-200 bg-violet-50 px-2 py-1.5 text-xs text-violet-900">
                              <div>Yapay zekâ seçti (%{Math.round(Number(it.ai_decision.confidence || 0) * 100)})</div>
                              <button type="button" disabled={approvingAI !== null} onClick={() => approveAIDecision(i)} className="mt-1 font-semibold underline disabled:opacity-50">
                                {approvingAI === i ? "Onaylanıyor…" : "Onayla ve öğren"}
                              </button>
                            </div>
                          )
                        )}
                        {!it.matched && it.ai_shadow?.safe && it.ai_shadow?.requires_admin_approval && it.ai_shadow?.recommended_product_id && (
                          <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-2 text-xs text-amber-900">
                            <div>AI önerisi: {it.ai_shadow.product_name}</div>
                            <button type="button" disabled={approvingAI !== null} onClick={() => approveAISuggestion(i)} className="mt-1 font-semibold underline disabled:opacity-50">
                              {approvingAI === i ? "Onaylanıyor…" : "Onayla ve öğren"}
                            </button>
                          </div>
                        )}
                        {it.price_override && (
                          <div className="mt-1 text-[10px] text-amber-800">
                            <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-800">Elle değiştirildi</Badge>
                            <div>Orijinal: {fmtTRY(it.original_list_price)} · nakit %{it.original_cash_discount} · kart %{it.original_card_discount}</div>
                          </div>
                        )}
                        {it.price_pending && <span className="ml-1 text-[9px] uppercase text-amber-700">Fiyat bekliyor</span>}
                        {it.admin_requested_by_customer && it.admin_request_status !== "completed" && (
                          <div className="mt-1 space-y-1">
                            <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 text-[9px]">Müşteri bu ürün için admin istedi</Badge>
                            {it.admin_request_note && (
                              <div className="text-[10px] text-[#0F1B2D] bg-blue-50 border border-blue-100 rounded px-2 py-1">
                                <strong>Müşteri notu:</strong> {it.admin_request_note}
                              </div>
                            )}
                          </div>
                        )}
                        {/* V15.4.2: teşhis paneli HER satırda açılabilir (deterministic eşleşmeler dahil). */}
                        <details className="mt-2 text-[10px] text-[#4B505B] bg-[#F7F4EF] border border-[#E6E0D6] rounded px-2 py-1.5">
                          <summary className="cursor-pointer font-semibold text-[#0F1B2D]">AI / Matcher teşhisi</summary>
                          <div className="mt-1 space-y-0.5">
                            <div>Kaynak: {it.match_via || (it.matched ? "deterministic" : "no_match")}</div>
                            <div>Section: {it.section || "—"}</div>
                            {it.v14_effective_line && <div className="break-words">Matcher satırı: {it.v14_effective_line}</div>}
                            {it.ai_diagnostics?.candidate_count !== undefined && <div>Aday: {it.ai_diagnostics.candidate_count}</div>}
                            {it.ai_diagnostics?.luna && <div>Luna: {it.ai_diagnostics.luna.selected_product_id || it.ai_diagnostics.luna.result || "—"} {it.ai_diagnostics.luna.confidence !== undefined ? `(%${Math.round(Number(it.ai_diagnostics.luna.confidence) * 100)})` : ""}</div>}
                            {it.ai_diagnostics?.sol && <div>Sol: {it.ai_diagnostics.sol.selected_product_id || it.ai_diagnostics.sol.result || "—"} {it.ai_diagnostics.sol.confidence !== undefined ? `(%${Math.round(Number(it.ai_diagnostics.sol.confidence) * 100)})` : ""}</div>}
                            {it.ai_diagnostics?.selected_by && <div>Seçen: {it.ai_diagnostics.selected_by}</div>}
                            {it.ai_diagnostics?.interpretation_conflict && <div className="text-red-700">Yorum çelişkisi: {it.ai_diagnostics.interpretation_conflict}</div>}
                            {it.ai_diagnostics?.veto_reason && <div className="text-red-700">Veto: {it.ai_diagnostics.veto_reason}</div>}
                            {it.safety_reason && <div className="text-red-700">Safety: {it.safety_reason}</div>}
                            {it.ai_diagnostics?.rule_rescue && <div>Kural rescue: {it.ai_diagnostics.rule_rescue.result || "—"} ({it.ai_diagnostics.rule_rescue.candidate_identities ?? 0} aday)</div>}
                            {it.ai_diagnostics?.latency_ms !== undefined && <div>Süre: {it.ai_diagnostics.latency_ms} ms (toplu çağrı)</div>}
                            {it.ai_diagnostics?.cost_usd_share !== undefined && <div>Tahmini maliyet payı: ${Number(it.ai_diagnostics.cost_usd_share).toFixed(5)}</div>}
                            {it.ai_diagnostics?.candidates?.length > 0 && (
                              <div className="pt-1">İlk aday: {it.ai_diagnostics.candidates[0]?.name || "—"}</div>
                            )}
                          </div>
                        </details>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMatchIdx(i)}
                        className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded flex items-center gap-1 ${it.matched ? "text-[#0F1B2D] bg-white border border-[#D9D3C9] hover:bg-[#F7F4EF]" : "text-white bg-[#E8762D] hover:bg-[#D4651F]"}`}
                        title={it.matched ? "Eşleşmiş ürünü katalogdan değiştir" : "Eşleşmeyen satır için katalogdan doğru ürünü seç"}
                      >
                        <Wand2 className="w-3 h-3" /> {it.matched ? "Değiştir" : "Ürün Seç"}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">{it.brand_name || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums align-top">
                    <QtyCell value={it.qty} disabled={rowBusy !== null} onSave={(n) => saveQty(i, n)} />
                    <button
                      type="button"
                      onClick={() => deleteRow(i)}
                      disabled={rowBusy !== null}
                      className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-red-600 hover:text-red-800 disabled:opacity-40"
                      title="Satırı tekliften sil"
                    >
                      <Trash2 className="w-3 h-3" /> Sil
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{canSelect ? `%${Number(it.cash_discount || 0).toFixed(2)} / %${Number(it.card_discount || 0).toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <div>{canSelect ? fmtTRY(it.line_list) : "—"}</div>
                    {it.matched && <button type="button" onClick={() => setPricingIdx(i)} className="mt-1 text-[10px] text-[#0F1B2D] underline">Fiyat / İskonto</button>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-[#E8762D]">{canSelect ? fmtTRY(it.line_cash) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{canSelect ? fmtTRY(it.line_card) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#0F1B2D] text-white">
              <td colSpan={6} className="px-3 py-2 text-right uppercase tracking-wide text-[10px]">Fiyatlanan kalemlerin toplamı</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtTRY(quote.totals?.list || 0)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtTRY(quote.totals?.cash || 0)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtTRY(quote.totals?.card || 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="flex justify-start">
        <Button type="button" variant="outline" onClick={() => setAddLineOpen(true)} className="text-xs border-[#E8762D] text-[#B85A1C] hover:bg-[#FFF7ED]">
          <Plus className="w-4 h-4 mr-1.5" /> Satır Ekle
        </Button>
      </div>
      {quote.summary?.is_draft && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Bu teklif tamamlanmamış olsa da PDF ve Excel alınabilir. Çıktıdaki toplamlar yalnızca o anda fiyatlandırılmış kalemleri kapsar.
        </p>
      )}

      {pricingIdx !== null && (
        <AdminItemPricingDialog key={`pricing-${quote.id}-${pricingIdx}`} quote={quote} itemIdx={pricingIdx} onClose={() => setPricingIdx(null)} onSaved={(updated) => onQuoteChange?.(updated)} />
      )}
      <AddQuoteLineDialog
        open={addLineOpen}
        quote={quote}
        onClose={() => setAddLineOpen(false)}
        onAdded={(updated) => { onQuoteChange?.(updated); setAddLineOpen(false); }}
      />
      <AdminProductMatchDialog
        key={`admin-mm-${quote?.id}-${matchIdx}`}
        open={matchIdx !== null}
        quote={quote}
        itemIdx={matchIdx}
        onClose={() => setMatchIdx(null)}
        onMatched={(updated) => { onQuoteChange?.(updated); setMatchIdx(null); }}
      />
    </div>
  );
}

function AdminItemPricingDialog({ quote, itemIdx, onClose, onSaved }) {
  const item = quote.items?.[itemIdx];
  const [form, setForm] = useState({ list_price: "", cash_discount: "", card_discount: "" });
  const [saveToCatalog, setSaveToCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);

  const save = async (reset = false) => {
    if (pending.current || !item?.matched) return;
    const payload = reset ? { reset: true, save_to_catalog: false } : { save_to_catalog: saveToCatalog };
    if (!reset) {
      for (const [key, value] of Object.entries(form)) {
        if (!value.trim()) continue;
        const number = Number(value.replace(",", "."));
        if (!Number.isFinite(number) || number < 0 || (key !== "list_price" && number > 100)) {
          toast.error(key === "list_price" ? "Liste fiyatı 0 veya daha büyük olmalı." : "İskonto 0–100 arasında olmalı.");
          return;
        }
        payload[key] = number;
      }
      if (!Object.keys(form).some((key) => key in payload)) {
        toast.error("En az bir alanı doldurun. Boş alanlar değişmez.");
        return;
      }
    }
    pending.current = true;
    setSaving(true);
    try {
      const response = await adminApi.patch(`/admin/quotes/${quote.id}/items/${itemIdx}/pricing`, payload);
      onSaved?.(response.data);
      toast.success(reset ? "Satır orijinal değerlerine döndü." : "Satır fiyatları ve teklif toplamları güncellendi.");
      onClose();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Fiyat düzenlemesi kaydedilemedi.");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  if (!item) return null;
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !pending.current) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Satır fiyatını ve iskontosunu düzenle</DialogTitle>
          <DialogDescription>{item.product_name} · {item.qty} birim. Boş alanlar değişmez.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {[
            ["list_price", "Liste birim fiyatı (₺, KDV hariç)", item.list_unit],
            ["cash_discount", "Nakit iskonto %", item.cash_discount],
            ["card_discount", "Kart iskonto %", item.card_discount],
          ].map(([key, label, current]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`item-pricing-${key}`}>{label}</Label>
              <Input id={`item-pricing-${key}`} type="number" min="0" max={key === "list_price" ? undefined : 100} step="0.01" disabled={saving} value={form[key]} placeholder={String(current ?? 0)} onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))} />
              <p className="text-xs text-[#6B7280]">Mevcut: {key === "list_price" ? fmtTRY(current || 0) : `%${current || 0}`}</p>
            </div>
          ))}
          {item.price_pending && <p className="text-xs text-amber-700">Fiyat bekliyor. Liste fiyatı girilene kadar bu satır toplama eklenmez.</p>}
          {item.price_override && <p className="text-xs text-amber-700">Orijinal: {fmtTRY(item.original_list_price)} · nakit %{item.original_cash_discount} · kart %{item.original_card_discount}</p>}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={saveToCatalog} disabled={saving} onChange={(e) => setSaveToCatalog(e.target.checked)} />
            Kataloğa da kaydet
          </label>
          <p className="text-xs text-[#6B7280]">Varsayılan olarak yalnız bu teklifin bu satırı değişir. Kataloğa kaydetmek yalnız bu ürünün girilen alanlarını günceller; markanın genel iskontosu değişmez.</p>
        </div>
        <DialogFooter>
          {item.price_override && <Button type="button" variant="outline" disabled={saving} onClick={() => save(true)}>Sıfırla</Button>}
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>Vazgeç</Button>
          <Button type="button" disabled={saving} onClick={() => save(false)}>{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminProductMatchDialog({ open, quote, itemIdx, onClose, onMatched }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");
  const searchRequest = useRef(0);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [learn, setLearn] = useState(true);
  const item = quote && typeof itemIdx === "number" ? quote.items?.[itemIdx] : null;

  const runSearch = async (query) => {
    const requestId = ++searchRequest.current;
    setSelectedId(null);
    setSearchMessage("");
    if (!String(query || "").trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await adminApi.get("/products/search", { params: { q: query, context: item?.searched || "", limit: 50 } });
      if (requestId !== searchRequest.current) return;
      const data = r.data;
      setResults(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
      setSearchMessage(Array.isArray(data) ? "" : String(data?.message || ""));
    } catch {
      if (requestId !== searchRequest.current) return;
      setResults([]);
      setSearchMessage("Katalog araması tamamlanamadı. Yeniden deneyin.");
    } finally {
      if (requestId === searchRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !item) return;
    // V17.11: baştaki sayı yalnız arkasından birim gelirse adet sayılır ("150x125 redüksiyon" korunur);
    // sondaki "— 1 adet" ve "[Talep: ...]" notları aramaya katılmaz.
    const initial = String(item.searched || "")
      .replace(/\s*\[[^\]]*\]\s*$/, "")
      .replace(/\s+(?:[—–-]\s*)?\d+(?:[.,]\d+)?\s*(adet|ad|metre|mt|kg|takım|takim|paket|kutu|boy|bağ|set)\.?\s*$/i, "")
      .replace(/^\s*\d+(?:[.,]\d+)?\s*(adet|ad|metre|mt|m|kg|takım|takim|paket|kutu|boy|bağ|set)\.?\s+/i, "")
      .trim();
    setQ(initial);
    setSelectedId(null);
    setLearn(true);
    runSearch(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  useEffect(() => () => { searchRequest.current += 1; }, [open]);

  const confirm = async () => {
    if (!selectedId) return toast.error("Bir katalog ürünü seçin.");
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/quotes/${quote.id}/items/${itemIdx}/select`, {
        product_id: selectedId,
        learn_match: learn,
      });
      toast.success(r.data?.quote?.items?.[itemIdx]?.price_pending
        ? "Ürün eşleştirildi; fiyat girişi bekliyor."
        : "Ürün eşleştirildi ve teklif yeniden hesaplandı.");
      onMatched?.(r.data.quote);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ürün eşleştirilemedi.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !item) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-[#0F1B2D] flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#E8762D]" /> Admin Ürün Eşleştirme</DialogTitle>
          <DialogDescription className="text-xs text-[#4B5563]">Aranan satır: <strong>{item.searched}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9EA1A8]" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Ürün adı, ölçü veya kod ile ara…" />
          </div>
          <div className="flex items-center justify-between text-[11px] text-[#6B6F78]">
            <span>{loading ? "Katalog taranıyor…" : `${results.length} sonuç`}</span>
            <span>DN/inch ve saha yazımları dahil akıllı sıralama</span>
          </div>
          <div className="border border-[#E6E0D6] rounded-md max-h-[420px] overflow-y-auto divide-y divide-[#F1ECE4]">
            {loading ? <div className="p-8 text-center text-sm text-[#6B6F78]">Aranıyor…</div> : results.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#9EA1A8]">{searchMessage || "Katalogda sonuç bulunamadı."}</div>
            ) : results.map((p) => (
              <button key={p.id} type="button" onClick={() => setSelectedId(p.id)} className={`w-full text-left px-4 py-3 flex gap-3 items-center ${selectedId === p.id ? "bg-orange-50 border-l-4 border-l-[#E8762D]" : "hover:bg-[#F7F4EF] border-l-4 border-l-transparent"}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[#1F2937] flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.recommended && <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">En uygun</span>}
                  </div>
                  <div className="text-[11px] text-[#6B6F78] mt-1 flex flex-wrap gap-x-2">
                    <span>{p.brand_name}</span>{p.code && <span className="font-mono">· {p.code}</span>}
                    {p.search_reason && <span className="text-emerald-700">· {p.search_reason}</span>}
                    {Number.isFinite(Number(p.search_score)) && <span>· skor {Number(p.search_score).toFixed(0)}</span>}
                  </div>
                </div>
                <div className="text-sm font-medium whitespace-nowrap">{Number(p.list_price || 0) > 0 ? fmtTRY(p.list_price) : <span className="text-amber-700 text-xs">Fiyat yok</span>}</div>
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4B505B]">
            <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} className="accent-[#E8762D]" />
            Bu admin onayından öğren; aynı müşteri ifadesinde gelecekte güçlü eşleşme olarak kullan.
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>İptal</Button>
            <Button onClick={confirm} disabled={!selectedId || saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{saving ? "Kaydediliyor…" : "Ürünü Eşleştir"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// PRAMID V17.7: müşterinin yüklediği orijinal dosya + sistemin okuduğu liste yan yana.
function QuoteSourceView({ quote, onQuoteChange }) {
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [matchIdx, setMatchIdx] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setSource(null);
    setActiveId(null);
    adminApi.get(`/admin/quotes/${quote.id}/source`)
      .then((r) => {
        if (!alive) return;
        setSource(r.data);
        setActiveId(r.data?.files?.[0]?.id || null);
      })
      .catch((e) => { if (alive) toast.error(e?.response?.data?.detail || "Gelen liste alınamadı."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [quote.id]);

  const files = source?.files || [];
  const active = files.find((f) => f.id === activeId) || null;
  const items = quote.items || [];
  const excelInfo = quote.summary?.excel_reading || null;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card className="border-[#E6E0D6] overflow-hidden flex flex-col min-h-[420px]">
        <div className="px-4 py-3 border-b border-[#F1ECE4] flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-[#0F1B2D]">Müşterinin gönderdiği</div>
            <div className="text-[11px] text-[#6B6F78]">
              {loading ? "Yükleniyor…" : files.length ? `${files.length} dosya` : (source?.source_type === "text" ? "Metin olarak girildi" : "Orijinal dosya yok")}
            </div>
          </div>
          {active && <SourceFileActions quoteId={quote.id} file={active} />}
        </div>
        {files.length > 1 && (
          <div className="px-3 py-2 border-b border-[#F1ECE4] flex gap-2 overflow-x-auto">
            {files.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setActiveId(f.id)}
                className={`shrink-0 text-[11px] px-2.5 py-1.5 rounded border ${f.id === activeId ? "border-[#E8762D] bg-orange-50 text-[#9A3412]" : "border-[#E6E0D6] text-[#4B505B] hover:bg-[#F7F4EF]"}`}
                title={f.filename}
              >
                {i + 1}. {f.filename.length > 22 ? `${f.filename.slice(0, 20)}…` : f.filename}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 bg-[#F7F4EF]">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#6B6F78]">Yükleniyor…</div>
          ) : active ? (
            <SourceFileViewer key={active.id} quoteId={quote.id} file={active} />
          ) : (
            <div className="p-5 space-y-3">
              {source?.source_type !== "text" && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Bu teklif için orijinal dosya saklanmamış (dosya saklama Sürüm 17.7 ile başladı).
                  {source?.legacy_filenames?.length > 0 && <> Dosya adı: <strong>{source.legacy_filenames.join(", ")}</strong></>}
                </div>
              )}
              <div className="text-[11px] uppercase tracking-wide text-[#6B6F78]">Gelen metin</div>
              <pre className="whitespace-pre-wrap text-xs text-[#1F2937] bg-white border border-[#E6E0D6] rounded p-3 max-h-[60vh] overflow-y-auto font-mono">
                {(source?.lines || []).join("\n") || "—"}
              </pre>
            </div>
          )}
        </div>
      </Card>

      <Card className="border-[#E6E0D6] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-[#F1ECE4]">
          <div className="text-sm font-semibold text-[#0F1B2D]">Sistemin okuduğu liste</div>
          {excelInfo && (
            <div className={`mt-1 text-[11px] rounded px-2 py-1 ${excelInfo.uncertain ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-[#F7F4EF] text-[#4B505B] border border-[#E6E0D6]"}`}>
              <div><strong>Excel okuma:</strong> {excelInfo.columns || "—"}</div>
              {excelInfo.skipped?.length > 0 && <div>Atlanan satırlar: {excelInfo.skipped.join(", ")}</div>}
              {excelInfo.no_qty?.length > 0 && <div>Miktarı okunamayan satırlar (1 adet, sarı): {excelInfo.no_qty.join(", ")}</div>}
              {excelInfo.assumed?.length > 0 && <div>Birimi yazılmayan boru satırları metre sayıldı: {excelInfo.assumed.join(", ")}</div>}
            </div>
          )}
          <div className="text-[11px] text-[#6B6F78]">
            {items.length} satır • {items.filter((it) => !it.matched).length} eşleşmeyen • {items.filter((it) => it.needs_review || it.qty_missing).length} kontrol gereken
          </div>
        </div>
        <div className="overflow-y-auto max-h-[70vh]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#F7F4EF] z-10">
              <tr>
                <Th className="w-8 px-2">#</Th>
                <Th className="px-2">Okunan satır</Th>
                <Th className="px-2">Eşleşen ürün</Th>
                <Th className="px-2 text-right w-14">Mik.</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const warn = it.review_reason && (!it.matched || it.needs_review || it.qty_missing);
                return (
                  <tr
                    key={i}
                    onClick={() => setMatchIdx(i)}
                    className={`border-b border-[#F1ECE4] cursor-pointer hover:bg-orange-50/60 ${!it.matched ? "bg-red-50/50" : warn ? "bg-amber-50/60" : ""}`}
                    title="Ürünü değiştirmek / seçmek için tıklayın"
                  >
                    <td className="px-2 py-2 text-[#9EA1A8] font-mono align-top">{String(i + 1).padStart(2, "0")}</td>
                    <td className="px-2 py-2 text-[#1F2937] align-top break-words">
                      {it.searched}
                      {warn && <div className="mt-1 text-[11px] text-amber-800">{it.review_reason}</div>}
                    </td>
                    <td className="px-2 py-2 align-top break-words">
                      {it.matched ? <span className="text-[#1F2937]">{it.product_name}</span> : <span className="text-red-600 font-medium">Eşleşme yok</span>}
                      {it.price_pending && <span className="ml-1 text-[9px] uppercase text-amber-700">Fiyat bekliyor</span>}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums align-top">{it.qty}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <AdminProductMatchDialog
        key={`src-mm-${quote.id}-${matchIdx}`}
        open={matchIdx !== null}
        quote={quote}
        itemIdx={matchIdx}
        onClose={() => setMatchIdx(null)}
        onMatched={(updated) => { onQuoteChange?.(updated); setMatchIdx(null); }}
      />
    </div>
  );
}

function useAdminBlob(quoteId, file) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!file || file.kind === "excel") return undefined;
    let alive = true;
    let objectUrl = null;
    setUrl(null);
    setError("");
    adminApi.get(`/admin/quotes/${quoteId}/source-files/${file.id}`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        const type = file.kind === "pdf" ? "application/pdf" : (r.data?.type || file.content_type || "application/octet-stream");
        objectUrl = URL.createObjectURL(new Blob([r.data], { type }));
        setUrl(objectUrl);
      })
      .catch(() => { if (alive) setError("Dosya açılamadı."); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [quoteId, file?.id, file?.kind, file?.content_type]);
  return { url, error };
}

function SourceFileActions({ quoteId, file }) {
  const download = async () => {
    try {
      const r = await adminApi.get(`/admin/quotes/${quoteId}/source-files/${file.id}`, { params: { download: 1 }, responseType: "blob" });
      const objectUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = file.filename || "dosya";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch {
      toast.error("Dosya indirilemedi.");
    }
  };
  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-[11px] text-[#6B6F78] max-w-[160px] truncate" title={file.filename}>{file.filename}</span>
      <Button type="button" variant="outline" size="sm" onClick={download}>
        <Download className="w-3.5 h-3.5 mr-1" /> İndir
      </Button>
    </div>
  );
}

function SourceFileViewer({ quoteId, file }) {
  const { url, error } = useAdminBlob(quoteId, file);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);

  if (file.kind === "excel") return <ExcelPreview quoteId={quoteId} file={file} />;
  if (error) return <div className="p-8 text-center text-sm text-red-600">{error}</div>;
  if (!url) return <div className="p-8 text-center text-sm text-[#6B6F78]">Dosya yükleniyor…</div>;

  if (file.kind === "pdf") {
    return <iframe title={file.filename} src={url} className="w-full h-[70vh] bg-white" />;
  }
  if (file.kind === "image") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-1 px-3 py-2 border-b border-[#E6E0D6] bg-white">
          <Button type="button" variant="ghost" size="sm" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} title="Uzaklaştır"><ZoomOut className="w-4 h-4" /></Button>
          <span className="text-[11px] tabular-nums text-[#4B505B] w-10 text-center">%{Math.round(zoom * 100)}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} title="Yakınlaştır"><ZoomIn className="w-4 h-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setRotate((r) => (r + 90) % 360)} title="Döndür"><RotateCw className="w-4 h-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => window.open(url, "_blank")} title="Yeni sekmede aç" className="ml-auto"><ExternalLink className="w-4 h-4" /></Button>
        </div>
        <div className="overflow-auto max-h-[66vh] p-3">
          <img
            src={url}
            alt={file.filename}
            style={{ transform: `rotate(${rotate}deg)`, width: `${zoom * 100}%`, transformOrigin: "center center" }}
            className="max-w-none mx-auto block transition-transform"
          />
        </div>
      </div>
    );
  }
  return (
    <div className="p-8 text-center text-sm text-[#6B6F78]">
      Bu dosya türü burada gösterilemiyor. Sağ üstteki <strong>İndir</strong> ile açabilirsiniz.
    </div>
  );
}

function ExcelPreview({ quoteId, file }) {
  const [sheets, setSheets] = useState([]);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    adminApi.get(`/admin/quotes/${quoteId}/source-files/${file.id}/preview`)
      .then((r) => { if (alive) { setSheets(r.data?.sheets || []); setSheetIdx(0); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Excel önizlenemedi."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [quoteId, file.id]);
  if (loading) return <div className="p-8 text-center text-sm text-[#6B6F78]">Excel okunuyor…</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-600">{error}</div>;
  const sheet = sheets[sheetIdx];
  if (!sheet) return <div className="p-8 text-center text-sm text-[#6B6F78]">Excel boş.</div>;
  return (
    <div className="flex flex-col">
      {sheets.length > 1 && (
        <div className="flex gap-1 px-3 py-2 border-b border-[#E6E0D6] bg-white overflow-x-auto">
          {sheets.map((sh, i) => (
            <button key={sh.name + i} type="button" onClick={() => setSheetIdx(i)} className={`shrink-0 text-[11px] px-2 py-1 rounded ${i === sheetIdx ? "bg-[#0F1B2D] text-white" : "bg-[#F1ECE4] text-[#4B505B]"}`}>{sh.name}</button>
          ))}
        </div>
      )}
      <div className="overflow-auto max-h-[66vh]">
        <table className="text-[11px] border-collapse bg-white">
          <tbody>
            {sheet.rows.map((row, r) => (
              <tr key={r}>
                <td className="px-2 py-1 border border-[#E6E0D6] bg-[#F7F4EF] text-[#9EA1A8] text-right tabular-nums">{r + 1}</td>
                {row.map((cell, c) => (
                  <td key={c} className="px-2 py-1 border border-[#E6E0D6] whitespace-nowrap text-[#1F2937]">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {sheet.truncated && <div className="p-2 text-[11px] text-[#6B6F78]">İlk 500 satır gösteriliyor; tamamı için dosyayı indirin.</div>}
      </div>
    </div>
  );
}

// PRAMID V17.7: adminin onayladığı (öğrenilen) eşleşmeler — ara, gör, sil.
// ---------- V17.15: Şifreler ----------
function PasswordView({ isAdmin }) {
  const [own, setOwn] = useState({ current: "", next: "", again: "" });
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [target, setTarget] = useState("");
  const [manual, setManual] = useState("");
  const [shown, setShown] = useState(null);
  const [log, setLog] = useState([]);

  const loadAdminData = async () => {
    if (!isAdmin) return;
    try {
      const [s, l] = await Promise.all([adminApi.get("/admin/staff"), adminApi.get("/admin/password/log")]);
      setStaff(Array.isArray(s.data) ? s.data : []);
      setLog(Array.isArray(l.data) ? l.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Liste yüklenemedi.");
    }
  };
  useEffect(() => { loadAdminData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isAdmin]);

  const changeOwn = async () => {
    if (!own.current || !own.next) return toast.error("Mevcut ve yeni şifreyi girin.");
    if (own.next.length < 8) return toast.error("Yeni şifre en az 8 karakter olmalı.");
    if (own.next !== own.again) return toast.error("Yeni şifreler aynı değil.");
    setSaving(true);
    try {
      await adminApi.post("/admin/password/change", { current_password: own.current, new_password: own.next });
      toast.success("Şifreniz değiştirildi. Bir sonraki girişte yeni şifreyi kullanın.");
      setOwn({ current: "", next: "", again: "" });
      loadAdminData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şifre değiştirilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const setStaffPassword = async () => {
    if (!target) return toast.error("Personel seçin.");
    if (manual && manual.length < 8) return toast.error("Şifre en az 8 karakter olmalı (boş bırakırsanız sistem üretir).");
    setSaving(true);
    try {
      const r = await adminApi.post("/admin/password/set", { staff_id: target, new_password: manual || "" });
      setShown({ name: r.data?.name || r.data?.email || "", password: r.data?.password || "" });
      setManual("");
      loadAdminData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şifre atanamadı.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Şifreler" subtitle="Kendi şifrenizi değiştirin; ana yönetici personel şifrelerini atayabilir ve değişiklik kaydını görür" />
      <div className="p-3 sm:p-8 space-y-6 max-w-3xl">
        <Card className="border-[#E6E0D6] p-5 space-y-3">
          <h3 className="font-semibold text-[#0F1B2D]">Şifremi değiştir</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Mevcut şifre</Label><Input type="password" autoComplete="current-password" value={own.current} onChange={(e) => setOwn({ ...own, current: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Yeni şifre</Label><Input type="password" autoComplete="new-password" value={own.next} onChange={(e) => setOwn({ ...own, next: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Yeni şifre (tekrar)</Label><Input type="password" autoComplete="new-password" value={own.again} onChange={(e) => setOwn({ ...own, again: e.target.value })} /></div>
          </div>
          <Button onClick={changeOwn} disabled={saving} className="bg-[#0F1B2D] hover:bg-[#1B2B44] text-white">Şifreyi değiştir</Button>
        </Card>

        {isAdmin && (
          <Card className="border-[#E6E0D6] p-5 space-y-3">
            <h3 className="font-semibold text-[#0F1B2D]">Personel şifresi ata</h3>
            <p className="text-xs text-[#6B7280]">Şifreler güvenlik için geri çözülemeyecek şekilde saklanır; mevcut şifre gösterilemez. Atadığınız yeni şifre yalnız bir kez aşağıda görünür, not alın.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label>Personel</Label>
                <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full h-9 rounded-md border border-[#D9D3C9] px-2 text-sm bg-white">
                  <option value="">Seçin…</option>
                  {staff.map((u) => <option key={u.id} value={u.id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label>Yeni şifre (boş = sistem üretsin)</Label><Input type="text" value={manual} onChange={(e) => setManual(e.target.value)} /></div>
              <Button onClick={setStaffPassword} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">Şifreyi ata</Button>
            </div>
            {shown && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <b>{shown.name}</b> için yeni şifre: <span className="font-mono font-bold select-all">{shown.password}</span>
                <div className="text-xs text-[#6B7280] mt-1">Bu şifre bir daha gösterilmeyecek.</div>
              </div>
            )}
          </Card>
        )}

        {isAdmin && (
          <Card className="border-[#E6E0D6] p-5">
            <h3 className="font-semibold text-[#0F1B2D] mb-3">Şifre değişiklik kaydı</h3>
            {log.length === 0 ? <p className="text-sm text-[#6B7280]">Henüz kayıt yok.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-[#6B7280]"><th className="py-1">Tarih</th><th>Yapan</th><th>Kimin şifresi</th><th>İşlem</th></tr></thead>
                <tbody>
                  {log.map((r, i) => (
                    <tr key={i} className="border-t border-[#E6E0D6]">
                      <td className="py-1.5">{fmtDate ? fmtDate(r.at) : r.at}</td><td>{r.actor}</td><td>{r.target}</td><td>{r.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function LearnedMatchesView() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const requestRef = useRef(0);

  const load = async (query) => {
    const id = ++requestRef.current;
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/learned-matches", { params: { q: query, limit: 300 } });
      if (id !== requestRef.current) return;
      setRows(r.data?.items || []);
      setTotal(Number(r.data?.total || 0));
    } catch (e) {
      if (id === requestRef.current) toast.error(e?.response?.data?.detail || "Öğrenilen eşleşmeler alınamadı.");
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const remove = async (row) => {
    setDeleting(row.key);
    try {
      await adminApi.delete("/admin/learned-matches", { params: { key: row.key } });
      setRows((prev) => prev.filter((x) => x.key !== row.key));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Öğrenilen eşleşme silindi. Bu yazılış artık otomatik bu ürüne gitmeyecek.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi.");
    } finally {
      setDeleting(null);
      setConfirmRow(null);
    }
  };

  const viaLabel = (v) => ({
    admin_manual_match: "Admin seçimi",
    admin_price_approval: "Fiyat onayı",
    admin_manual: "Admin seçimi",
    admin_approved: "Admin onayı",
  }[v] || "Admin seçimi");

  return (
    <div>
      <PageHeader
        title="Öğrenilen Eşleşmeler"
        subtitle="Admin olarak seçtiğiniz eşleşmeler aynı yazılış tekrar gelince otomatik kullanılır. Yanlış öğrenileni buradan silebilirsiniz."
      />
      <div className="p-3 sm:p-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xl">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9EA1A8]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Müşteri yazılışı veya ürün adı ile ara…" />
          </div>
          <span className="text-xs text-[#6B6F78]">{loading ? "Yükleniyor…" : `${total} kayıt`}</span>
        </div>
        <Card className="border-[#E6E0D6] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F7F4EF]">
                  <Th>Müşteri yazılışı</Th>
                  <Th>Öğrenilen ürün</Th>
                  <Th>Marka</Th>
                  <Th>Kaynak</Th>
                  <Th>Son güncelleme</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr><td colSpan={6} className="p-10 text-center text-[#6B6F78]">{q ? "Aramaya uygun kayıt yok." : "Henüz öğrenilen eşleşme yok."}</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.key} className="border-b border-[#F1ECE4] hover:bg-[#F7F4EF]">
                    <td className="px-4 py-3 text-[#1F2937] max-w-[320px] break-words">{row.query}</td>
                    <td className="px-4 py-3 text-[#1F2937] max-w-[360px] break-words">{row.product_name || "—"}</td>
                    <td className="px-4 py-3 text-[#4B505B]">{row.brand_name || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{viaLabel(row.approved_via)}</Badge></td>
                    <td className="px-4 py-3 text-xs text-[#6B6F78] whitespace-nowrap">{row.updated_at ? fmtDate(row.updated_at) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setConfirmRow(row)}
                        disabled={deleting === row.key}
                        className="p-1.5 rounded hover:bg-red-50 text-[#6B6F78] hover:text-red-600 disabled:opacity-50"
                        title="Bu öğrenilen eşleşmeyi sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={!!confirmRow} onOpenChange={(o) => { if (!o && !deleting) setConfirmRow(null); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">Öğrenilen eşleşme silinsin mi?</DialogTitle>
            <DialogDescription className="text-xs">
              <strong>{confirmRow?.query}</strong> yazılışı artık otomatik olarak <strong>{confirmRow?.product_name}</strong> ürününe gitmeyecek. Eşleştirme kuralları yine çalışır.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRow(null)} disabled={!!deleting}>Vazgeç</Button>
            <Button onClick={() => remove(confirmRow)} disabled={!!deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Siliniyor…" : "Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- 17.29: Müşteriler ----------
const emptyCustomer = { name: "", company: "", phone: "", email: "", note: "" };

function CustomersView({ isAdmin }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null); // {mode, form, id}
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null); // {customer, quotes}
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(null);
  const load = async (query = q) => {
    try {
      const r = await adminApi.get("/admin/customers", { params: { q: query || "" }, timeout: 60000 });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Müşteriler alınamadı."); }
  };
  useEffect(() => { load(""); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 350); return () => clearTimeout(t); }, [q]);

  const save = async () => {
    const f = edit.form;
    if (!f.name.trim() || !f.company.trim() || !f.phone.trim() || !f.email.trim()) return toast.error("Ad soyad, firma, telefon ve e-posta zorunlu.");
    setSaving(true);
    try {
      if (edit.mode === "new") await adminApi.post("/admin/customers", f);
      else await adminApi.put(`/admin/customers/${edit.id}`, f);
      let extra = "";
      if (edit.mode !== "new" && (edit.apply || "unsent") !== "none") {
        try {
          const ar = await adminApi.post(`/admin/customers/${edit.id}/apply-to-quotes`, { scope: edit.apply || "unsent" });
          extra = ` ${ar.data?.changed || 0} teklif güncellendi${ar.data?.skipped_sent ? `, gönderilmiş ${ar.data.skipped_sent} teklif değişmedi` : ""}.`;
        } catch (e) { toast.error("Teklifler güncellenemedi: " + (e?.response?.data?.detail || "")); }
      }
      toast.success((edit.mode === "new" ? "Müşteri eklendi." : "Müşteri güncellendi.") + extra);
      setEdit(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`${c.name} (${c.company}) silinsin mi?\nTeklifleri silinmez; tekliflerdeki müşteri bilgisi kalır.`)) return;
    try { await adminApi.delete(`/admin/customers/${c.id}`); toast.success("Silindi."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi."); }
  };
  const anonymize = async (c) => {
    if (!window.confirm(`KVKK silme: ${c.name} (${c.company})\n\nMüşteri kartındaki ve tekliflerindeki ad, firma, telefon, e-posta silinir; yüklediği dosyalar ve WhatsApp yazışmaları silinir. Teklif satırları ve tutarlar kalır.\n\nBu işlem GERİ ALINAMAZ. Devam edilsin mi?`)) return;
    try {
      const r = await adminApi.post(`/admin/customers/${c.id}/anonymize`);
      toast.success(`Kişisel veriler silindi (${r.data?.quotes || 0} teklif, ${r.data?.whatsapp_messages || 0} WhatsApp mesajı).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi."); }
  };
  const applyEntry = async (c) => {
    try { await adminApi.post(`/admin/customers/${c.id}/apply-entry`); toast.success("Yeni bilgiler karta işlendi."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlenemedi."); }
  };
  const dismissEntry = async (c) => {
    try { await adminApi.post(`/admin/customers/${c.id}/dismiss-entry`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi."); }
  };
  const exportXlsx = async () => {
    try {
      const r = await adminApi.get("/admin/customers-export", { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pramid-musteriler-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Excel indirilemedi."); }
  };
  const merge = async () => {
    try {
      const pre = await adminApi.post("/admin/customers/merge-duplicates", null, { params: { dry_run: true } });
      if (!pre.data.removed) return toast.success("Tekrar eden kayıt yok.");
      if (!window.confirm(`Aynı telefonla açılmış ${pre.data.groups} müşteri için ${pre.data.removed} tekrar eden kayıt birleştirilecek.\nEn son yazılan bilgiler kalır, teklifler birleşen kayda bağlanır, teklif içerikleri değişmez.\nÖnce bütün müşteri kayıtlarının yedeği alınır. Devam edilsin mi?`)) return;
      const r = await adminApi.post("/admin/customers/merge-duplicates", null, { params: { dry_run: false }, timeout: 120000 });
      toast.success(`${r.data.removed} tekrar eden kayıt birleştirildi (${r.data.quotes_moved} teklif bağlandı).`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Birleştirilemedi."); }
  };
  const showHistory = async (c) => {
    try {
      const r = await adminApi.get(`/admin/customers/${c.id}/quotes`);
      setHistory({ customer: c, quotes: r.data.quotes || [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Teklifler alınamadı."); }
  };
  const openQuote = async (id) => {
    setOpening(id);
    try { const r = await adminApi.get(`/admin/quotes/${id}`, { timeout: 60000 }); setSelected(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Teklif açılamadı."); }
    finally { setOpening(null); }
  };
  const rows = data?.customers || [];
  const srcLabel = { site: "Site", whatsapp: "WhatsApp", admin: "Admin" };
  return (
    <div>
      <PageHeader
        title="Müşteriler"
        subtitle="Aynı telefon tek müşteri. Kayıtlı müşteri farklı bilgi yazarsa kart değişmez; burada 'farklı bilgi' olarak görünür."
        actions={
          <div className="flex flex-wrap gap-2">
            {isAdmin && Number(data?.duplicates || 0) > 0 && (
              <Button variant="outline" onClick={merge} className="border-amber-300 text-amber-800 hover:bg-amber-50">
                Tekrar edenleri birleştir ({data.duplicates})
              </Button>
            )}
            <Button variant="outline" onClick={exportXlsx}><Download className="w-4 h-4 mr-1.5" /> Excel'e aktar</Button>
            <Button onClick={() => setEdit({ mode: "new", form: { ...emptyCustomer } })} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Müşteri
            </Button>
          </div>
        }
      />
      <div className="p-3 sm:p-6 space-y-4">
      <CustomerSettingsCard isAdmin={isAdmin} />
      <Card className="border-[#E6E0D6] p-3">
        <div className="relative">
          <Search className="w-4 h-4 text-[#9EA1A8] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, firma, telefon ya da e-posta ile ara…" className="pl-9" />
        </div>
        <div className="text-xs text-[#6B6F78] mt-2">{data ? `${data.total} müşteri` : "Yükleniyor…"}</div>
      </Card>
      <Card className="border-[#E6E0D6] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EF]">
            <tr><Th>Müşteri</Th><Th className="hidden md:table-cell">Telefon</Th><Th className="hidden lg:table-cell">E-posta</Th><Th className="text-right">Teklif</Th><Th className="hidden lg:table-cell">Son teklif</Th><Th className="hidden md:table-cell">Kaynak</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center text-[#6B6F78]">{data ? "Müşteri bulunamadı." : "Yükleniyor…"}</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} className="border-b border-[#F1ECE4] align-top hover:bg-[#F7F4EF]">
                <td className="px-3 md:px-4 py-3 min-w-0">
                  <div className="font-medium text-[#1F2937]">{c.name || "—"}</div>
                  <div className="text-xs text-[#6B6F78]">{c.company || "—"}</div>
                  <div className="md:hidden text-xs text-[#6B6F78] mt-0.5">{c.phone || "—"}</div>
                  <div className="lg:hidden text-xs text-[#9EA1A8] break-all">{c.email || ""}</div>
                  {c.note && <div className="text-xs text-[#9EA1A8] mt-0.5">Not: {c.note}</div>}
                  {c.entry_differs && (
                    <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                      <div className="font-medium">Son girişte farklı bilgi yazdı ({fmtDate(c.last_entry?.at)}):</div>
                      <div>{c.last_entry?.name} · {c.last_entry?.company} · {c.last_entry?.email}</div>
                      <div className="mt-1 flex gap-2">
                        <button type="button" onClick={() => applyEntry(c)} className="underline font-medium">Karta işle</button>
                        <button type="button" onClick={() => dismissEntry(c)} className="underline">Yoksay</button>
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">{c.phone || "—"}</td>
                <td className="px-4 py-3 break-all hidden lg:table-cell">{c.email || <span className="text-[#9EA1A8]">—</span>}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {c.quote_count > 0 ? <button type="button" onClick={() => showHistory(c)} className="text-[#0F1B2D] underline">{c.quote_count}</button> : 0}
                </td>
                <td className="px-4 py-3 text-xs text-[#6B6F78] whitespace-nowrap hidden lg:table-cell">{c.last_quote_at ? fmtDate(c.last_quote_at) : "—"}</td>
                <td className="px-4 py-3 text-xs hidden md:table-cell">{srcLabel[c.created_via] || "Site"}</td>
                <td className="px-2 md:px-4 py-3 text-right whitespace-nowrap">
                  {c.phone && (
                    <button type="button" title="WhatsApp konuşması" onClick={() => nav("/admin/whatsapp", { state: { wa: c.phone } })} className="p-1.5 rounded hover:bg-green-50">
                      <MessageCircle className="w-4 h-4 text-[#16A34A]" />
                    </button>
                  )}
                  <button type="button" title="Düzenle" onClick={() => setEdit({ mode: "edit", id: c.id, form: { name: c.name || "", company: c.company || "", phone: c.phone || "", email: c.email || "", note: c.note || "" } })} className="p-1.5 rounded hover:bg-[#F1ECE4]">
                    <Pencil className="w-4 h-4 text-[#0F1B2D]" />
                  </button>
                  {isAdmin && (
                    <button type="button" title="Sil" onClick={() => remove(c)} className="p-1.5 rounded hover:bg-red-50 ml-1">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  )}
                  {isAdmin && c.name !== "Silinmiş müşteri" && (
                    <button type="button" title="KVKK: kişisel verileri sil" onClick={() => anonymize(c)} className="p-1.5 rounded hover:bg-red-50 ml-1">
                      <ShieldCheck className="w-4 h-4 text-red-700" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o && !saving) setEdit(null); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">{edit?.mode === "new" ? "Yeni Müşteri" : "Müşteriyi Düzenle"}</DialogTitle>
            <DialogDescription>Telefon tek olmalı (aynı numarayla ikinci kayıt açılmaz).{edit?.mode !== "new" ? " Değişikliği müşterinin tekliflerine de işleyebilirsiniz." : ""}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              {[["name", "Ad Soyad *"], ["company", "Firma *"], ["phone", "Telefon * (05xx xxx xx xx)"], ["email", "E-posta *"], ["note", "Not (yalnız siz görürsünüz)"]].map(([k, lbl]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{lbl}</Label>
                  <Input value={edit.form[k]} onChange={(e) => setEdit({ ...edit, form: { ...edit.form, [k]: e.target.value } })} />
                </div>
              ))}
              {edit.mode !== "new" && (
                <div className="space-y-1.5">
                  <Label>Tekliflerine de işle</Label>
                  <select value={edit.apply || "unsent"} onChange={(e) => setEdit({ ...edit, apply: e.target.value })}
                    className="w-full h-9 rounded-md border border-[#E6E0D6] bg-white px-3 text-sm">
                    <option value="unsent">Gönderilmemiş tekliflerine (WhatsApp/mail ile gidenler değişmez)</option>
                    <option value="all">Tüm tekliflerine</option>
                    <option value="none">Hiçbirine (yalnız kart)</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEdit(null)} disabled={saving}>İptal</Button>
                <Button onClick={save} disabled={saving} className="bg-[#E8762D] hover:bg-[#D4651F] text-white">{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">{history?.customer?.name} — teklifleri</DialogTitle>
            <DialogDescription>{history?.customer?.company} · {history?.customer?.phone}</DialogDescription>
          </DialogHeader>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F4EF]"><tr><Th>Tarih</Th><Th>No</Th><Th className="text-right">Kalem</Th><Th className="text-right">Nakit</Th><Th></Th></tr></thead>
            <tbody>
              {(history?.quotes || []).map((qq) => (
                <tr key={qq.id} className="border-b border-[#F1ECE4]">
                  <td className="px-3 py-2 text-xs text-[#6B6F78] whitespace-nowrap">{fmtDate(qq.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{String(qq.id).slice(0, 8).toUpperCase()}{qq.source_type === "whatsapp" && <span className="ml-1 text-green-700">WA</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{qq.summary?.total_items ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtTRY(qq.totals?.cash || 0)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled={!!opening} onClick={() => openQuote(qq.id)}>
                      {opening === qq.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : "Aç"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>
      <QuoteDetailDialog quote={selected} onClose={() => setSelected(null)} onQuoteChange={(u) => setSelected(u)} />
      </div>
    </div>
  );
}

// ---------- 17.27: WhatsApp ----------
function waStatusText(st) {
  return ({ sent: "WhatsApp: gönderildi", delivered: "WhatsApp: teslim edildi", read: "WhatsApp: okundu",
    failed: "WhatsApp: GÖNDERİLEMEDİ", new: "WhatsApp: yeni", seen: "WhatsApp" })[st] || "WhatsApp";
}

function waDigits(v) {
  let d = String(v || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 11 && d.startsWith("0")) d = "9" + d;
  if (d.length === 10 && d.startsWith("5")) d = "90" + d;
  return d;
}

function waTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay) return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === y.toDateString()) return "Dün";
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: d.getFullYear() === now.getFullYear() ? undefined : "2-digit" });
}

function waDay(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Bugün";
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Dün";
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

function WaTicks({ status }) {
  if (status === "failed") return <span className="text-red-600 text-[10px] font-semibold">gönderilemedi</span>;
  if (status === "read") return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
  if (status === "delivered") return <CheckCheck className="w-3.5 h-3.5 text-[#9EA1A8]" />;
  return <Check className="w-3.5 h-3.5 text-[#9EA1A8]" />;
}

const WA_IN_KIND = { list: "listeye alındı", note: "teklife not", info: "bilgi cevabı" };

// 17.31: WhatsApp konuşma geçmişi (solda kişiler, sağda konuşma; telefonda tek tek)
function WaConversations({ initialWa, onOpenQuote, onChanged, opening }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(initialWa ? waDigits(initialWa) : null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const lastCount = useRef(0);

  const loadList = async (query = q) => {
    try {
      const r = await adminApi.get("/admin/whatsapp/conversations", { params: { q: query || "" } });
      setList(r.data?.conversations || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Konuşmalar alınamadı."); }
  };
  const loadThread = async (wa = sel) => {
    if (!wa) return;
    try {
      const r = await adminApi.get(`/admin/whatsapp/conversations/${encodeURIComponent(wa)}`);
      setThread(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Konuşma açılamadı."); }
  };
  useEffect(() => { const t = setTimeout(() => loadList(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { const t = setInterval(() => loadList(), 30000); return () => clearInterval(t); }, [q]);
  useEffect(() => {
    setThread(null); setText(""); lastCount.current = 0;
    if (!sel) return undefined;
    loadThread(sel);
    const t = setInterval(() => loadThread(sel), 15000);
    return () => clearInterval(t);
  }, [sel]);
  useEffect(() => {
    const n = thread?.messages?.length || 0;
    if (n !== lastCount.current) {
      lastCount.current = n;
      setTimeout(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, 30);
    }
  }, [thread]);

  const current = (list || []).find((c) => c.wa_id === sel);
  const title = thread?.customer?.name || current?.name || thread?.wa_name || thread?.phone || "";
  const company = thread?.customer?.company && thread.customer.company !== "WhatsApp" ? thread.customer.company : (current?.company || "");
  const unread = current?.unread || 0;

  const send = async () => {
    const body = text.trim();
    if (!body || !sel) return;
    if (!window.confirm(`Bu mesaj ${title || thread?.phone} numarasına WhatsApp'tan gönderilsin mi?\n\n${body.slice(0, 300)}`)) return;
    setBusy(true);
    try {
      await adminApi.post("/admin/whatsapp/reply", { wa_id: sel, text: body });
      setText("");
      await Promise.all([loadThread(sel), loadList()]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi."); }
    finally { setBusy(false); }
  };
  // 17.45: konuşmadan dosya gönderme (yazılı mesaj kutusundaki metin açıklama olarak gider)
  const fileRef = useRef(null);
  const sendFile = async (f) => {
    if (!f || !sel) return;
    if (f.size > 16 * 1024 * 1024) { toast.error("Dosya 16 MB'dan büyük; WhatsApp ile gönderilemez."); return; }
    const cap = text.trim();
    if (!window.confirm(`"${f.name}" dosyası ${title || thread?.phone} numarasına WhatsApp'tan gönderilsin mi?${cap ? `\n\nAçıklama: ${cap.slice(0, 200)}` : ""}`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("wa_id", sel);
      fd.append("caption", cap);
      fd.append("file", f);
      await adminApi.post("/admin/whatsapp/send-file", fd, { timeout: 120000 });
      setText("");
      toast.success("Dosya gönderildi.");
      await Promise.all([loadThread(sel), loadList()]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dosya gönderilemedi."); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const openOutFile = async (m) => {
    try {
      const r = await adminApi.get(`/admin/whatsapp/out-file/${m.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Dosya açılamadı."); }
  };
  const markDone = async () => {
    try { await adminApi.post(`/admin/whatsapp/conversations/${encodeURIComponent(sel)}/handled`); await loadList(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşaretlenemedi."); }
  };

  const msgs = thread?.messages || [];
  let lastDay = "";
  const leftHours = Math.floor((thread?.reply_seconds_left || 0) / 3600);

  return (
    <Card className="border-[#E6E0D6] overflow-hidden">
      <div className="flex h-[calc(100dvh-215px)] lg:h-[calc(100dvh-230px)] min-h-[460px]">
        {/* Kişiler */}
        <div className={`${sel ? "hidden lg:flex" : "flex"} w-full lg:w-[340px] shrink-0 flex-col border-r border-[#E6E0D6] min-w-0`}>
          <div className="p-3 border-b border-[#F1ECE4]">
            <div className="relative">
              <Search className="w-4 h-4 text-[#9EA1A8] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, firma ya da telefon ara…" className="pl-9" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {list === null ? (
              <div className="p-6 text-center text-sm text-[#6B6F78]">Yükleniyor…</div>
            ) : list.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6B6F78]">{q ? "Aramaya uyan konuşma yok." : "Henüz WhatsApp konuşması yok."}</div>
            ) : list.map((c) => (
              <button
                key={c.wa_id}
                type="button"
                onClick={() => setSel(c.wa_id)}
                className={`w-full text-left px-3 py-3 border-b border-[#F1ECE4] flex gap-3 items-start hover:bg-[#F7F4EF] ${sel === c.wa_id ? "bg-[#F1ECE4]" : ""}`}
              >
                <div className="w-10 h-10 shrink-0 rounded-full bg-[#DCFCE7] text-[#166534] flex items-center justify-center font-semibold text-sm">
                  {(c.name || "?").replace(/^[^A-Za-zÇĞİÖŞÜçğıöşü]+/, "").slice(0, 1).toUpperCase() || <Phone className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-[#1F2937] truncate">{c.name}</div>
                    <div className={`text-[11px] shrink-0 ${c.unread ? "text-[#16A34A] font-semibold" : "text-[#9EA1A8]"}`}>{waTime(c.last_at)}</div>
                  </div>
                  {c.company && <div className="text-xs text-[#6B6F78] truncate">{c.company}</div>}
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="text-xs text-[#6B6F78] truncate flex items-center gap-1 min-w-0">
                      {c.last_direction === "out" && <span className="shrink-0"><WaTicks status={c.last_status} /></span>}
                      <span className="truncate">{c.last_text || "—"}</span>
                    </div>
                    {c.unread > 0 && <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-[#16A34A] text-white text-[10px] flex items-center justify-center">{c.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Konuşma */}
        <div className={`${sel ? "flex" : "hidden lg:flex"} flex-1 min-w-0 flex-col`}>
          {!sel ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#9EA1A8] p-6 text-center">Soldan bir konuşma seçin.</div>
          ) : (
            <>
              <div className="px-3 sm:px-4 py-2.5 border-b border-[#E6E0D6] bg-white flex items-center gap-2">
                <button type="button" onClick={() => setSel(null)} className="lg:hidden p-1.5 -ml-1 rounded hover:bg-[#F1ECE4]" title="Geri">
                  <ArrowLeft className="w-5 h-5 text-[#0F1B2D]" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0F1B2D] truncate">{title || "…"}</div>
                  <div className="text-xs text-[#6B6F78] truncate">{[company, thread?.phone].filter(Boolean).join(" · ")}</div>
                </div>
                {unread > 0 && (
                  <Button size="sm" variant="outline" onClick={markDone} title="Bekleyen mesajları 'Tamam' yap">
                    <Check className="w-3.5 h-3.5 mr-1" /> Tamam
                  </Button>
                )}
              </div>
              {(thread?.quotes || []).length > 0 && (
                <div className="px-3 sm:px-4 py-2 border-b border-[#F1ECE4] bg-[#F7F4EF] flex gap-2 overflow-x-auto scroll-soft">
                  {thread.quotes.map((qq) => (
                    <button
                      key={qq.id}
                      type="button"
                      disabled={!!opening}
                      onClick={() => onOpenQuote(qq)}
                      className="shrink-0 text-xs border border-[#E6E0D6] bg-white rounded-full px-3 py-1 hover:border-[#E8762D] disabled:opacity-60"
                      title="Teklifi aç"
                    >
                      {opening === qq.id ? "Açılıyor…" : <>Teklif {String(qq.id).slice(0, 8).toUpperCase()} · {waTime(qq.created_at)}{qq.totals?.cash ? ` · ${fmtTRY(qq.totals.cash)}` : ""}</>}
                    </button>
                  ))}
                </div>
              )}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 bg-[#EFEAE2]">
                {!thread ? (
                  <div className="text-center text-sm text-[#6B6F78]">Yükleniyor…</div>
                ) : msgs.length === 0 ? (
                  <div className="text-center text-sm text-[#6B6F78]">Mesaj yok.</div>
                ) : msgs.map((m) => {
                  const day = waDay(m.created_at);
                  const showDay = day !== lastDay;
                  lastDay = day;
                  const out = m.direction === "out";
                  return (
                    <div key={m.id}>
                      {showDay && (
                        <div className="flex justify-center my-3">
                          <span className="text-[11px] bg-white/90 text-[#6B6F78] rounded-md px-2.5 py-1 shadow-sm">{day}</span>
                        </div>
                      )}
                      <div className={`flex mb-1.5 ${out ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] sm:max-w-[70%] rounded-lg px-3 py-2 shadow-sm text-sm ${out ? "bg-[#D9FDD3]" : "bg-white"} ${m.status === "failed" ? "ring-1 ring-red-300" : ""}`}>
                          {out && m.kind_label && <div className="text-[10px] uppercase tracking-wide text-[#15803D] mb-0.5">{m.kind_label}{m.by ? ` · ${m.by}` : ""}</div>}
                          <div className="whitespace-pre-wrap break-words text-[#1F2937]">
                            {(m.type === "image" || (m.type === "document" && !out)) && <span className="mr-1">{m.type === "image" ? "📷" : "📄"}</span>}
                            {m.preview || m.text || ""}
                          </div>
                          {out && m.file_id && (
                            <button type="button" onClick={() => openOutFile(m)} className="mt-1 text-[11px] text-[#0F1B2D] underline">
                              {m.type === "image" ? "📷" : "📄"} {m.filename || "Dosyayı aç"}
                            </button>
                          )}
                          {!out && WA_IN_KIND[m.kind] && <div className="mt-1 text-[11px] text-[#6B6F78]">{WA_IN_KIND[m.kind]}</div>}
                          {m.quote_id && (
                            <button type="button" disabled={!!opening} onClick={() => onOpenQuote({ id: m.quote_id })} className="mt-1 text-[11px] text-[#0F1B2D] underline disabled:opacity-60">
                              {opening === m.quote_id ? "Açılıyor…" : `Teklifi aç (${String(m.quote_id).slice(0, 8).toUpperCase()})`}
                            </button>
                          )}
                          {m.status === "failed" && m.error && <div className="mt-1 text-[11px] text-red-700">{m.error}</div>}
                          <div className="flex items-center justify-end gap-1 mt-0.5 text-[10px] text-[#9EA1A8]">
                            {new Date(m.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                            {out && <WaTicks status={m.status} />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-[#E6E0D6] bg-white p-2 sm:p-3">
                {thread && !thread.configured ? (
                  <div className="text-xs text-red-600 px-1 py-2">WhatsApp yapılandırılmadı (.env); mesaj gönderilemez.</div>
                ) : thread && !thread.can_reply ? (
                  <div className="text-xs text-[#6B6F78] px-1 py-2">Müşterinin son mesajından 24 saat geçti; Meta serbest mesaja izin vermiyor. Teklif "Gönder" düğmesiyle (şablonla) gönderilebilir; diğer konular için telefonla arayın.</div>
                ) : (
                  <div className="flex items-end gap-2">
                    <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.txt"
                      onChange={(e) => sendFile(e.target.files?.[0])} />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy || !thread}
                      className="h-11 px-3 border-[#E6E0D6]" title="Dosya gönder (PDF, fotoğraf, Excel, Word…). Kutudaki yazı açıklama olarak gider.">
                      <Upload className="w-4 h-4" /><span className="hidden md:inline ml-1.5">Dosya</span>
                    </Button>
                    <Textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Mesaj yazın…"
                      className="min-h-[44px] max-h-40 resize-none"
                      rows={1}
                      disabled={busy || !thread}
                    />
                    <Button onClick={send} disabled={busy || !text.trim() || !thread} className="bg-[#16A34A] hover:bg-[#15803D] text-white h-11 px-4">
                      <Send className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">{busy ? "Gönderiliyor…" : "Gönder"}</span>
                    </Button>
                  </div>
                )}
                {thread?.can_reply && leftHours < 6 && <div className="text-[11px] text-amber-700 px-1 pt-1">Cevap süresi ~{leftHours} saat sonra kapanır (24 saat kuralı).</div>}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function WhatsAppView({ onChanged }) {
  const loc = useLocation();
  const initialWa = loc?.state?.wa || null;
  const [tab, setTab] = useState("chats");
  const [chatWa, setChatWa] = useState(initialWa);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [waQuotes, setWaQuotes] = useState(null);
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(null);
  const load = async () => {
    try {
      const [r, rq] = await Promise.all([
        adminApi.get("/admin/whatsapp/messages", { params: { show: showAll ? "all" : "open" } }),
        adminApi.get("/admin/whatsapp/quotes"),
      ]);
      setData(r.data);
      setWaQuotes(rq.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "WhatsApp bilgileri alınamadı."); }
  };
  const openQuote = async (q) => {
    setOpening(q.id);
    try {
      const r = await adminApi.get(`/admin/quotes/${q.id}`, { timeout: 60000 });
      setSelected(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Teklif açılamadı."); }
    finally { setOpening(null); }
  };
  const quoteChanged = (updated) => { setSelected(updated); load(); onChanged?.(); };
  const isAdmin = getAdminRole() === "admin";
  const removeMsg = async (m) => {
    if (!window.confirm("Bu mesaj listeden kaldırılsın mı? (Konuşma geçmişinde durmaya devam eder)")) return;
    try { await adminApi.delete(`/admin/whatsapp/messages/${encodeURIComponent(m.id)}`); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaldırılamadı."); }
  };
  const purgeHandled = async () => {
    if (!window.confirm("'Tamam' denmiş bütün mesajlar bu listeden kaldırılsın mı? (Konuşma geçmişinde durmaya devam eder)")) return;
    try { const r = await adminApi.post("/admin/whatsapp/messages/purge-handled"); toast.success(`${r.data?.deleted || 0} mesaj listeden kaldırıldı.`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Kaldırılamadı."); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [showAll]);
  const done = async (m) => {
    try { await adminApi.post(`/admin/whatsapp/messages/${encodeURIComponent(m.id)}/handled`); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşaretlenemedi."); }
  };
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await adminApi.post("/admin/whatsapp/reply", { wa_id: replyTo.wa_id, text: text.trim() });
      toast.success("Cevap gönderildi.");
      setReplyTo(null); setText(""); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi."); }
    finally { setBusy(false); }
  };
  const rows = data?.messages || [];
  const openCount = rows.filter((m) => !m.handled).length;
  const newQuotes = (waQuotes?.quotes || []).filter((q) => q.whatsapp_status === "new").length;
  const tabBtn = (key, label, n, cls) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${tab === key ? "border-[#E8762D] text-[#0F1B2D]" : "border-transparent text-[#6B6F78] hover:text-[#0F1B2D]"}`}
    >
      {label}
      {n > 0 && <span className={`min-w-5 h-5 px-1 rounded-full text-[10px] flex items-center justify-center ${cls}`}>{n}</span>}
    </button>
  );
  return (
    <div>
      <PageHeader title="WhatsApp" subtitle="Konuşmalar, WhatsApp'tan gelen teklifler ve liste olmayan mesajlar." />
      <div className="px-3 sm:px-6 pt-2 bg-white border-b border-[#E6E0D6] flex items-end justify-between gap-2">
        <div className="flex overflow-x-auto scroll-soft">
          {tabBtn("chats", "Konuşmalar", 0)}
          {tabBtn("quotes", "Teklifler", newQuotes, "bg-[#16A34A] text-white")}
          {tabBtn("messages", "Bekleyen mesajlar", openCount, "bg-[#E8762D] text-white")}
        </div>
        <div className="hidden md:block text-xs text-[#6B6F78] pb-2.5 whitespace-nowrap">
          {data ? (data.configured ? <span className="text-green-700 font-medium">bağlı</span> : <span className="text-red-600 font-medium">yapılandırılmadı (.env)</span>) : "…"}
          {data?.last_webhook_at && <> · son bildirim {fmtDate(data.last_webhook_at)}</>}
        </div>
      </div>
      <div className="p-3 sm:p-6 space-y-4">
      {tab === "chats" && (
        <WaConversations key={chatWa || "-"} initialWa={chatWa} onOpenQuote={openQuote} onChanged={() => { load(); onChanged?.(); }} opening={opening} />
      )}
      {tab === "quotes" && (
      <Card className="border-[#E6E0D6] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1ECE4] flex flex-wrap items-center justify-between gap-1">
          <div className="text-sm font-semibold text-[#0F1B2D]">WhatsApp teklifleri</div>
          {Number(waQuotes?.preparing || 0) > 0 && <div className="text-xs text-[#6B6F78]">{waQuotes.preparing} liste hazırlanıyor (müşteri 2 dk yazmayınca teklif düşer)</div>}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EF]"><tr><Th className="hidden md:table-cell">Tarih</Th><Th>Müşteri</Th><Th className="hidden md:table-cell">Durum</Th><Th className="text-right hidden md:table-cell">Kalem</Th><Th className="text-right">Toplam<span className="hidden sm:inline"> (Nakit)</span></Th><Th></Th></tr></thead>
          <tbody>
            {!(waQuotes?.quotes || []).length ? (
              <tr><td colSpan={6} className="p-8 text-center text-[#6B6F78]">{waQuotes ? "Henüz WhatsApp'tan teklif gelmedi." : "Yükleniyor…"}</td></tr>
            ) : waQuotes.quotes.map((q) => {
              const badges = (
                <div className="flex flex-wrap gap-1">
                  {q.whatsapp_status === "new" && <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50">yeni</Badge>}
                  {q.summary?.is_draft ? <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Taslak</Badge> : <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Hazır</Badge>}
                  {q.whatsapp?.info_missing && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">bilgi eksik</Badge>}
                  {(q.customer_notes || []).length > 0 && <Badge variant="outline" className={q.customer_notes_new ? "border-orange-400 text-orange-700 bg-orange-50" : "border-amber-200 text-amber-700 bg-white"} title={(q.customer_notes || []).map((n) => n.text).join("\n")}>{q.customer_notes_new ? "yeni not" : `${q.customer_notes.length} not`}</Badge>}
                  {q.whatsapp_sent_at && <Badge variant="outline" className={q.whatsapp_status === "failed" ? "border-red-300 text-red-700 bg-red-50" : "border-green-300 text-green-700 bg-white"}>{waStatusText(q.whatsapp_status)}</Badge>}
                  {(q.whatsapp?.read_errors || []).length > 0 && <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50" title={(q.whatsapp.read_errors || []).join("\n")}>okuma uyarısı</Badge>}
                </div>
              );
              return (
              <tr key={q.id} className={`border-b border-[#F1ECE4] ${q.whatsapp_status === "new" ? "bg-green-50/50" : ""}`}>
                <td className="px-4 py-3 text-xs text-[#6B6F78] whitespace-nowrap hidden md:table-cell">{fmtDate(q.created_at)}</td>
                <td className="px-3 md:px-4 py-3">
                  <div className="font-medium text-[#1F2937]">{q.customer?.name || "—"}</div>
                  <div className="text-xs text-[#6B6F78]">{[q.customer?.company !== "WhatsApp" ? q.customer?.company : "", q.customer?.phone].filter(Boolean).join(" · ")}</div>
                  <div className="md:hidden text-[11px] text-[#9EA1A8] mt-0.5">{fmtDate(q.created_at)} · {q.summary?.total_items ?? "—"} kalem</div>
                  <div className="md:hidden mt-1.5">{badges}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{badges}</td>
                <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{q.summary?.total_items ?? "—"}</td>
                <td className="px-2 md:px-4 py-3 text-right tabular-nums font-medium text-[#E8762D] whitespace-nowrap text-xs md:text-sm">{fmtTRY(q.totals?.cash || 0)}</td>
                <td className="px-2 md:px-4 py-3 text-right">
                  <Button size="sm" variant="outline" disabled={!!opening} onClick={() => openQuote(q)}>
                    {opening === q.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Eye className="w-3.5 h-3.5 md:mr-1" /><span className="hidden md:inline">Aç</span></>}
                  </Button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      )}
      {tab === "messages" && (
      <Card className="border-[#E6E0D6] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1ECE4] flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-[#0F1B2D]">Mesajlar (liste olmayan)</div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-[#4B505B]">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-[#E8762D]" /> Tamamlananları da göster
            </label>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={purgeHandled} className="text-red-700 border-red-200 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Tamamlananları kaldır
              </Button>
            )}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#6B6F78]">{data ? "Bekleyen mesaj yok." : "Yükleniyor…"}</div>
        ) : rows.map((m) => (
          <div key={m.id} className={`px-4 py-3 border-b border-[#F1ECE4] flex flex-col md:flex-row md:items-start gap-2 md:gap-4 ${m.handled ? "opacity-60" : ""}`}>
            <div className="md:w-48 shrink-0 flex md:block items-baseline justify-between gap-2">
              <div className="font-medium text-[#1F2937]">{m.name || "—"}</div>
              <div className="text-xs text-[#6B6F78]">{m.phone}</div>
              <div className="text-[11px] text-[#9EA1A8] md:mt-0.5">{fmtDate(m.created_at)}</div>
            </div>
            <div className="flex-1 min-w-0 text-sm">
              <div className="whitespace-pre-wrap break-words text-[#1F2937]">{m.text || (m.type !== "text" ? `[${m.type}]` : "")}</div>
              {m.note && <div className="mt-1 text-xs text-amber-800">{m.note}</div>}
              {m.quote_id && <div className="mt-1 text-xs text-[#6B6F78]">Teklif: {String(m.quote_id).slice(0, 8).toUpperCase()}</div>}
            </div>
            <div className="shrink-0 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => { setChatWa(m.wa_id); setTab("chats"); }} title="Konuşmanın tamamını aç">
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> Konuşma
              </Button>
              {m.can_reply && !m.handled && (
                <Button size="sm" variant="outline" onClick={() => { setReplyTo(m); setText(""); }}>Cevapla</Button>
              )}
              {!m.handled && <Button size="sm" variant="outline" onClick={() => done(m)}>Tamam</Button>}
              {isAdmin && (
                <button type="button" onClick={() => removeMsg(m)} className="p-1.5 rounded hover:bg-red-50" title="Listeden kaldır">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              )}
            </div>
          </div>
        ))}
      </Card>
      )}
      <Dialog open={!!replyTo} onOpenChange={(o) => { if (!o && !busy) setReplyTo(null); }}>
        <DialogContent className="bg-white max-w-lg w-[calc(100vw-16px)] sm:w-full">
          <DialogHeader>
            <DialogTitle className="text-[#0F1B2D]">WhatsApp cevabı</DialogTitle>
            <DialogDescription>{replyTo?.name || ""} {replyTo?.phone}. Müşterinin son mesajından 24 saat geçtiyse Meta serbest mesaja izin vermez.</DialogDescription>
          </DialogHeader>
          <div className="text-xs bg-[#F7F4EF] border border-[#E6E0D6] rounded p-2 whitespace-pre-wrap">{replyTo?.text}</div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-[120px]" placeholder="Cevabınız…" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReplyTo(null)} disabled={busy}>İptal</Button>
            <Button onClick={send} disabled={busy || !text.trim()} className="bg-[#16A34A] hover:bg-[#15803D] text-white">{busy ? "Gönderiliyor…" : "Gönder"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuoteDetailDialog quote={selected} onClose={() => { setSelected(null); load(); onChanged?.(); }} onQuoteChange={quoteChanged} />
      </div>
    </div>
  );
}

// 17.25: miktar hücresi — kaleme tıkla, yaz, Enter (Esc vazgeç)
function parseQty(v) {
  const n = Number(String(v ?? "").trim().replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 1000) / 1000 : null;
}

function QtyCell({ value, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const start = () => { if (disabled) return; setText(String(value ?? "").replace(".", ",")); setEditing(true); };
  const commit = async () => {
    if (busy) return;
    const n = parseQty(text);
    if (n === null) { toast.error("Miktar 0 dan büyük bir sayı olmalı."); return; }
    if (n === Number(value)) { setEditing(false); return; }
    setBusy(true);
    const ok = await onSave(n);
    setBusy(false);
    if (ok) setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 justify-end">
        <Input
          autoFocus
          value={text}
          disabled={busy}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          onBlur={() => { if (!busy) setEditing(false); }}
          className="h-7 w-20 px-2 text-right text-xs"
          inputMode="decimal"
        />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={commit} disabled={busy}
          className="text-[10px] font-semibold text-white bg-[#E8762D] hover:bg-[#D4651F] rounded px-1.5 py-1 disabled:opacity-50">
          {busy ? "…" : "Kaydet"}
        </button>
      </span>
    );
  }
  return (
    <button type="button" onClick={start} disabled={disabled}
      className="inline-flex items-center gap-1 tabular-nums hover:text-[#E8762D] disabled:opacity-60" title="Miktarı değiştir">
      {value}
      <Pencil className="w-3 h-3 text-[#9EA1A8]" />
    </button>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.1em] font-semibold border-b border-[#E6E0D6] text-left text-[#4B5563] ${className}`}>
    {children}
  </th>
);

// PRAMID V15.7.1: additive Excel sheet selection
import "@/addons/excelSheetAddon";
